import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    TextField,
    InputAdornment,
    Paper,
    List,
    ListItemButton,
    ListItemText,
    Checkbox,
    CircularProgress,
    Alert,
    Fade,
    Radio,
    RadioGroup,
    FormControlLabel,
    Chip,
    Stack,
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import * as SliderPrimitive from '@radix-ui/react-slider';
import { useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { useHistory, useLocation } from 'react-router-dom';
import { DesktopLayout } from 'src/layout';
import AutoTopUpChoice from 'src/components/AutoTopUpChoice';
import useIsMobileSize from 'src/hooks/useIsMobileSize';
import IntentService from 'src/services/IntentService';
import EventService from 'src/services/EventService';

import LeadStreamsService from 'src/services/LeadStreamsService';
import ProjectsService from 'src/services/ProjectsService';
import NicheAnalysisService from 'src/services/NicheAnalysisService';
import GeoService from 'src/services/GeoService';
import YandexDirectService from 'src/services/YandexDirectService';

import Points from 'src/screens/Payment/components/Points';
import TicketField from 'src/components/TicketField';
import useUserStore from 'src/stores/user';
import formatNumber from 'src/utils/formatNumber';
import SubscriptionsService from 'src/services/SubscriptionsService';
import RotateLeftS from 'src/icons/RotateLeftS';
import Checked from 'src/icons/Checked';
import LeadsmaniaCollapsedLogo from 'src/icons/LeadsmaniaCollapsedLogo';
import { WavyBackground } from 'src/components/ui/wavy-background';
import { updateBeginWorkStep } from 'src/providers/UpdateBeginWorkStepsProvider';
import { BEGIN_WORK_STEPS_IDS } from 'src/constants/beginWorksSteps';
import { ROUTES_URL } from 'src/constants/routes';
import { clearPublicLaunchIntent, readPublicLaunchIntent } from 'src/utils/publicLaunchIntent';
import { packageRequestKey, rememberPackageCheckout } from 'src/utils/packageCheckout';

import {
    DEFAULT_LEAD_STREAM_EXCLUDE_RULES,
    DEFAULT_LEAD_STREAM_TARGET_INTENTS,
    buildLeadStreamFilterProfile,
    compileLeadStreamPromptPreview,
} from 'src/utils/leadStreamFilterProfile';
import leadcardIcon from 'src/assets/leadstream/leadcard.svg';
import leadyesIcon from 'src/assets/leadstream/leadyes.svg';
import payforleadIcon from 'src/assets/leadstream/payforlead.svg';
import leadnoIcon from 'src/assets/leadstream/leadno.svg';
import searchleadIcon from 'src/assets/leadstream/searchlead.svg';

import SetupDrafts from './SetupDrafts';

import type { Workspace } from 'src/services/IntentService';

import type {
    ActivePackagePromotion,
    LeadCategory,
    LeadPackagePrice,
} from 'src/services/LeadStreamsService/agent';
import type { ILeadExample, INicheAnalysisResponse } from 'src/services/NicheAnalysisService';
import type { IDirectForecastSummary } from 'src/services/NicheAnalysisService';
import type { IYandexDirectAuditRun } from 'src/services/YandexDirectService';
import type { ILocation } from 'src/data/locations';

const formatLeadPrice = (value: number) =>
    new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(value);

interface SelectedCategory {
    category: LeadCategory;
    packageSize: number | null;
    prices: LeadPackagePrice[];
}

const normalizePromotionMultiplier = (value: number | string | null | undefined): number => {
    const numeric = typeof value === 'string' ? Number(value) : value ?? 0;
    return Number.isFinite(numeric) && numeric > 1 ? numeric : 1;
};

const getCreditedLeadCount = (packageSize: number, promotion?: ActivePackagePromotion | null): number => {
    const normalizedSize = Math.max(0, Math.trunc(packageSize || 0));
    if (normalizedSize <= 0) {
        return 0;
    }
    return Math.max(0, Math.round(normalizedSize * normalizePromotionMultiplier(promotion?.multiplier)));
};

const getPromotionBadgeLabel = (promotion?: ActivePackagePromotion | null): string => {
    if (!promotion) {
        return 'Акция';
    }
    return (
        promotion.badge_text ||
        promotion.multiplier_label ||
        `x${String(normalizePromotionMultiplier(promotion.multiplier)).replace(/\.0+$/, '')}`
    );
};

type LeadStreamForm = {
    paymentMethod: string;
    bonus: boolean;
    auto_renewal: boolean;
    auto_renewal_touched: boolean;
    promo_code?: string;
    ai_prompt?: string;
    setup_description?: string;
    setup_geo?: string;
    setup_address?: string;
};

type LeadExampleChoice = 'fit' | 'skip' | 'maybe';

type LeadStreamAnalysis = INicheAnalysisResponse & {
    suggestedCategories: LeadCategory[];
    bestCategory: LeadCategory | null;
};

const GEO_QUICK_REPLIES = ['Москва', 'Москва и область', 'Вся Россия', 'Локация не важна'];

const SKIP_REASON_PRESETS = ['Не то гео', 'Не та услуга', 'Спам или реклама', 'Слишком общий запрос'];

const LEADSTREAM_BORDER = 'var(--lm-border)';
const LEADSTREAM_SOFT_BORDER = 'var(--lm-border)';
const LEADSTREAM_CARD_RADIUS = '16px';
const LEADSTREAM_PANEL_RADIUS = '14px';
const LEADSTREAM_CONTROL_RADIUS = '12px';
const LEADSTREAM_BUTTON_FONT_WEIGHT = 500;
const LEADSTREAM_CARD_SHADOW = '0 8px 22px rgba(17, 24, 39, 0.04)';
const LEADSTREAM_FLOW_BORDER = 'rgba(0, 68, 255, 0.2)';
const LEADSTREAM_FLOW_SHADOW = '0 24px 80px rgba(0, 68, 255, 0.12)';
const LEADSTREAM_FLOW_COLORS = ['var(--lm-accent-ink)', '#006BFF', '#00B2FF', '#42C8FF', '#7E7AFF', '#B236FF'];
const LEADSTREAM_SECTION_TITLE_SX = {
    fontWeight: 850,
    fontSize: { xs: 22, sm: 24 },
    lineHeight: 1.2,
} as const;
const LEADSTREAM_CARD_SX = {
    borderRadius: LEADSTREAM_CARD_RADIUS,
    border: `1px solid ${LEADSTREAM_BORDER}`,
    bgcolor: 'var(--lm-surface)',
    boxShadow: LEADSTREAM_CARD_SHADOW,
} as const;

const leadstreamQuickAppear = keyframes`
  0% {
    opacity: 0;
    transform: translate3d(0, 18px, 0) scale(0.985);
  }
  70% {
    opacity: 1;
    transform: translate3d(0, -2px, 0) scale(1.005);
  }
  100% {
    opacity: 1;
    transform: translate3d(0, 0, 0) scale(1);
  }
`;

const getLeadstreamAppearSx = (delayMs = 0) =>
    ({
        animation: `${leadstreamQuickAppear} 360ms cubic-bezier(0.16, 1, 0.3, 1) both`,
        animationDelay: `${delayMs}ms`,
        '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
            animationDelay: '0ms',
        },
    } as const);

const formatCategoryLabel = (category?: LeadCategory | null) => {
    if (!category) {
        return '';
    }

    return `${category.icon || ''} ${category.name}`.trim();
};

const hasCategorySnapshotChanged = (current: LeadCategory, fresh: LeadCategory) => {
    return (
        current.id !== fresh.id ||
        current.name !== fresh.name ||
        current.description !== fresh.description ||
        current.icon !== fresh.icon ||
        current.color !== fresh.color ||
        current.is_active !== fresh.is_active ||
        current.is_available_for_packages !== fresh.is_available_for_packages ||
        current.is_delivery_enabled !== fresh.is_delivery_enabled ||
        current.availability_hint !== fresh.availability_hint ||
        current.package_pricing_profile !== fresh.package_pricing_profile ||
        current.package_base_price_per_lead !== fresh.package_base_price_per_lead ||
        current.package_recommended_size !== fresh.package_recommended_size ||
        current.active_package_promotion !== fresh.active_package_promotion
    );
};

const getVisibleLeadExamples = (analysis: LeadStreamAnalysis | null) => analysis?.lead_examples.slice(0, 6) || [];

const hasCompletedLeadExamplesReview = (
    analysis: LeadStreamAnalysis | null,
    choices: Record<number, LeadExampleChoice>,
    manualCategoryFallback = false
) => {
    const visibleExamples = getVisibleLeadExamples(analysis);

    if (!analysis || !visibleExamples.length) {
        return false;
    }

    const reviewed = visibleExamples.every((example) => Boolean(choices[example.id]));
    const hasPositiveChoice = visibleExamples.some((example) => choices[example.id] === 'fit');

    return reviewed && (hasPositiveChoice || manualCategoryFallback);
};

const StageDivider = () => (
    <Box
        sx={{
            height: '1px',
            width: '100%',
            backgroundColor: LEADSTREAM_BORDER,
            my: { xs: 3, sm: 4 },
        }}
    />
);

const LeadstreamAssetIcon = ({ src, alt, size = 32 }: { src: string; alt: string; size?: number }) => (
    <Box
        component="span"
        sx={{
            width: size,
            height: size,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
        }}
    >
        <Box
            component="img"
            src={src}
            alt={alt}
            sx={{
                width: size,
                height: size,
                display: 'block',
            }}
        />
    </Box>
);

const SummarySearchIcon = () => (
    <LeadstreamAssetIcon
        src={searchleadIcon}
        alt=""
    />
);

const SummaryRejectIcon = () => (
    <LeadstreamAssetIcon
        src={leadnoIcon}
        alt=""
    />
);

type TariffIncludedBadge = {
    icon: string;
    label: string;
};

const getTariffIncludedBadges = (packageSize?: number | null): TariffIncludedBadge[] => [
    { icon: '🛡️', label: 'Спам-фильтр' },
    { icon: '📱', label: 'Telegram уведомления' },
    { icon: '📊', label: 'Выгрузка лидов' },
    ...(packageSize === 500 ? [{ icon: '👤', label: 'Настройка под ключ' }] : []),
];

const TariffIncludedBadges = ({ badges }: { badges: TariffIncludedBadge[] }) => {
    if (!badges.length) {
        return null;
    }

    return (
        <Box sx={{ mt: 1.75 }}>
            <Typography
                variant="caption"
                sx={{
                    display: 'block',
                    color: 'var(--lm-muted)',
                    fontSize: 12,
                    fontWeight: 700,
                    lineHeight: 1.2,
                    mb: 0.75,
                }}
            >
                Включено в тариф
            </Typography>
            <Stack
                direction="row"
                flexWrap="wrap"
                gap={0.75}
            >
                {badges.map((badge) => (
                    <Chip
                        key={badge.label}
                        label={
                            <Box
                                component="span"
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                            >
                                <Box
                                    component="span"
                                    sx={{ lineHeight: 1 }}
                                >
                                    {badge.icon}
                                </Box>
                                {badge.label}
                            </Box>
                        }
                        size="small"
                        sx={{
                            height: 30,
                            borderRadius: '12px',
                            backgroundColor: 'rgba(209, 213, 219, 0.15)',
                            color: 'var(--lm-text)',
                            border: '1.5px solid #D1D5DB',
                            fontSize: 13,
                            fontWeight: 600,
                            '& .MuiChip-label': {
                                px: 1.1,
                            },
                        }}
                    />
                ))}
            </Stack>
        </Box>
    );
};

const LaunchStepIcon = ({ type }: { type: 'delivery' | 'training' | 'balance' }) => {
    const iconByType = {
        delivery: leadcardIcon,
        training: leadyesIcon,
        balance: payforleadIcon,
    };

    return (
        <LeadstreamAssetIcon
            src={iconByType[type]}
            alt=""
            size={32}
        />
    );
};

const QuerySearchAgentIcon = ({ size = 28 }: { size?: number }) => (
    <Box
        component="span"
        sx={{
            width: size,
            height: size,
            borderRadius: '50%',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, rgba(221, 244, 255, 0.98) 0%, rgba(142, 219, 255, 0.72) 100%)',
            boxShadow: '0 12px 28px rgba(142, 219, 255, 0.22)',
        }}
    >
        <svg
            width={Math.round(size * 0.56)}
            height={Math.round(size * 0.56)}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M10.7 17.4a6.7 6.7 0 1 1 0-13.4 6.7 6.7 0 0 1 0 13.4Z"
                stroke="#071722"
                strokeWidth="2"
            />
            <path
                d="m15.7 15.7 4.1 4.1"
                stroke="#071722"
                strokeWidth="2"
                strokeLinecap="round"
            />
            <path
                d="M17.6 3.2c.11-.43.73-.43.84 0l.28 1.08c.15.58.6 1.03 1.18 1.18l1.08.28c.43.11.43.73 0 .84l-1.08.28c-.58.15-1.03.6-1.18 1.18l-.28 1.08c-.11.43-.73.43-.84 0l-.28-1.08a1.74 1.74 0 0 0-1.18-1.18l-1.08-.28c-.43-.11-.43-.73 0-.84l1.08-.28c.58-.15 1.03-.6 1.18-1.18l.28-1.08Z"
                fill="#2AABEE"
            />
        </svg>
    </Box>
);

const pulseAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(0, 68, 255, 0.26), 0 0 0 0 rgba(126, 122, 255, 0.16);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 0 20px rgba(0, 68, 255, 0), 0 0 0 30px rgba(126, 122, 255, 0);
    transform: scale(1.05);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(0, 68, 255, 0), 0 0 0 0 rgba(126, 122, 255, 0);
    transform: scale(1);
  }
`;

const floatAnimation = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const dotsAnimation = keyframes`
  0%, 20% { opacity: 0; }
  40% { opacity: 1; }
  60%, 100% { opacity: 0; }
`;

const telegramVibeDrift = keyframes`
  0% {
    transform: translate3d(-6%, -4%, 0) scale(1) rotate(-3deg);
  }
  50% {
    transform: translate3d(5%, 3%, 0) scale(1.14) rotate(3deg);
  }
  100% {
    transform: translate3d(10%, -2%, 0) scale(1.06) rotate(-2deg);
  }
`;

const telegramVibeWave = keyframes`
  0% {
    transform: translate3d(-8%, 0, 0) scaleX(1);
    opacity: 0.28;
  }
  50% {
    transform: translate3d(4%, -5%, 0) scaleX(1.08);
    opacity: 0.5;
  }
  100% {
    transform: translate3d(10%, 2%, 0) scaleX(0.96);
    opacity: 0.34;
  }
`;

const telegramVibeRing = keyframes`
  0% {
    transform: rotate(0deg) scale(0.98);
    opacity: 0.34;
  }
  50% {
    transform: rotate(8deg) scale(1.04);
    opacity: 0.72;
  }
  100% {
    transform: rotate(-6deg) scale(1);
    opacity: 0.42;
  }
`;

const telegramVibeFloat = keyframes`
  0%, 100% {
    transform: translate3d(0, 0, 0);
  }
  50% {
    transform: translate3d(0, -8px, 0);
  }
`;

const agentRadarSpin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const agentCorePulse = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow:
      0 18px 44px rgba(0, 0, 0, 0.28),
      inset 0 0 34px rgba(42, 171, 238, 0.22),
      0 0 0 0 rgba(142, 219, 255, 0.2);
  }
  50% {
    transform: scale(1.045);
    box-shadow:
      0 22px 56px rgba(0, 0, 0, 0.34),
      inset 0 0 44px rgba(42, 171, 238, 0.34),
      0 0 0 16px rgba(142, 219, 255, 0);
  }
`;

const agentSignalDash = keyframes`
  0% {
    stroke-dashoffset: 220;
    opacity: 0.1;
  }
  18%, 72% {
    opacity: 0.95;
  }
  100% {
    stroke-dashoffset: 0;
    opacity: 0.12;
  }
`;

const agentPacketFloat = keyframes`
  0% {
    transform: translate3d(var(--from-x), var(--from-y), 0) scale(0.84);
    opacity: 0;
  }
  18% {
    opacity: 1;
  }
  62% {
    transform: translate3d(var(--mid-x), var(--mid-y), 0) scale(1);
    opacity: 0.92;
  }
  100% {
    transform: translate3d(0, 0, 0) scale(0.72);
    opacity: 0;
  }
`;

const agentParticleRise = keyframes`
  0% {
    transform: translate3d(0, 20px, 0) scale(0.72);
    opacity: 0;
  }
  22% {
    opacity: 0.82;
  }
  100% {
    transform: translate3d(var(--to-x), -68px, 0) scale(1.08);
    opacity: 0;
  }
`;

const normalizeText = (value?: string | null) => (value || '').toLowerCase().trim();

const CATEGORY_SCORE_STOP_WORDS = new Set([
    'бизнес',
    'компания',
    'продукт',
    'сервис',
    'сервиса',
    'сервисы',
    'услуга',
    'услуги',
    'решение',
    'решения',
]);

const hasAnyTextToken = (text: string, tokens: string[]) => tokens.some((token) => text.includes(token));

const formatProjectName = (value: string) =>
    value.trim().slice(0, 80) || `Поток лидов ${new Date().toLocaleDateString('ru-RU')}`;

const isWideGeo = (value: string) => {
    const normalized = normalizeText(value);
    return (
        !normalized ||
        normalized.includes('не важно') ||
        normalized.includes('вся россия') ||
        normalized.includes('по всей россии') ||
        normalized.includes('без гео')
    );
};

const getGeoForecastFactor = (geo: string) => {
    const normalized = normalizeText(geo);
    if (isWideGeo(normalized)) {
        return 1;
    }
    if (normalized.includes('москва') && (normalized.includes('область') || normalized.includes('мо'))) {
        return 0.4;
    }
    if (normalized.includes('москва')) {
        return 0.32;
    }
    if (normalized.includes('санкт') || normalized.includes('петербург') || normalized.includes('спб')) {
        return 0.18;
    }
    if (
        normalized.includes('область') ||
        normalized.includes('край') ||
        normalized.includes('республика') ||
        normalized.includes('регион')
    ) {
        return 0.18;
    }
    return 0.1;
};

const getCategoryMonthlyLeadBase = (category: LeadCategory) => {
    const totalBase = Math.max(0, Number(category.lead_base_total_count || 0));
    const recent30 = Math.max(0, Number(category.lead_base_recent_count || 0));
    const recent90 = Math.max(0, Number(category.lead_base_90d_count || 0));
    const backendMonthlyEstimate = Math.max(0, Number(category.lead_base_monthly_estimate || 0));
    const monthlyFrom90Days = recent90 > 0 ? recent90 / 3 : 0;
    const historicalMonthly = totalBase > 0 ? totalBase / 6 : 0;
    const weightedMonthly = recent30 * 0.55 + monthlyFrom90Days * 0.35 + historicalMonthly * 0.1;
    const signalCount = [recent30, recent90, totalBase].filter((value) => value > 0).length;

    return {
        monthlyBase: weightedMonthly > 0 ? weightedMonthly : backendMonthlyEstimate,
        signalCount,
    };
};

const getCategoryConfidenceFactor = ({
    category,
    categoryName,
    keywords,
    businessDescription,
    isBestCategory,
}: {
    category: LeadCategory;
    categoryName?: string;
    keywords: string[];
    businessDescription: string;
    isBestCategory: boolean;
}) => {
    const score = scoreCategory(category, categoryName, keywords, businessDescription);
    const baseConfidence = score >= 12 ? 0.96 : score >= 7 ? 0.88 : score > 0 ? 0.78 : 0.65;
    return Math.min(1, Math.max(0.55, baseConfidence + (isBestCategory ? 0.04 : -0.04)));
};

const scoreCategory = (
    category: LeadCategory,
    categoryName: string | undefined,
    keywords: string[],
    businessDescription: string
) => {
    const haystack = normalizeText(`${category.name} ${category.description || ''}`);
    const normalizedCategory = normalizeText(categoryName);
    const normalizedBusiness = normalizeText(businessDescription);
    const normalizedContext = normalizeText(`${categoryName || ''} ${keywords.join(' ')} ${businessDescription}`);
    let score = 0;

    const isItCategory = normalizeText(category.name).includes('it') || haystack.includes('разработка');
    const isAutomationCategory = normalizeText(category.name).includes('1с') || haystack.includes('автоматизация');
    if (
        isAutomationCategory &&
        hasAnyTextToken(normalizedContext, ['1с', 'crm', 'битрикс', 'bitrix', 'amo', 'интеграц'])
    ) {
        score += 12;
    }
    if (
        isItCategory &&
        hasAnyTextToken(normalizedContext, [
            'vpn',
            'proxy',
            'прокси',
            'vps',
            'хостинг',
            'сервер',
            'devops',
            'api',
            'backend',
            'frontend',
            'кибер',
            'бот',
            'mini app',
        ])
    ) {
        score += 12;
    }

    if (normalizedCategory && haystack.includes(normalizedCategory)) score += 6;
    if (normalizedCategory && normalizeText(category.name).includes(normalizedCategory)) score += 4;
    keywords.forEach((keyword) => {
        const normalizedKeyword = normalizeText(keyword);
        if (normalizedKeyword && haystack.includes(normalizedKeyword)) score += 1;
    });
    normalizedBusiness
        .split(/\s+/)
        .filter((word) => word.length > 2 && !CATEGORY_SCORE_STOP_WORDS.has(word))
        .slice(0, 12)
        .forEach((word) => {
            if (haystack.includes(word)) score += 0.5;
        });

    return score;
};

const pickSuggestedCategories = (
    categoryName: string | undefined,
    categories: LeadCategory[],
    keywords: string[],
    businessDescription: string
) => {
    return categories
        .map((category) => ({
            category,
            score: scoreCategory(category, categoryName, keywords, businessDescription),
        }))
        .filter(({ score }) => score > 0)
        .sort((left, right) => right.score - left.score)
        .slice(0, 4)
        .map(({ category }) => category);
};

const buildAnalysisDescription = (businessDescription: string, geo: string, address?: string) =>
    [
        `Услуга или продукт: ${businessDescription.trim()}`,
        geo.trim() ? (isWideGeo(geo) ? 'Гео: не критично' : `Гео: ${geo.trim()}`) : '',
        address?.trim() ? `Адрес: ${address.trim()}` : '',
    ]
        .filter(Boolean)
        .join('\n');

const getLocationDisplayName = (location: ILocation) =>
    location.display_name || [location.name, location.region, location.country].filter(Boolean).join(', ');

const buildGeoContext = (geo: string, address?: string) =>
    [geo.trim() && !isWideGeo(geo) ? geo.trim() : '', address?.trim() || ''].filter(Boolean).join(', ');

const buildAiPrompt = ({
    businessDescription,
    geo,
    category,
    examples,
    choices,
    skipReasons,
}: {
    businessDescription: string;
    geo: string;
    category?: LeadCategory | null;
    examples: ILeadExample[];
    choices: Record<number, LeadExampleChoice>;
    skipReasons: Record<string, string>;
}) => {
    return compileLeadStreamPromptPreview(
        buildLeadStreamFilterProfile({
            businessDescription,
            geo,
            category,
            examples,
            choices,
            skipReasons,
        })
    );
};

const LeadStreamProgress = ({
    hasAnalysis,
    examplesDone,
    examplesCount = 0,
    examplesProgressPercent = 0,
    hasPrompt,
    onReset,
    compact = false,
}: {
    hasAnalysis: boolean;
    examplesDone: boolean;
    examplesCount?: number;
    examplesProgressPercent?: number;
    hasPrompt: boolean;
    onReset?: () => void;
    compact?: boolean;
}) => {
    const examplesProgressFilled = examplesCount > 0 && examplesProgressPercent >= 100;
    const examplesStepDone = examplesDone || examplesProgressFilled;
    const steps = [
        { label: 'Анализ ниши', done: hasAnalysis },
        { label: 'Проверка примеров', done: examplesStepDone, examplesProgress: true },
        { label: 'Запуск потока', done: hasPrompt },
    ];
    const completedCount = steps.filter((step) => step.done).length;
    const activeIndex = steps.findIndex((step) => !step.done);
    const currentStepNumber = activeIndex === -1 ? steps.length : activeIndex + 1;
    const showExamplesProgress = examplesCount > 0 && !examplesStepDone;

    return (
        <Box
            sx={{
                borderRadius: compact ? LEADSTREAM_PANEL_RADIUS : LEADSTREAM_CARD_RADIUS,
                border: `1px solid ${LEADSTREAM_BORDER}`,
                bgcolor: 'var(--lm-surface)',
                p: compact ? 1.5 : 2,
            }}
        >
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                mb={1.5}
            >
                <Typography sx={{ fontWeight: 800 }}>Подготовка потока</Typography>
                <Chip
                    size="small"
                    label={`Шаг ${currentStepNumber} из ${steps.length}`}
                    sx={{ fontWeight: 700, bgcolor: 'var(--lm-accent-soft)', color: 'var(--lm-accent)' }}
                />
            </Box>
            <Box
                sx={{
                    height: 8,
                    bgcolor: 'var(--lm-border)',
                    borderRadius: 999,
                    overflow: 'hidden',
                    mb: 1.5,
                }}
            >
                <Box
                    sx={{
                        width: `${(completedCount / steps.length) * 100}%`,
                        height: '100%',
                        bgcolor: 'var(--lm-accent)',
                        borderRadius: 999,
                        transition: 'width 180ms ease',
                    }}
                />
            </Box>
            <Box
                display="grid"
                gridTemplateColumns={compact ? '1fr' : { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }}
                gap={1}
            >
                {steps.map((step, index) => {
                    const active = index === activeIndex;
                    return (
                        <Box
                            key={step.label}
                            display="flex"
                            alignItems="center"
                            gap={1}
                            px={1.25}
                            py={1}
                            borderRadius="12px"
                            sx={{
                                border: `1px solid ${active ? 'var(--lm-accent-ink)' : LEADSTREAM_BORDER}`,
                                bgcolor: step.done
                                    ? 'var(--lm-success-bg)'
                                    : active
                                    ? 'var(--lm-accent-soft)'
                                    : 'var(--lm-canvas)',
                            }}
                        >
                            <Box
                                sx={{
                                    width: 24,
                                    height: 24,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                {step.done ? (
                                    <Checked />
                                ) : (
                                    <Box
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            borderRadius: '50%',
                                            bgcolor: active ? 'var(--lm-accent)' : 'var(--lm-border)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{ color: active ? 'var(--lm-accent-contrast)' : 'var(--lm-muted)' }}
                                        >
                                            {index + 1}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: 'block',
                                        fontWeight: step.done || active ? 700 : 500,
                                        color: step.done || active ? 'var(--lm-text)' : 'var(--lm-muted)',
                                    }}
                                >
                                    {step.label}
                                </Typography>
                                {step.examplesProgress && showExamplesProgress && (
                                    <Box mt={0.75}>
                                        <Box
                                            sx={{
                                                height: 5,
                                                borderRadius: 999,
                                                bgcolor: 'var(--lm-border)',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: `${examplesProgressPercent}%`,
                                                    height: '100%',
                                                    borderRadius: 999,
                                                    bgcolor: examplesDone
                                                        ? 'var(--lm-success-fill)'
                                                        : 'var(--lm-accent)',
                                                    transition: 'width 180ms ease',
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
            {onReset && (
                <Button
                    type="button"
                    size="small"
                    variant="text"
                    startIcon={<RotateLeftS />}
                    onClick={onReset}
                    sx={{
                        mt: 1.25,
                        minHeight: 28,
                        px: 0.75,
                        borderRadius: '9px',
                        color: 'var(--lm-muted)',
                        fontSize: 12,
                        fontWeight: 500,
                        textTransform: 'none',
                        '& .MuiButton-startIcon': {
                            mr: 0.5,
                            color: 'inherit',
                        },
                        '&:hover': {
                            color: 'var(--lm-text)',
                            bgcolor: 'var(--lm-hover)',
                        },
                    }}
                >
                    Начать заново
                </Button>
            )}
        </Box>
    );
};

const LeadExampleReviewCard = ({
    example,
    index,
    choice,
    skipReason,
    onChoose,
    onSkipReason,
}: {
    example: ILeadExample;
    index: number;
    choice?: LeadExampleChoice;
    skipReason?: string;
    onChoose: (exampleId: number, choice: LeadExampleChoice) => void;
    onSkipReason: (exampleId: number, reason: string) => void;
}) => {
    const isFit = choice === 'fit';
    const isSkip = choice === 'skip';
    const isMaybe = choice === 'maybe';
    const categoryLabel = example.category?.trim();
    const selectedBorder = isFit
        ? '#22C55E'
        : isSkip
        ? 'var(--lm-danger-border)'
        : isMaybe
        ? 'var(--lm-border)'
        : 'var(--lm-border)';
    const selectedBackground = isFit ? 'rgba(34, 197, 94, 0.04)' : 'var(--lm-surface)';
    const reviewOptions: Array<{
        value: LeadExampleChoice;
        label: string;
        selected: boolean;
        color: string;
        bg: string;
    }> = [
        {
            value: 'fit',
            label: 'Подходит',
            selected: isFit,
            color: 'var(--lm-success-ink)',
            bg: 'var(--lm-success-bg)',
        },
        {
            value: 'skip',
            label: 'Не подходит',
            selected: isSkip,
            color: 'var(--lm-danger-ink)',
            bg: 'var(--lm-danger-bg)',
        },
        { value: 'maybe', label: 'Не уверен', selected: isMaybe, color: 'var(--lm-muted)', bg: 'var(--lm-canvas)' },
    ];
    const segmentedButtonSx = {
        minHeight: 32,
        borderRadius: '8px',
        fontSize: 12,
        fontWeight: LEADSTREAM_BUTTON_FONT_WEIGHT,
        lineHeight: '18px',
        whiteSpace: 'nowrap',
        px: 0.75,
        color: 'var(--lm-muted)',
        textTransform: 'none',
    } as const;

    return (
        <Card
            elevation={0}
            sx={{
                height: '100%',
                borderRadius: LEADSTREAM_CARD_RADIUS,
                border: `1px solid ${selectedBorder}`,
                bgcolor: selectedBackground,
                boxShadow: LEADSTREAM_CARD_SHADOW,
                transition: 'border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 10px 28px rgba(17, 24, 39, 0.08)',
                },
            }}
        >
            <CardContent sx={{ height: '100%', p: 2, '&:last-child': { pb: 2 } }}>
                <Stack
                    spacing={1.5}
                    sx={{ height: '100%' }}
                >
                    <Stack spacing={0.75}>
                        <Box
                            display="flex"
                            alignItems="center"
                        >
                            <Stack
                                direction="row"
                                flexWrap="wrap"
                                alignItems="center"
                                gap={0.75}
                                sx={{ minWidth: 0 }}
                            >
                                <Chip
                                    size="small"
                                    label={`Лид ${index}`}
                                    sx={{
                                        height: 24,
                                        fontWeight: 800,
                                        bgcolor: 'var(--lm-accent-soft)',
                                        color: 'var(--lm-accent)',
                                    }}
                                />
                                {categoryLabel && (
                                    <Chip
                                        size="small"
                                        label={`${example.category_icon || ''} ${categoryLabel}`.trim()}
                                        sx={{
                                            height: 'auto',
                                            minHeight: 26,
                                            maxWidth: '100%',
                                            bgcolor: 'var(--lm-accent-soft)',
                                            color: 'var(--lm-accent-ink)',
                                            border: '1px solid #C7D7FE',
                                            fontWeight: 700,
                                            '& .MuiChip-label': {
                                                display: 'block',
                                                whiteSpace: 'normal',
                                                lineHeight: '18px',
                                                py: '3px',
                                            },
                                        }}
                                    />
                                )}
                            </Stack>
                        </Box>
                    </Stack>

                    <Typography
                        variant="body1"
                        sx={{
                            whiteSpace: 'pre-line',
                            fontSize: { xs: 16, sm: 17 },
                            lineHeight: '26px',
                            color: 'var(--lm-text)',
                            p: 1.5,
                            borderRadius: LEADSTREAM_CONTROL_RADIUS,
                            bgcolor: 'var(--lm-surface)',
                            border: `1px solid ${LEADSTREAM_SOFT_BORDER}`,
                        }}
                    >
                        {example.text}
                    </Typography>

                    <Box sx={{ mt: 'auto' }}>
                        <Typography
                            variant="caption"
                            sx={{ display: 'block', mb: 1, color: 'var(--lm-muted)', fontWeight: 700 }}
                        >
                            Такой лид вам подходит?
                        </Typography>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                                gap: 0.5,
                                p: 0.5,
                                borderRadius: LEADSTREAM_CONTROL_RADIUS,
                                bgcolor: 'var(--lm-canvas)',
                                border: '1px solid var(--lm-border)',
                            }}
                        >
                            {reviewOptions.map((option) => (
                                <Button
                                    key={option.value}
                                    type="button"
                                    fullWidth
                                    variant="text"
                                    onClick={() => onChoose(example.id, option.value)}
                                    sx={{
                                        ...segmentedButtonSx,
                                        ...(option.selected
                                            ? {
                                                  bgcolor: option.bg,
                                                  color: option.color,
                                                  boxShadow: '0 1px 3px rgba(17, 24, 39, 0.08)',
                                              }
                                            : {
                                                  '&:hover': {
                                                      bgcolor: 'var(--lm-surface)',
                                                      color: 'var(--lm-text)',
                                                  },
                                              }),
                                    }}
                                >
                                    {option.label}
                                </Button>
                            ))}
                        </Box>
                    </Box>

                    {isSkip && (
                        <Stack spacing={1}>
                            <Typography
                                variant="caption"
                                sx={{ color: 'var(--lm-muted)' }}
                            >
                                Почему не подходит?
                            </Typography>
                            <Stack
                                direction="row"
                                flexWrap="wrap"
                                gap={1}
                            >
                                {SKIP_REASON_PRESETS.map((reason) => (
                                    <Chip
                                        key={reason}
                                        label={reason}
                                        size="small"
                                        clickable
                                        color={skipReason === reason ? 'primary' : 'default'}
                                        onClick={() => onSkipReason(example.id, reason)}
                                        sx={{ fontWeight: skipReason === reason ? 700 : 500 }}
                                    />
                                ))}
                            </Stack>
                        </Stack>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
};

const NicheAnalysisLoadingCard = () => {
    return (
        <Fade
            in
            timeout={300}
        >
            <Box
                sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'var(--lm-surface)',
                    borderRadius: LEADSTREAM_CARD_RADIUS,
                    border: `1px solid ${LEADSTREAM_FLOW_BORDER}`,
                    boxShadow: LEADSTREAM_FLOW_SHADOW,
                    p: { xs: 2.5, sm: 3 },
                    mb: 3,
                }}
            >
                <Stack
                    alignItems="center"
                    spacing={3}
                >
                    <Box
                        sx={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Box
                            sx={{
                                animation: `${pulseAnimation} 2s ease-in-out infinite`,
                                borderRadius: '50%',
                                p: 2,
                            }}
                        >
                            <Box sx={{ animation: `${floatAnimation} 2s ease-in-out infinite` }}>
                                <Box
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        '& svg': {
                                            width: 64,
                                            height: 64,
                                            display: 'block',
                                        },
                                        '& path': {
                                            fill: 'var(--lm-accent-ink)',
                                        },
                                    }}
                                >
                                    <LeadsmaniaCollapsedLogo
                                        width={64}
                                        height={64}
                                    />
                                </Box>
                            </Box>
                        </Box>
                        <CircularProgress
                            size={80}
                            thickness={2}
                            sx={{
                                position: 'absolute',
                                color: 'rgba(0, 68, 255, 0.32)',
                            }}
                        />
                    </Box>

                    <Stack
                        alignItems="center"
                        spacing={1}
                    >
                        <Typography
                            variant="h5"
                            sx={{
                                color: 'var(--lm-text)',
                                fontWeight: 850,
                                fontSize: { xs: 22, sm: 24 },
                                lineHeight: 1.2,
                            }}
                        >
                            ИИ анализирует вашу нишу
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                textAlign: 'center',
                                maxWidth: 420,
                                color: 'var(--lm-muted)',
                                fontWeight: 600,
                                lineHeight: 1.45,
                            }}
                        >
                            Ищем примеры заявок из вашей ниши
                            <Box
                                component="span"
                                sx={{
                                    display: 'inline-block',
                                    ml: 0.5,
                                    '&::after': {
                                        content: '"..."',
                                        animation: `${dotsAnimation} 1.4s infinite`,
                                    },
                                }}
                            />
                        </Typography>
                    </Stack>

                    <Stack
                        direction="row"
                        spacing={0.5}
                    >
                        {LEADSTREAM_FLOW_COLORS.map((color, index) => (
                            <Box
                                key={color}
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    backgroundColor: color,
                                    animation: `${dotsAnimation} 1.4s infinite`,
                                    animationDelay: `${index * 0.2}s`,
                                }}
                            />
                        ))}
                    </Stack>
                </Stack>
            </Box>
        </Fade>
    );
};

const DIRECT_INTENT_LABELS: Record<string, string> = {
    hot: 'Горячий',
    warm: 'Смежный',
    research: 'Инфо',
    negative: 'Минус',
};

const DIRECT_RISK_LABELS: Record<string, string> = {
    green: 'Готово',
    yellow: 'Нужна проверка',
    red: 'Есть блокеры',
};

const DirectForecastCard = ({
    forecast,
    audit,
    isAuditLoading,
    isApplyLoading,
    isConfirmLoading,
    onRunAudit,
    onApplyAudit,
    onConfirmAdvertiserData,
    onConnect,
}: {
    forecast: IDirectForecastSummary;
    audit: IYandexDirectAuditRun | null;
    isAuditLoading: boolean;
    isApplyLoading: boolean;
    isConfirmLoading: boolean;
    onRunAudit: () => void;
    onApplyAudit: () => void;
    onConfirmAdvertiserData: () => void;
    onConnect: () => void;
}) => {
    const summary = forecast.summary;
    const leads = summary.lead_forecast;
    const isConnected = forecast.connection_status === 'connected';
    const topQueries = (forecast.queries || []).slice(0, 8);
    const topCluster = (forecast.clusters || [])[0];
    const recommendedStrategy =
        (forecast.strategies || []).find((strategy) => strategy.recommended) || forecast.strategies?.[0];
    const canRunAudit = isConnected && !isAuditLoading;
    const needsAdvertiserConfirmation = Boolean(
        audit?.findings?.some((finding) => finding.finding_type === 'labeling' && finding.status === 'open')
    );

    return (
        <Box
            sx={{
                ...LEADSTREAM_CARD_SX,
                p: { xs: 2, sm: 2.5 },
                mb: 3,
            }}
        >
            <Stack spacing={2.25}>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    gap={2}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            gap={1}
                            flexWrap="wrap"
                            sx={{ mb: 0.75 }}
                        >
                            <Typography
                                variant="h6"
                                sx={{ fontWeight: 850, lineHeight: 1.2 }}
                            >
                                Яндекс Директ
                            </Typography>
                            <Chip
                                size="small"
                                label={isConnected ? 'Подключен' : 'Оценка без аккаунта'}
                                color={isConnected ? 'success' : 'default'}
                                sx={{ fontWeight: 700 }}
                            />
                        </Stack>
                        <Typography
                            variant="body2"
                            sx={{ color: 'var(--lm-muted)', lineHeight: 1.5, maxWidth: 720 }}
                        >
                            {isConnected
                                ? 'Можно проверить текущие кампании и собрать план правок.'
                                : 'Предварительный спрос по нише. Точный прогноз появится после подключения Директа.'}
                        </Typography>
                    </Box>
                    <Button
                        variant={isConnected ? 'contained' : 'outlined'}
                        onClick={isConnected ? onRunAudit : onConnect}
                        disabled={isConnected && !canRunAudit}
                        sx={{
                            minWidth: { xs: '100%', sm: 180 },
                            alignSelf: { xs: 'stretch', md: 'flex-start' },
                            borderRadius: LEADSTREAM_CONTROL_RADIUS,
                            textTransform: 'none',
                            fontWeight: LEADSTREAM_BUTTON_FONT_WEIGHT,
                        }}
                    >
                        {isAuditLoading ? (
                            <>
                                <CircularProgress
                                    color="inherit"
                                    size={16}
                                    sx={{ mr: 1 }}
                                />
                                Аудит...
                            </>
                        ) : isConnected ? (
                            'Аудит Директа'
                        ) : (
                            'Подключить Директ'
                        )}
                    </Button>
                </Stack>

                <Box
                    display="grid"
                    gridTemplateColumns={{ xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }}
                    gap={1.25}
                >
                    {[
                        {
                            label: 'Лиды/мес',
                            value: `${formatNumber(leads.low)}–${formatNumber(leads.high)}`,
                            hint: `База: ${formatNumber(leads.base)}`,
                        },
                        {
                            label: 'Бюджет/мес',
                            value: `${formatNumber(summary.budget_low)}–${formatNumber(summary.budget_high)} ₽`,
                            hint: `База: ${formatNumber(summary.budget_base)} ₽`,
                        },
                        {
                            label: 'Запросы',
                            value: formatNumber(forecast.queries?.length || 0),
                            hint: topCluster ? topCluster.title : forecast.geo?.input || 'Гео не указано',
                        },
                    ].map((metric) => (
                        <Box
                            key={metric.label}
                            sx={{
                                border: `1px solid ${LEADSTREAM_SOFT_BORDER}`,
                                borderRadius: LEADSTREAM_PANEL_RADIUS,
                                p: 1.5,
                                minWidth: 0,
                                backgroundColor: 'var(--lm-accent-soft)',
                            }}
                        >
                            <Typography sx={{ fontSize: 12, color: 'var(--lm-muted)', mb: 0.35 }}>
                                {metric.label}
                            </Typography>
                            <Typography sx={{ fontWeight: 850, fontSize: 20, lineHeight: 1.15 }}>
                                {metric.value}
                            </Typography>
                            <Typography
                                sx={{
                                    mt: 0.35,
                                    color: 'var(--lm-muted)',
                                    fontSize: 12.5,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {metric.hint}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {recommendedStrategy && (
                    <Box
                        sx={{
                            borderRadius: LEADSTREAM_PANEL_RADIUS,
                            border: `1px solid ${LEADSTREAM_SOFT_BORDER}`,
                            p: 1.5,
                        }}
                    >
                        <Typography sx={{ fontWeight: 800, mb: 0.4 }}>{recommendedStrategy.title}</Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: 'var(--lm-muted)', lineHeight: 1.45 }}
                        >
                            {recommendedStrategy.direct_strategy}
                        </Typography>
                    </Box>
                )}

                {topQueries.length > 0 && (
                    <Stack spacing={1}>
                        <Typography sx={{ fontWeight: 800, fontSize: 14 }}>Запросы для проверки</Typography>
                        <Stack
                            direction="row"
                            flexWrap="wrap"
                            gap={0.75}
                        >
                            {topQueries.map((query) => (
                                <Chip
                                    key={query.query}
                                    size="small"
                                    label={`${query.query} · ${DIRECT_INTENT_LABELS[query.intent] || query.intent}`}
                                    sx={{
                                        maxWidth: '100%',
                                        fontWeight: query.intent === 'hot' ? 700 : 500,
                                        borderRadius: '10px',
                                    }}
                                />
                            ))}
                        </Stack>
                    </Stack>
                )}

                {audit && (
                    <Box
                        sx={{
                            borderRadius: LEADSTREAM_PANEL_RADIUS,
                            border: `1px solid ${
                                audit.overall_risk === 'red' ? 'var(--lm-danger-border)' : 'var(--lm-border)'
                            }`,
                            backgroundColor: audit.overall_risk === 'red' ? 'var(--lm-danger-bg)' : 'var(--lm-surface)',
                            p: 1.5,
                        }}
                    >
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            justifyContent="space-between"
                            gap={1}
                            sx={{ mb: audit.findings.length ? 1 : 0 }}
                        >
                            <Typography sx={{ fontWeight: 850 }}>
                                Аудит: {DIRECT_RISK_LABELS[audit.overall_risk] || audit.overall_risk}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ color: 'var(--lm-muted)' }}
                            >
                                Правок: {formatNumber(audit.performance_summary?.quick_wins_count || 0)}
                            </Typography>
                        </Stack>
                        <Stack spacing={0.75}>
                            {audit.findings.slice(0, 3).map((finding) => (
                                <Box key={finding.id}>
                                    <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                                        {finding.severity} · {finding.title}
                                    </Typography>
                                    {finding.recommended_action && (
                                        <Typography
                                            variant="body2"
                                            sx={{ color: 'var(--lm-muted)', lineHeight: 1.45 }}
                                        >
                                            {finding.recommended_action}
                                        </Typography>
                                    )}
                                </Box>
                            ))}
                        </Stack>
                        {needsAdvertiserConfirmation && (
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                justifyContent="space-between"
                                alignItems={{ xs: 'stretch', sm: 'center' }}
                                gap={1.25}
                                sx={{
                                    mt: 1.5,
                                    pt: 1.5,
                                    borderTop: '1px solid var(--lm-border)',
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{ color: 'var(--lm-muted)', lineHeight: 1.45 }}
                                >
                                    Проверьте анкету рекламодателя в Директе.
                                </Typography>
                                <Button
                                    variant="outlined"
                                    onClick={onConfirmAdvertiserData}
                                    disabled={isConfirmLoading}
                                    sx={{
                                        borderRadius: LEADSTREAM_CONTROL_RADIUS,
                                        textTransform: 'none',
                                        fontWeight: LEADSTREAM_BUTTON_FONT_WEIGHT,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {isConfirmLoading ? 'Сохраняем...' : 'Данные проверены'}
                                </Button>
                            </Stack>
                        )}
                        {audit.can_apply_changes && (
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                justifyContent="space-between"
                                alignItems={{ xs: 'stretch', sm: 'center' }}
                                gap={1.25}
                                sx={{
                                    mt: 1.5,
                                    pt: 1.5,
                                    borderTop: '1px solid var(--lm-border)',
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{ color: 'var(--lm-muted)', lineHeight: 1.45 }}
                                >
                                    Можно применить безопасные правки после подтверждения.
                                </Typography>
                                <Button
                                    variant="contained"
                                    onClick={onApplyAudit}
                                    disabled={isApplyLoading}
                                    sx={{
                                        borderRadius: LEADSTREAM_CONTROL_RADIUS,
                                        textTransform: 'none',
                                        fontWeight: LEADSTREAM_BUTTON_FONT_WEIGHT,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {isApplyLoading ? (
                                        <>
                                            <CircularProgress
                                                color="inherit"
                                                size={16}
                                                sx={{ mr: 1 }}
                                            />
                                            Применяем...
                                        </>
                                    ) : (
                                        'Применить правки'
                                    )}
                                </Button>
                            </Stack>
                        )}
                    </Box>
                )}

                <Typography
                    variant="caption"
                    sx={{ color: 'var(--lm-muted)', lineHeight: 1.4 }}
                >
                    Прогноз не гарантия. Итог зависит от сайта, оффера и обработки заявок.
                </Typography>
            </Stack>
        </Box>
    );
};

const AiTrainingLoadingCard = () => {
    return (
        <Fade
            in
            timeout={300}
        >
            <Box
                sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background:
                        'radial-gradient(circle at 74% 18%, rgba(0, 68, 255, 0.11), transparent 26%), radial-gradient(circle at 58% 80%, rgba(126, 122, 255, 0.09), transparent 28%), radial-gradient(circle at 12% 88%, rgba(178, 54, 255, 0.08), transparent 32%), radial-gradient(circle at 46% 46%, rgba(66, 200, 255, 0.13), transparent 36%), linear-gradient(180deg, rgba(255, 255, 255, 0.99) 0%, rgba(255, 255, 255, 0.96) 100%)',
                    borderRadius: LEADSTREAM_CARD_RADIUS,
                    border: `1px solid ${LEADSTREAM_FLOW_BORDER}`,
                    boxShadow: LEADSTREAM_FLOW_SHADOW,
                    p: { xs: 2.5, sm: 3 },
                    mb: 3,
                }}
            >
                <Stack
                    alignItems="center"
                    spacing={3}
                >
                    <Box
                        sx={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Box
                            sx={{
                                animation: `${pulseAnimation} 2s ease-in-out infinite`,
                                borderRadius: '50%',
                                p: 2,
                            }}
                        >
                            <Box sx={{ animation: `${floatAnimation} 2s ease-in-out infinite` }}>
                                <Box
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        '& svg': {
                                            width: 64,
                                            height: 64,
                                            display: 'block',
                                        },
                                        '& path': {
                                            fill: 'var(--lm-accent-ink)',
                                        },
                                    }}
                                >
                                    <LeadsmaniaCollapsedLogo
                                        width={64}
                                        height={64}
                                    />
                                </Box>
                            </Box>
                        </Box>
                        <CircularProgress
                            size={80}
                            thickness={2}
                            sx={{
                                position: 'absolute',
                                color: 'rgba(0, 68, 255, 0.32)',
                            }}
                        />
                    </Box>

                    <Stack
                        alignItems="center"
                        spacing={1}
                    >
                        <Typography
                            variant="h5"
                            sx={{
                                color: 'var(--lm-text)',
                                fontWeight: 850,
                                fontSize: { xs: 22, sm: 24 },
                                lineHeight: 1.2,
                            }}
                        >
                            Обучение ИИ
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                textAlign: 'center',
                                maxWidth: 440,
                                color: 'var(--lm-muted)',
                                fontWeight: 600,
                                lineHeight: 1.45,
                            }}
                        >
                            Анализируем ваши ответы и уточняем фильтр лидов
                            <Box
                                component="span"
                                sx={{
                                    display: 'inline-block',
                                    ml: 0.5,
                                    '&::after': {
                                        content: '"..."',
                                        animation: `${dotsAnimation} 1.4s infinite`,
                                    },
                                }}
                            />
                        </Typography>
                    </Stack>

                    <Stack
                        direction="row"
                        spacing={0.5}
                    >
                        {LEADSTREAM_FLOW_COLORS.map((color, index) => (
                            <Box
                                key={color}
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    backgroundColor: color,
                                    animation: `${dotsAnimation} 1.4s infinite`,
                                    animationDelay: `${index * 0.2}s`,
                                }}
                            />
                        ))}
                    </Stack>
                </Stack>
            </Box>
        </Fade>
    );
};

const LeadStreamRightSidebar = ({
    hasAnalysis,
    examplesDone,
    examplesCount,
    examplesProgressPercent,
    hasPrompt,
    onReset,
}: {
    hasAnalysis: boolean;
    examplesDone: boolean;
    examplesCount: number;
    examplesProgressPercent: number;
    hasPrompt: boolean;
    onReset?: () => void;
}) => {
    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                p: '1rem 1rem 1rem 0',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    width: '100%',
                    backgroundColor: 'var(--lm-canvas)',
                    borderRadius: '24px',
                    p: 2,
                }}
            >
                <LeadStreamProgress
                    compact
                    hasAnalysis={hasAnalysis}
                    examplesDone={examplesDone}
                    examplesCount={examplesCount}
                    examplesProgressPercent={examplesProgressPercent}
                    hasPrompt={hasPrompt}
                    onReset={onReset}
                />
            </Box>
        </Box>
    );
};

export default function LeadStreamScreen() {
    const { enqueueSnackbar } = useSnackbar();
    const history = useHistory();
    const isMobile = useIsMobileSize();
    const { bonusPoints, businessDescription, isOnboardingCompleted, onboardingStepName } = useUserStore(
        ({ bonusPoints, business_description, isOnboardingCompleted, onboardingStepName }) => ({
            bonusPoints,
            businessDescription: business_description || '',
            isOnboardingCompleted,
            onboardingStepName,
        })
    );

    // При открытии leadstream в онбординге фиксируем третий шаг только после бизнес-анкеты.
    useEffect(() => {
        if (isOnboardingCompleted) {
            return;
        }

        // The launch form already collects the description; a second questionnaire
        // must not interrupt the visitor's selected launch.
        if (!businessDescription.trim()) return;

        if (onboardingStepName && onboardingStepName !== BEGIN_WORK_STEPS_IDS.CHOOSE_STREAM) {
            updateBeginWorkStep({
                currentStep: BEGIN_WORK_STEPS_IDS.CHOOSE_STREAM,
            });
        }
    }, [businessDescription, history, isOnboardingCompleted, onboardingStepName]);

    const flowLocation = useLocation();
    const requestedFlowId =
        new URLSearchParams(flowLocation.search).get('draft') || new URLSearchParams(flowLocation.search).get('flow');
    const [launchWorkspace, setLaunchWorkspace] = useState<Workspace | null>(null);
    const STORAGE_KEY = requestedFlowId ? 'leadstream_selection:flow:' + requestedFlowId : 'leadstream_selection';

    // Загрузка сохраненного выбора из localStorage
    const loadSavedSelection = (): {
        selectedCategories: Map<number, SelectedCategory>;
        formData: Partial<LeadStreamForm>;
        analysis: LeadStreamAnalysis | null;
        exampleChoices: Record<number, LeadExampleChoice>;
        exampleSkipReasons: Record<string, string>;
        showManualCategories: boolean;
        hasCheckoutStarted: boolean;
        manualCategoryFallback: boolean;
        aiTrainingComplete: boolean;
        hasSavedState: boolean;
    } => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                const categoriesMap = new Map<number, SelectedCategory>();
                const savedAnalysis = parsed.analysis || null;
                const savedExampleChoices = parsed.exampleChoices || {};
                const savedManualCategoryFallback = Boolean(parsed.manualCategoryFallback);

                if (parsed.selectedCategories && Array.isArray(parsed.selectedCategories)) {
                    parsed.selectedCategories.forEach((item: any) => {
                        if (item.category && item.category.id) {
                            categoriesMap.set(item.category.id, {
                                category: item.category,
                                packageSize: item.packageSize || null,
                                prices: item.prices || [],
                            });
                        }
                    });
                }

                return {
                    selectedCategories: categoriesMap,
                    formData: parsed.formData || {},
                    analysis: savedAnalysis,
                    exampleChoices: savedExampleChoices,
                    exampleSkipReasons: parsed.exampleSkipReasons || {},
                    showManualCategories: Boolean(parsed.showManualCategories),
                    hasCheckoutStarted: Boolean(parsed.hasCheckoutStarted),
                    manualCategoryFallback: savedManualCategoryFallback,
                    aiTrainingComplete:
                        Boolean(parsed.aiTrainingComplete) ||
                        hasCompletedLeadExamplesReview(savedAnalysis, savedExampleChoices, savedManualCategoryFallback),
                    hasSavedState: true,
                };
            }
        } catch (error) {
            console.error('Ошибка загрузки сохраненного выбора:', error);
        }

        return {
            selectedCategories: new Map(),
            formData: {},
            analysis: null,
            exampleChoices: {},
            exampleSkipReasons: {},
            showManualCategories: false,
            hasCheckoutStarted: false,
            manualCategoryFallback: false,
            aiTrainingComplete: false,
            hasSavedState: false,
        };
    };

    const [categories, setCategories] = useState<LeadCategory[]>([]);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [categoriesError, setCategoriesError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const savedSelection = loadSavedSelection();
    const publicLaunchIntentRef = React.useRef(requestedFlowId ? null : readPublicLaunchIntent());
    const publicLaunchIntentAppliedRef = React.useRef(false);
    const shouldRestoreScrollRef = React.useRef(savedSelection.hasSavedState);
    const restoredLeadstreamScrollRef = React.useRef(false);
    const [selectedCategories, setSelectedCategories] = useState<Map<number, SelectedCategory>>(
        savedSelection.selectedCategories
    );
    const [categoryPrices, setCategoryPrices] = useState<Map<number, LeadPackagePrice[]>>(new Map());
    const [billingMode, setBillingMode] = useState<'monthly' | 'one_time'>('one_time');
    const [loadingPrices, setLoadingPrices] = useState<Set<number>>(new Set());
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasCheckoutStarted, setHasCheckoutStarted] = useState(savedSelection.hasCheckoutStarted);
    const [promoDiscount, setPromoDiscount] = useState<number | null>(null);
    const [promoError, setPromoError] = useState<string | null>(null);
    const [isValidatingPromo, setIsValidatingPromo] = useState(false);
    const [setupDescription, setSetupDescription] = useState(savedSelection.formData.setup_description || '');
    const [setupGeo, setSetupGeo] = useState(savedSelection.formData.setup_geo || '');
    const [setupAddressInput, setSetupAddress] = useState(savedSelection.formData.setup_address || '');
    const [setupAddressEnabled, setSetupAddressEnabled] = useState(Boolean(savedSelection.formData.setup_address));
    // Keep the draft when toggled off, but exclude it from geocoding, analysis and saved filters.
    const setupAddress = setupAddressEnabled ? setupAddressInput : '';
    useEffect(() => {
        if (!requestedFlowId) return;
        let live = true;
        setLaunchWorkspace(null);
        IntentService.list()
            .then(({ workspaces }) => {
                if (!live) return;
                const flow = workspaces.find((w) => String(w.id) === requestedFlowId);
                if (!flow) {
                    enqueueSnackbar('Поток недоступен. Откройте его заново из меню.', { variant: 'error' });
                    return;
                }
                if (flow.streams.length) {
                    history.replace('/leadstream/' + flow.streams[0].id);
                    return;
                }
                setLaunchWorkspace(flow);
                if (!savedSelection.hasSavedState) {
                    setSetupDescription([flow.profile.description, flow.profile.criteria,
                        flow.profile.exclusions ? 'Не подходят: ' + flow.profile.exclusions : ''].filter(Boolean).join('\n\n'));
                    setSetupGeo(flow.profile.region || '');
                    const size = flow.profile.selection?.package_size;
                    if (flow.profile.category_id && size && [30, 50, 100, 200, 500].includes(size)) {
                        publicLaunchIntentRef.current = { categoryId: flow.profile.category_id, packageSize: size, capturedAt: new Date().toISOString() };
                    }
                }
            })
            .catch(() => {
                if (live)
                    enqueueSnackbar('Не удалось загрузить профиль потока. Обновите страницу.', { variant: 'error' });
            });
        return () => {
            live = false;
        };
    }, [requestedFlowId]);
    const [setupSelectedLocation, setSetupSelectedLocation] = useState<ILocation | null>(null);
    const [setupLocationOptions, setSetupLocationOptions] = useState<ILocation[]>([]);
    const [setupLocationOpen, setSetupLocationOpen] = useState(false);
    const [setupLocationLoading, setSetupLocationLoading] = useState(false);
    const [setupSelectedAddress, setSetupSelectedAddress] = useState<ILocation | null>(null);
    const [setupAddressOptions, setSetupAddressOptions] = useState<ILocation[]>([]);
    const [setupAddressOpen, setSetupAddressOpen] = useState(false);
    const [setupAddressLoading, setSetupAddressLoading] = useState(false);
    const [analysis, setAnalysis] = useState<LeadStreamAnalysis | null>(savedSelection.analysis);
    const [analysisLoading, setAnalysisLoading] = useState(false);
    const [analysisError, setAnalysisError] = useState<string | null>(null);
    const [directAuditResult, setDirectAuditResult] = useState<IYandexDirectAuditRun | null>(null);
    const [directAuditLoading, setDirectAuditLoading] = useState(false);
    const [directApplyLoading, setDirectApplyLoading] = useState(false);
    const [directConfirmLoading, setDirectConfirmLoading] = useState(false);
    const [directConnectionStatus, setDirectConnectionStatus] = useState(
        savedSelection.analysis?.direct_forecast?.connection_status || ''
    );
    const [exampleChoices, setExampleChoices] = useState<Record<number, LeadExampleChoice>>(
        savedSelection.exampleChoices
    );
    const [exampleSkipReasons, setExampleSkipReasons] = useState<Record<string, string>>(
        savedSelection.exampleSkipReasons
    );
    const [showManualCategories, setShowManualCategories] = useState(savedSelection.showManualCategories);
    const [manualCategoryFallback, setManualCategoryFallback] = useState(savedSelection.manualCategoryFallback);
    const [aiTrainingComplete, setAiTrainingComplete] = useState(savedSelection.aiTrainingComplete);
    const [aiTrainingRequested, setAiTrainingRequested] = useState(false);
    const aiTrainingBlockRef = useRef<HTMLDivElement | null>(null);
    const setupFormRef = React.useRef<HTMLDivElement | null>(null);
    const setupDescriptionRef = React.useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);
    const setupGeoSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const setupAddressSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const examplesSectionRef = React.useRef<HTMLDivElement | null>(null);
    const manualCategoriesSectionRef = React.useRef<HTMLDivElement | null>(null);
    const launchSectionRef = React.useRef<HTMLDivElement | null>(null);
    const directCallbackHandledRef = React.useRef(false);

    const { control, handleSubmit, watch, setValue } = useForm<LeadStreamForm>({
        defaultValues: {
            paymentMethod: 'UMoney',
            bonus: false,
            auto_renewal: Boolean(savedSelection.formData.auto_renewal_touched && savedSelection.formData.auto_renewal),
            auto_renewal_touched: Boolean(savedSelection.formData.auto_renewal_touched),
            promo_code: savedSelection.formData.promo_code || '',
            ai_prompt: savedSelection.formData.ai_prompt || '',
        },
    });

    // Сохранение выбора в localStorage
    const saveSelection = (
        categories: Map<number, SelectedCategory>,
        formData: Partial<LeadStreamForm>,
        analysisOverride: LeadStreamAnalysis | null = analysis,
        stateOverrides: Partial<{
            showManualCategories: boolean;
            hasCheckoutStarted: boolean;
            manualCategoryFallback: boolean;
            aiTrainingComplete: boolean;
        }> = {}
    ) => {
        try {
            const categoriesArray = Array.from(categories.values()).map((selected) => ({
                category: selected.category,
                packageSize: selected.packageSize,
                prices: selected.prices,
            }));

            localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify({
                    revision: Date.now().toString(36) + Math.random().toString(36).slice(2),
                    selectedCategories: categoriesArray,
                    formData: {
                        promo_code: formData.promo_code || '',
                        auto_renewal: Boolean(formData.auto_renewal_touched && formData.auto_renewal),
                        auto_renewal_touched: Boolean(formData.auto_renewal_touched),
                        ai_prompt: formData.ai_prompt || '',
                        setup_description: formData.setup_description || '',
                        setup_geo: formData.setup_geo || '',
                        setup_address: formData.setup_address || '',
                    },
                    analysis: analysisOverride,
                    exampleChoices,
                    exampleSkipReasons,
                    showManualCategories: stateOverrides.showManualCategories ?? showManualCategories,
                    hasCheckoutStarted: stateOverrides.hasCheckoutStarted ?? hasCheckoutStarted,
                    manualCategoryFallback: stateOverrides.manualCategoryFallback ?? manualCategoryFallback,
                    aiTrainingComplete: stateOverrides.aiTrainingComplete ?? aiTrainingComplete,
                })
            );
        } catch (error) {
            console.error('Ошибка сохранения выбора:', error);
        }
    };

    // Загрузка категорий с фильтрацией
    useEffect(() => {
        setCategoriesLoading(true);
        setCategoriesError(null);
        LeadStreamsService.getCategories()
            .then((data) => {
                // Фильтруем только активные и доступные для пакетов категории
                const filtered = data.filter((cat) => cat.is_active && cat.is_available_for_packages);
                setCategories(filtered);
                setCategoriesLoading(false);
            })
            .catch((error) => {
                console.error('Ошибка загрузки категорий:', error);
                const errorMessage =
                    error?.response?.data?.error ||
                    error?.response?.data?.message ||
                    'Не удалось загрузить категории. Попробуйте обновить страницу.';
                setCategoriesError(errorMessage);
                enqueueSnackbar(errorMessage, { variant: 'error' });
                setCategoriesLoading(false);
            });
    }, [enqueueSnackbar]);

    useEffect(() => {
        if (setupGeoSearchTimeoutRef.current) {
            clearTimeout(setupGeoSearchTimeoutRef.current);
        }

        const query = setupGeo.trim();
        const selectedLabel = setupSelectedLocation ? getLocationDisplayName(setupSelectedLocation) : '';

        if (!query || isWideGeo(query) || query === selectedLabel) {
            setSetupLocationOptions([]);
            setSetupLocationOpen(false);
            setSetupLocationLoading(false);
            return;
        }

        let cancelled = false;
        setSetupLocationLoading(true);
        setupGeoSearchTimeoutRef.current = setTimeout(async () => {
            try {
                const locations = await GeoService.searchCitiesCombined(query, 8);
                if (cancelled) {
                    return;
                }

                setSetupLocationOptions(locations);
                setSetupLocationOpen(locations.length > 0);
            } catch {
                if (!cancelled) {
                    setSetupLocationOptions([]);
                    setSetupLocationOpen(false);
                }
            } finally {
                if (!cancelled) {
                    setSetupLocationLoading(false);
                }
            }
        }, 300);

        return () => {
            cancelled = true;
            if (setupGeoSearchTimeoutRef.current) {
                clearTimeout(setupGeoSearchTimeoutRef.current);
            }
        };
    }, [setupGeo, setupSelectedLocation]);

    useEffect(() => {
        if (setupAddressSearchTimeoutRef.current) {
            clearTimeout(setupAddressSearchTimeoutRef.current);
        }

        const query = setupAddress.trim();
        const selectedLabel = setupSelectedAddress ? getLocationDisplayName(setupSelectedAddress) : '';

        if (!query || query.length < 5 || query === selectedLabel) {
            setSetupAddressOptions([]);
            setSetupAddressOpen(false);
            setSetupAddressLoading(false);
            return;
        }

        let cancelled = false;
        const geoPrefix = setupGeo.trim() && !isWideGeo(setupGeo) ? setupGeo.trim() : '';
        const geocodeQuery =
            geoPrefix && !query.toLowerCase().includes(geoPrefix.toLowerCase()) ? `${geoPrefix}, ${query}` : query;

        setSetupAddressLoading(true);
        setupAddressSearchTimeoutRef.current = setTimeout(async () => {
            try {
                const locations = await GeoService.geocodeAddress(
                    geocodeQuery,
                    5,
                    setupSelectedLocation?.country_code ? [setupSelectedLocation.country_code] : undefined
                );
                if (cancelled) {
                    return;
                }

                setSetupAddressOptions(locations);
                setSetupAddressOpen(locations.length > 0);
            } catch {
                if (!cancelled) {
                    setSetupAddressOptions([]);
                    setSetupAddressOpen(false);
                }
            } finally {
                if (!cancelled) {
                    setSetupAddressLoading(false);
                }
            }
        }, 300);

        return () => {
            cancelled = true;
            if (setupAddressSearchTimeoutRef.current) {
                clearTimeout(setupAddressSearchTimeoutRef.current);
            }
        };
    }, [setupAddress, setupGeo, setupSelectedAddress, setupSelectedLocation]);

    // Загрузка цен для выбранных категорий
    useEffect(() => {
        selectedCategories.forEach((selected, categoryId) => {
            if (!categoryPrices.has(categoryId) && !loadingPrices.has(categoryId)) {
                setLoadingPrices((prev) => new Set(prev).add(categoryId));
                LeadStreamsService.getCategoryPrices(categoryId)
                    .then((prices) => {
                        // Фильтруем только активные цены
                        const activePrices = prices.filter((p) => p.is_active);
                        setCategoryPrices((prev) => {
                            const newMap = new Map(prev);
                            newMap.set(categoryId, activePrices);
                            return newMap;
                        });

                        const selectedPackageExists = selected.packageSize
                            ? activePrices.some((p) => p.package_size === selected.packageSize)
                            : false;
                        const defaultPrice =
                            activePrices.find((p) => p.package_size === 100) ||
                            activePrices.reduce<LeadPackagePrice | null>(
                                (min, p) => (!min || p.package_size < min.package_size ? p : min),
                                null
                            );

                        // Если текущий выбор уже валиден, сохраняем его.
                        if (selectedPackageExists) {
                            setLoadingPrices((prev) => {
                                const newSet = new Set(prev);
                                newSet.delete(categoryId);
                                return newSet;
                            });
                            return;
                        }

                        // По умолчанию ведём к пакету 100 лидов, если он доступен.
                        if (defaultPrice) {
                            setSelectedCategories((prev) => {
                                const newMap = new Map(prev);
                                const selectedCat = newMap.get(categoryId);
                                if (selectedCat) {
                                    newMap.set(categoryId, {
                                        ...selectedCat,
                                        packageSize: defaultPrice.package_size,
                                    });
                                }
                                return newMap;
                            });
                        }

                        setLoadingPrices((prev) => {
                            const newSet = new Set(prev);
                            newSet.delete(categoryId);
                            return newSet;
                        });
                    })
                    .catch((error) => {
                        console.error(`Ошибка загрузки цен для категории ${categoryId}:`, error);
                        enqueueSnackbar(`Не удалось загрузить цены для категории "${selected.category.name}"`, {
                            variant: 'error',
                        });
                        setLoadingPrices((prev) => {
                            const newSet = new Set(prev);
                            newSet.delete(categoryId);
                            return newSet;
                        });
                    });
            }
        });
    }, [selectedCategories, categoryPrices, loadingPrices, enqueueSnackbar]);

    // Фильтрация категорий по поисковому запросу
    const filteredCategories = useMemo(() => {
        if (!searchQuery.trim()) return categories;
        const query = searchQuery.toLowerCase();
        return categories.filter(
            (cat) =>
                cat.name.toLowerCase().includes(query) ||
                (cat.description && cat.description.toLowerCase().includes(query))
        );
    }, [categories, searchQuery]);

    const getCurrentFormData = (aiPromptOverride?: string): Partial<LeadStreamForm> => ({
        promo_code: watch('promo_code'),
        auto_renewal: watch('auto_renewal'),
        auto_renewal_touched: watch('auto_renewal_touched'),
        ai_prompt: aiPromptOverride ?? watch('ai_prompt'),
        setup_description: setupDescription,
        setup_geo: setupGeo,
        setup_address: setupAddress,
    });

    useEffect(() => {
        const intent = publicLaunchIntentRef.current;
        if (!intent || !categories.length || publicLaunchIntentAppliedRef.current) return;

        const category = categories.find((item) => item.id === intent.categoryId);
        if (!category) {
            publicLaunchIntentRef.current = null;
            clearPublicLaunchIntent(intent);
            enqueueSnackbar('Выбранная на сайте категория сейчас недоступна', { variant: 'warning' });
            return;
        }

        publicLaunchIntentAppliedRef.current = true;
        setSelectedCategories((current) => {
            const next = new Map(current);
            next.set(category.id, {
                category,
                packageSize: intent.packageSize,
                prices: categoryPrices.get(category.id) || [],
            });
            saveSelection(next, getCurrentFormData());
            return next;
        });
        setShowManualCategories(true);
    }, [categories, categoryPrices, enqueueSnackbar, launchWorkspace]);

    useEffect(() => {
        const intent = publicLaunchIntentRef.current;
        if (!intent || !publicLaunchIntentAppliedRef.current || !categoryPrices.has(intent.categoryId)) return;

        const prices = categoryPrices.get(intent.categoryId) || [];
        const packageExists = prices.some((price) => price.package_size === intent.packageSize);
        publicLaunchIntentRef.current = null;
        publicLaunchIntentAppliedRef.current = false;
        clearPublicLaunchIntent(intent);

        if (!packageExists) {
            enqueueSnackbar('Выбранный пакет недоступен. Показан ближайший вариант', { variant: 'warning' });
            return;
        }
        enqueueSnackbar(`Выбран пакет на ${intent.packageSize} лидов`, { variant: 'success' });
    }, [categoryPrices, enqueueSnackbar]);

    useEffect(() => {
        if (!categories.length || (!selectedCategories.size && !analysis)) {
            return;
        }

        const categoriesById = new Map(categories.map((category) => [category.id, category]));
        let nextSelectedCategories = selectedCategories;
        let selectionChanged = false;

        selectedCategories.forEach((selected, categoryId) => {
            const freshCategory = categoriesById.get(categoryId);
            if (!freshCategory || !hasCategorySnapshotChanged(selected.category, freshCategory)) {
                return;
            }

            if (!selectionChanged) {
                nextSelectedCategories = new Map(selectedCategories);
                selectionChanged = true;
            }
            nextSelectedCategories.set(categoryId, {
                ...selected,
                category: freshCategory,
            });
        });

        let nextAnalysis = analysis;
        if (analysis) {
            const freshBestCategory = analysis.bestCategory
                ? categoriesById.get(analysis.bestCategory.id) || analysis.bestCategory
                : null;
            const freshSuggestedCategories = analysis.suggestedCategories.map(
                (category) => categoriesById.get(category.id) || category
            );
            const analysisChanged =
                (analysis.bestCategory &&
                    freshBestCategory &&
                    hasCategorySnapshotChanged(analysis.bestCategory, freshBestCategory)) ||
                analysis.suggestedCategories.some((category, index) =>
                    hasCategorySnapshotChanged(category, freshSuggestedCategories[index])
                );

            if (analysisChanged) {
                nextAnalysis = {
                    ...analysis,
                    bestCategory: freshBestCategory,
                    suggestedCategories: freshSuggestedCategories,
                };
                setAnalysis(nextAnalysis);
            }
        }

        if (selectionChanged) {
            setSelectedCategories(nextSelectedCategories);
            saveSelection(nextSelectedCategories, getCurrentFormData(), nextAnalysis);
        }
    }, [analysis, categories, selectedCategories]);

    const getPromptCategory = (preferredCategory?: LeadCategory | null) =>
        preferredCategory || analysis?.bestCategory || Array.from(selectedCategories.values())[0]?.category || null;

    const selectSuggestedCategory = (
        category: LeadCategory,
        aiPromptOverride?: string,
        analysisOverride: LeadStreamAnalysis | null = analysis
    ) => {
        const nextMap = new Map<number, SelectedCategory>();
        nextMap.set(category.id, {
            category,
            packageSize: 100,
            prices: categoryPrices.get(category.id) || [],
        });
        setSelectedCategories(nextMap);
        saveSelection(nextMap, getCurrentFormData(aiPromptOverride), analysisOverride);
    };

    const updatePromptFromReview = (
        nextChoices: Record<number, LeadExampleChoice>,
        nextSkipReasons: Record<string, string>,
        preferredCategory?: LeadCategory | null,
        targetAnalysis: LeadStreamAnalysis | null = analysis
    ) => {
        if (!targetAnalysis) {
            return '';
        }

        const nextPrompt = buildAiPrompt({
            businessDescription: setupDescription,
            geo: buildGeoContext(setupGeo, setupAddress) || setupGeo,
            category: getPromptCategory(preferredCategory || targetAnalysis.bestCategory),
            examples: targetAnalysis.lead_examples,
            choices: nextChoices,
            skipReasons: nextSkipReasons,
        });

        setValue('ai_prompt', nextPrompt, { shouldDirty: true });
        saveSelection(selectedCategories, getCurrentFormData(nextPrompt));
        return nextPrompt;
    };

    const persistSelectedLeads = (
        nextChoices: Record<number, LeadExampleChoice>,
        targetAnalysis: LeadStreamAnalysis | null = analysis
    ) => {
        if (!targetAnalysis?.project_id) {
            return;
        }

        const selectedLeadIds = targetAnalysis.lead_examples
            .filter((example) => nextChoices[example.id] === 'fit')
            .map((example) => example.id);

        ProjectsService.saveSelectedLeads(targetAnalysis.project_id, selectedLeadIds).catch(() => undefined);
    };

    const requestAiTrainingAfterLeadDecision = (
        nextChoices: Record<number, LeadExampleChoice>,
        targetAnalysis: LeadStreamAnalysis | null = analysis
    ) => {
        if (
            manualCategoryFallback ||
            !hasCompletedLeadExamplesReview(targetAnalysis, nextChoices, manualCategoryFallback)
        ) {
            return;
        }

        setAiTrainingComplete(false);
        setAiTrainingRequested(true);
    };

    const clearSetupAddressSelection = () => {
        setSetupSelectedAddress(null);
        setSetupAddressOptions([]);
        setSetupAddressOpen(false);
    };

    const handleSetupGeoChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSetupGeo(event.target.value);
        setSetupSelectedLocation(null);
        clearSetupAddressSelection();
    };

    const handleSetupGeoQuickReply = (reply: string) => {
        setSetupGeo(reply);
        setSetupSelectedLocation(null);
        setSetupLocationOptions([]);
        setSetupLocationOpen(false);
        clearSetupAddressSelection();
    };

    const handleSetupLocationSelect = (location: ILocation) => {
        setSetupSelectedLocation(location);
        setSetupGeo(getLocationDisplayName(location));
        setSetupLocationOptions([]);
        setSetupLocationOpen(false);
        clearSetupAddressSelection();
    };

    const handleSetupAddressChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSetupAddress(event.target.value);
        setSetupSelectedAddress(null);
    };

    const handleSetupAddressSelect = (location: ILocation) => {
        setSetupSelectedAddress(location);
        setSetupAddress(location.formatted_address || getLocationDisplayName(location));
        setSetupAddressOptions([]);
        setSetupAddressOpen(false);
    };

    const getSetupAddressCityName = () => {
        if (setupSelectedAddress?.city_name) {
            return setupSelectedAddress.city_name;
        }
        if (setupSelectedLocation?.name) {
            return setupSelectedLocation.name;
        }
        const geo = setupGeo.trim();
        if (!geo || isWideGeo(geo)) {
            return undefined;
        }
        return geo.split(',')[0]?.trim() || geo;
    };

    const getSetupSelectedLocationsPayload = (): Array<{ name: string; [key: string]: any }> => {
        const payload: Array<{ name: string; [key: string]: any }> = [];

        if (setupGeo.trim() && !isWideGeo(setupGeo)) {
            if (setupSelectedLocation) {
                payload.push({
                    name: setupSelectedLocation.name,
                    display_name: getLocationDisplayName(setupSelectedLocation),
                    country_code: setupSelectedLocation.country_code || undefined,
                    region: setupSelectedLocation.region || undefined,
                    geoname_id: setupSelectedLocation.geoname_id || undefined,
                });
            } else {
                payload.push({ name: setupGeo.trim() });
            }
        }

        if (setupAddress.trim()) {
            const addressDisplay = setupSelectedAddress
                ? setupSelectedAddress.formatted_address || getLocationDisplayName(setupSelectedAddress)
                : buildGeoContext(setupGeo, setupAddress) || setupAddress.trim();

            payload.push({
                kind: 'address',
                name: setupSelectedAddress?.name || setupAddress.trim(),
                address: setupSelectedAddress?.address || setupAddress.trim(),
                display_name: addressDisplay,
                formatted_address: addressDisplay,
                country_code: setupSelectedAddress?.country_code || setupSelectedLocation?.country_code || undefined,
                country_name: setupSelectedAddress?.country || setupSelectedLocation?.country || undefined,
                region: setupSelectedAddress?.region || setupSelectedLocation?.region || undefined,
                region_name: setupSelectedAddress?.region || setupSelectedLocation?.region || undefined,
                city_name: getSetupAddressCityName(),
                district_name: setupSelectedAddress?.district_name || undefined,
                street_name: setupSelectedAddress?.street_name || undefined,
                house: setupSelectedAddress?.house || undefined,
                latitude: setupSelectedAddress?.coordinates.lat || undefined,
                longitude: setupSelectedAddress?.coordinates.lng || undefined,
                precision: setupSelectedAddress?.precision || undefined,
                source: setupSelectedAddress?.source || 'manual_address',
            });
        }

        return payload;
    };

    const runSetupAnalysis = async () => {
        if (!setupDescription.trim()) {
            enqueueSnackbar('Опишите, какие заявки хотите получать', { variant: 'warning' });
            return;
        }

        if (categoriesLoading) {
            enqueueSnackbar('Подождите, пока загрузятся категории', { variant: 'info' });
            return;
        }

        setAnalysisLoading(true);
        setAnalysisError(null);
        setAnalysis(null);
        setDirectAuditResult(null);
        setDirectConnectionStatus('');
        setDirectApplyLoading(false);
        setDirectConfirmLoading(false);
        setExampleChoices({});
        setExampleSkipReasons({});
        setShowManualCategories(false);
        setSelectedCategories(new Map());
        setHasCheckoutStarted(false);
        setManualCategoryFallback(false);
        setAiTrainingComplete(false);
        setAiTrainingRequested(false);
        setValue('ai_prompt', '', { shouldDirty: true });
        saveSelection(new Map(), getCurrentFormData(''), null);

        try {
            const geoContext = buildGeoContext(setupGeo, setupAddress) || setupGeo;
            const description = buildAnalysisDescription(setupDescription, setupGeo, setupAddress);
            const project = await ProjectsService.createProject({
                name: formatProjectName(setupDescription),
                audience_description: description,
                project_goal: 'attract_clients',
                source_product: 'leadsmania',
                bundles: [],
                keywords: [],
            });
            const selectedLocations = getSetupSelectedLocationsPayload();
            const useGeo = selectedLocations.length > 0;
            const result = await NicheAnalysisService.analyzeNiche({
                service_description: description,
                project_goal: 'attract_clients',
                project_id: project.id,
                use_geolocation: useGeo,
                selected_locations: useGeo ? selectedLocations : [],
            });
            const suggestedCategories = pickSuggestedCategories(
                result.category,
                categories,
                result.keywords,
                setupDescription
            );
            const bestCategory = suggestedCategories[0] || null;
            const nextAnalysis: LeadStreamAnalysis = {
                ...result,
                suggestedCategories,
                bestCategory,
            };
            const nextPrompt = buildAiPrompt({
                businessDescription: setupDescription,
                geo: geoContext,
                category: bestCategory,
                examples: result.lead_examples,
                choices: {},
                skipReasons: {},
            });

            setAnalysis(nextAnalysis);
            setDirectConnectionStatus(result.direct_forecast?.connection_status || '');
            setValue('ai_prompt', nextPrompt, { shouldDirty: true });
            if (bestCategory) {
                selectSuggestedCategory(bestCategory, nextPrompt, nextAnalysis);
                enqueueSnackbar(`Подобрали категорию: ${bestCategory.name}`, { variant: 'success' });
            } else {
                setShowManualCategories(true);
                saveSelection(new Map(), getCurrentFormData(nextPrompt), nextAnalysis);
                enqueueSnackbar('Анализ готов. Выберите категорию ниже вручную.', { variant: 'info' });
            }
        } catch (error: any) {
            console.error('Ошибка анализа потока:', error);
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Не удалось провести анализ. Проверьте описание и попробуйте еще раз.';
            setAnalysisError(errorMessage);
            enqueueSnackbar(errorMessage, { variant: 'error' });
        } finally {
            setAnalysisLoading(false);
        }
    };

    const handleConnectYandexDirect = async () => {
        try {
            const result = await YandexDirectService.startOAuth(window.location.pathname + window.location.search);
            window.location.href = result.authorization_url;
        } catch (error: any) {
            console.error('Ошибка подключения Яндекс Директа:', error);
            const message =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                'Не удалось начать подключение Директа';
            enqueueSnackbar(message, { variant: 'error' });
        }
    };

    const handleRunDirectAudit = async () => {
        if (!analysis?.project_id) {
            return;
        }

        setDirectAuditLoading(true);
        try {
            const audit = await YandexDirectService.runAudit(analysis.project_id);
            setDirectAuditResult(audit);
            if (audit.overall_risk === 'red') {
                enqueueSnackbar('Аудит нашел блокеры перед изменениями', { variant: 'warning' });
            } else if (audit.can_apply_changes) {
                enqueueSnackbar('Аудит готов. Есть правки для применения.', { variant: 'success' });
            } else {
                enqueueSnackbar('Аудит готов. Сначала закройте обязательные проверки.', { variant: 'info' });
            }
        } catch (error: any) {
            console.error('Ошибка аудита Яндекс Директа:', error);
            const message =
                error?.response?.data?.error || error?.response?.data?.message || 'Не удалось запустить аудит Директа';
            enqueueSnackbar(message, { variant: 'error' });
        } finally {
            setDirectAuditLoading(false);
        }
    };

    useEffect(() => {
        if (directCallbackHandledRef.current) {
            return;
        }

        const params = new URLSearchParams(window.location.search);
        const directStatus = params.get('direct');
        if (!directStatus) {
            return;
        }

        directCallbackHandledRef.current = true;
        params.delete('direct');
        const nextSearch = params.toString();
        history.replace(`${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}`);

        if (directStatus === 'connected') {
            setDirectConnectionStatus('connected');
            if (analysis?.direct_forecast) {
                const nextAnalysis = {
                    ...analysis,
                    direct_forecast: {
                        ...analysis.direct_forecast,
                        connection_status: 'connected',
                        next_action: 'run_direct_audit',
                    },
                };
                setAnalysis(nextAnalysis);
                saveSelection(selectedCategories, getCurrentFormData(), nextAnalysis);
            }
            enqueueSnackbar('Яндекс Директ подключен', { variant: 'success' });
            if (analysis?.project_id) {
                handleRunDirectAudit();
            }
            return;
        }

        enqueueSnackbar('Не удалось подключить Яндекс Директ', { variant: 'error' });
    }, [
        analysis,
        enqueueSnackbar,
        getCurrentFormData,
        handleRunDirectAudit,
        history,
        saveSelection,
        selectedCategories,
    ]);

    const handleConfirmDirectAdvertiserData = async () => {
        const confirmed = window.confirm(
            'Подтвердить, что вы рекламодатель или уполномочены работать с этим аккаунтом, а данные рекламодателя в Директе корректны?'
        );
        if (!confirmed) {
            return;
        }

        setDirectConfirmLoading(true);
        try {
            await YandexDirectService.confirmAdvertiserData({
                project_id: analysis?.project_id,
                confirmed_flags: {
                    i_am_advertiser_or_authorized: true,
                    i_confirm_advertiser_data: true,
                    i_accept_advertising_law_responsibility: true,
                },
            });
            enqueueSnackbar('Данные рекламодателя подтверждены', { variant: 'success' });
            await handleRunDirectAudit();
        } catch (error: any) {
            console.error('Ошибка подтверждения данных рекламодателя:', error);
            const message =
                error?.response?.data?.error || error?.response?.data?.message || 'Не удалось сохранить подтверждение';
            enqueueSnackbar(message, { variant: 'error' });
        } finally {
            setDirectConfirmLoading(false);
        }
    };

    const handleApplyDirectAudit = async () => {
        if (!analysis?.project_id || !directAuditResult) {
            return;
        }

        const findingIds = directAuditResult.findings
            .filter((finding) => finding.can_auto_fix && finding.status === 'open')
            .map((finding) => finding.id);

        if (!findingIds.length) {
            enqueueSnackbar('Нет автоматических правок для применения', { variant: 'info' });
            return;
        }

        const confirmed = window.confirm(
            'Применить правки в клиентском аккаунте Яндекс Директа? Вы подтверждаете полномочия и корректность данных рекламодателя.'
        );
        if (!confirmed) {
            return;
        }

        setDirectApplyLoading(true);
        try {
            const result = await YandexDirectService.applyAuditRecommendations(
                analysis.project_id,
                directAuditResult.id,
                {
                    finding_ids: findingIds,
                    confirmed_flags: {
                        i_am_advertiser_or_authorized: true,
                        i_confirm_advertiser_data: true,
                        i_confirm_budget_and_changes: true,
                        i_understand_changes_are_made_in_client_account: true,
                    },
                }
            );

            if (result.status === 'requires_direct_adapter') {
                enqueueSnackbar('Подтверждение сохранено. Нужен боевой Direct API adapter.', { variant: 'info' });
            } else if (result.status === 'applied') {
                enqueueSnackbar('Правки применены', { variant: 'success' });
                await handleRunDirectAudit();
            } else {
                enqueueSnackbar(result.message || 'Запрос на применение обработан', { variant: 'info' });
            }
        } catch (error: any) {
            console.error('Ошибка применения правок Яндекс Директа:', error);
            const message =
                error?.response?.data?.error || error?.response?.data?.message || 'Не удалось применить правки Директа';
            enqueueSnackbar(message, { variant: 'error' });
        } finally {
            setDirectApplyLoading(false);
        }
    };

    const handleExampleChoice = (exampleId: number, choice: LeadExampleChoice) => {
        const nextChoices = { ...exampleChoices, [exampleId]: choice };
        setExampleChoices(nextChoices);
        updatePromptFromReview(nextChoices, exampleSkipReasons);
        persistSelectedLeads(nextChoices);
        requestAiTrainingAfterLeadDecision(nextChoices);
    };

    const handleSkipReason = (exampleId: number, reason: string) => {
        const nextChoices = { ...exampleChoices, [exampleId]: 'skip' as LeadExampleChoice };
        const nextSkipReasons = { ...exampleSkipReasons, [String(exampleId)]: reason };
        setExampleChoices(nextChoices);
        setExampleSkipReasons(nextSkipReasons);
        updatePromptFromReview(nextChoices, nextSkipReasons);
        persistSelectedLeads(nextChoices);
        requestAiTrainingAfterLeadDecision(nextChoices);
    };

    // Переключение выбора категории
    const toggleCategory = (category: LeadCategory) => {
        setSelectedCategories((prev) => {
            const newMap = new Map(prev);
            if (newMap.has(category.id)) {
                newMap.delete(category.id);
                setCategoryPrices((prev) => {
                    const newMap = new Map(prev);
                    newMap.delete(category.id);
                    return newMap;
                });
            } else {
                newMap.set(category.id, {
                    category,
                    packageSize: 100,
                    prices: [],
                });
            }
            // Сохраняем выбор
            setTimeout(() => {
                saveSelection(newMap, getCurrentFormData());
            }, 0);
            return newMap;
        });
    };

    // Выбор пакета для категории
    const selectPackage = (categoryId: number, packageSize: number) => {
        setSelectedCategories((prev) => {
            const newMap = new Map(prev);
            const selected = newMap.get(categoryId);
            if (selected) {
                newMap.set(categoryId, {
                    ...selected,
                    packageSize,
                });
            }
            // Сохраняем выбор
            setTimeout(() => {
                saveSelection(newMap, getCurrentFormData());
            }, 0);
            return newMap;
        });
    };

    const monthlyCheckoutAvailable = selectedCategories.size === 1 &&
        Array.from(selectedCategories.values()).every((selected) =>
            (categoryPrices.get(selected.category.id) || []).some((price) => Boolean(price.monthly_price_per_lead))
        );
    const billingSelection = Array.from(selectedCategories.values())[0];
    const billingPrice = billingSelection
        ? (categoryPrices.get(billingSelection.category.id) || []).find(
            (price) => price.package_size === billingSelection.packageSize
        )
        : undefined;
    const monthlyBillingUnit = Number(billingPrice?.monthly_price_per_lead || billingPrice?.price_per_lead || 0);
    const oneTimeBillingUnit = Number(billingPrice?.one_time_price_per_lead || billingPrice?.price_per_lead || 0);
    const monthlyMode = monthlyCheckoutAvailable && billingMode === 'monthly';
    const getBillingPrices = (categoryId: number): LeadPackagePrice[] =>
        (categoryPrices.get(categoryId) || []).map((price) => ({
            ...price,
            price_per_lead: monthlyMode
                ? price.monthly_price_per_lead || price.price_per_lead
                : price.one_time_price_per_lead || price.price_per_lead,
            discount_percent: price.discount_percent,
        }));

    // Расчет базовой цены (цена минимального пакета)
    const getBasePrice = (prices: LeadPackagePrice[]): number => {
        if (!prices.length) return 0;
        const minPackage = prices.reduce((min, p) => (p.package_size < min.package_size ? p : min));
        return parseFloat(minPackage.price_per_lead);
    };

    // Расчет скидки и экономии
    const calculateDiscount = (basePrice: number, currentPrice: number, packageSize: number) => {
        if (basePrice === 0) return { discount: 0, savings: 0 };
        const baseTotal = basePrice * packageSize;
        const currentTotal = currentPrice * packageSize;
        const discount = ((basePrice - currentPrice) / basePrice) * 100;
        const savings = baseTotal - currentTotal;
        return { discount: Math.max(0, discount), savings: Math.max(0, savings) };
    };

    const hasVisibleBasePrice = (baseTotal: number, total: number) => {
        if (baseTotal <= 0) {
            return false;
        }

        return baseTotal > total;
    };

    // Расчет общей выгоды
    const calculateTotalSavings = (): number => {
        let totalSavings = 0;
        selectedCategories.forEach((selected) => {
            if (selected.packageSize && categoryPrices.has(selected.category.id)) {
                const prices = getBillingPrices(selected.category.id);
                const basePrice = getBasePrice(prices);
                const priceObj = prices.find((p) => p.package_size === selected.packageSize);
                if (priceObj) {
                    const pricePerLead = parseFloat(priceObj.price_per_lead);
                    const { savings } = calculateDiscount(basePrice, pricePerLead, selected.packageSize);
                    totalSavings += savings;
                }
            }
        });
        return totalSavings;
    };

    // Расчет итоговой суммы
    const calculateTotal = (): number => {
        let total = 0;
        selectedCategories.forEach((selected) => {
            if (selected.packageSize && categoryPrices.has(selected.category.id)) {
                const prices = getBillingPrices(selected.category.id);
                const priceObj = prices.find((p) => p.package_size === selected.packageSize);
                if (priceObj) {
                    total += parseFloat(priceObj.price_per_lead) * selected.packageSize;
                }
            }
        });
        return total;
    };

    const total = calculateTotal();
    const totalSavings = calculateTotalSavings();
    const isUseBonus = watch('bonus');
    const autoRenewal = watch('auto_renewal');
    const autoRenewalTouched = watch('auto_renewal_touched');
    const promoCode = watch('promo_code');
    const aiPrompt = watch('ai_prompt');
    const examplesCount = Math.min(analysis?.lead_examples.length || 0, 6);
    const selectedCategoryItems = useMemo(() => Array.from(selectedCategories.values()), [selectedCategories]);
    const primarySelectedCategory = selectedCategoryItems[0] || null;
    const primaryCategory = primarySelectedCategory?.category || analysis?.bestCategory || null;
    const directForecast = useMemo(() => {
        if (!analysis?.direct_forecast) {
            return null;
        }

        const connectionStatus = directConnectionStatus || analysis.direct_forecast.connection_status;
        return {
            ...analysis.direct_forecast,
            connection_status: connectionStatus,
            next_action: connectionStatus === 'connected' ? 'run_direct_audit' : analysis.direct_forecast.next_action,
        };
    }, [analysis?.direct_forecast, directConnectionStatus]);
    const primaryCategoryColor = primaryCategory?.color || 'var(--lm-accent-ink)';
    const flowWaveColors = useMemo(
        () => [primaryCategoryColor, '#006BFF', '#00B2FF', '#42C8FF', '#7E7AFF', '#B236FF'],
        [primaryCategoryColor]
    );
    const hasMultipleSelectedCategories = selectedCategoryItems.length > 1;
    const leadProductGeoLabel = buildGeoContext(setupGeo, setupAddress) || setupGeo.trim() || 'Локация не указана';
    const filterGeoLabel = leadProductGeoLabel;
    const filterCategoryLabel = primaryCategory
        ? formatCategoryLabel(primaryCategory)
        : analysis?.category || 'Категория подбирается';
    const selectedCategoriesLabel = selectedCategoryItems.length
        ? selectedCategoryItems.map((selected) => formatCategoryLabel(selected.category)).join(', ')
        : filterCategoryLabel;
    const leadSearchRules = DEFAULT_LEAD_STREAM_TARGET_INTENTS;
    const leadRejectRules = DEFAULT_LEAD_STREAM_EXCLUDE_RULES;
    const leadPreviewRejectRules = DEFAULT_LEAD_STREAM_EXCLUDE_RULES.slice(0, 4);
    const launchNextSteps = [
        {
            icon: 'delivery' as const,
            title: 'Получение лидов',
            description:
                'В личном кабинете, Telegram-боте, в вашей CRM или через API. Способ получения настраивается после запуска',
        },
        {
            icon: 'training' as const,
            title: 'ИИ обучается для вас',
            description:
                'Отмечайте лиды как подходящие или неподходящие. По вашим ответам агент будет точнее понимать, какие лиды нужны вашему бизнесу',
        },
    ];
    const leadBalancePolicy = {
        icon: 'balance' as const,
        title: 'Списание за целевые лиды',
        description:
            'Баланс автоматически расходуется только на лиды по вашим настройкам. Если после проверки лид явно не соответствует критериям, он автоматически возвращается на баланс',
    };
    const launchInfoCards = [...launchNextSteps, leadBalancePolicy];
    const reviewedExamplesCount = useMemo(() => {
        if (!analysis) {
            return 0;
        }
        return analysis.lead_examples.slice(0, examplesCount).filter((example) => Boolean(exampleChoices[example.id]))
            .length;
    }, [analysis, exampleChoices, examplesCount]);
    const fitExamplesCount = useMemo(() => {
        if (!analysis) {
            return 0;
        }
        return analysis.lead_examples.slice(0, examplesCount).filter((example) => exampleChoices[example.id] === 'fit')
            .length;
    }, [analysis, exampleChoices, examplesCount]);
    const maybeExamplesCount = useMemo(() => {
        if (!analysis) {
            return 0;
        }
        return analysis.lead_examples
            .slice(0, examplesCount)
            .filter((example) => exampleChoices[example.id] === 'maybe').length;
    }, [analysis, exampleChoices, examplesCount]);
    const hasPositiveExample = useMemo(() => {
        if (!analysis) {
            return false;
        }
        return analysis.lead_examples.slice(0, examplesCount).some((example) => exampleChoices[example.id] === 'fit');
    }, [analysis, exampleChoices, examplesCount]);
    const hasLeadExamples = examplesCount > 0;
    const examplesReviewed = Boolean(analysis) && hasLeadExamples && reviewedExamplesCount >= examplesCount;
    const noExamplesFound = Boolean(analysis) && !hasLeadExamples;
    const examplesDone =
        Boolean(analysis) &&
        (hasLeadExamples ? examplesReviewed && (hasPositiveExample || manualCategoryFallback) : manualCategoryFallback);
    const examplesProgressPercent = examplesCount ? Math.round((reviewedExamplesCount / examplesCount) * 100) : 0;
    const shouldSkipAiTraining = manualCategoryFallback || !hasLeadExamples;
    const showAiTraining = aiTrainingRequested && examplesDone && !shouldSkipAiTraining && !aiTrainingComplete;
    const shouldShowPostExamples = examplesDone && (shouldSkipAiTraining || aiTrainingComplete);
    const hasLaunchPrompt = selectedCategories.size > 0 && Boolean(aiPrompt?.trim()) && shouldShowPostExamples;
    const hasSuggestedCategories = Boolean(analysis?.suggestedCategories.length);
    const shouldShowManualCategories =
        shouldShowPostExamples &&
        (manualCategoryFallback || (selectedCategories.size === 0 && !hasSuggestedCategories));
    const shouldShowConfiguredFlowDetails = selectedCategories.size > 0 && !manualCategoryFallback;
    const potentialResult = useMemo(() => {
        if (!analysis || !shouldShowConfiguredFlowDetails || !examplesDone || reviewedExamplesCount === 0) {
            return null;
        }

        const categoryForecasts = selectedCategoryItems.map((selected) => {
            const { monthlyBase, signalCount } = getCategoryMonthlyLeadBase(selected.category);
            const confidenceFactor = getCategoryConfidenceFactor({
                category: selected.category,
                categoryName: analysis.category,
                keywords: analysis.keywords,
                businessDescription: setupDescription,
                isBestCategory: analysis.bestCategory?.id === selected.category.id,
            });

            return {
                monthlyBase,
                signalCount,
                confidenceFactor,
            };
        });
        const monthlyBase = categoryForecasts.reduce((sum, item) => sum + item.monthlyBase, 0);

        if (monthlyBase <= 0) {
            return null;
        }

        const fitRatio = Math.min(
            1,
            Math.max(0, (fitExamplesCount + maybeExamplesCount * 0.5 + 1) / (reviewedExamplesCount + 2))
        );
        const geoFactor = getGeoForecastFactor(setupGeo);
        const expectedMonthly = categoryForecasts.reduce(
            (sum, item) => sum + item.monthlyBase * geoFactor * fitRatio * item.confidenceFactor,
            0
        );
        const totalSignalCount = categoryForecasts.reduce((sum, item) => sum + item.signalCount, 0);
        const confidenceFactor =
            monthlyBase > 0
                ? categoryForecasts.reduce((sum, item) => sum + item.confidenceFactor * item.monthlyBase, 0) /
                  monthlyBase
                : 0;
        const isLowDataForecast = monthlyBase < 10 || totalSignalCount <= selectedCategoryItems.length;
        const minMultiplier = isLowDataForecast ? 0.6 : 0.75;
        const maxMultiplier = isLowDataForecast ? 1.4 : 1.25;
        const min = Math.max(1, Math.round(expectedMonthly * minMultiplier));
        const max = Math.max(min, Math.round(expectedMonthly * maxMultiplier));

        return {
            min,
            max,
            monthlyBase,
            fitRatio,
            geoFactor,
            confidenceFactor,
            expectedMonthly,
            isLowDataForecast,
        };
    }, [
        analysis,
        shouldShowConfiguredFlowDetails,
        examplesDone,
        reviewedExamplesCount,
        selectedCategoryItems,
        fitExamplesCount,
        maybeExamplesCount,
        setupDescription,
        setupGeo,
    ]);

    // Расчет скидки по промокоду
    const promoDiscountAmount = promoDiscount && !promoError && total > 0
        ? Math.min((total * promoDiscount) / 100, monthlyMode ? Math.max(0, total - 1) : total)
        : 0;
    const totalAfterPromo = total - promoDiscountAmount;
    const maxBonusToUse = monthlyMode ? Math.max(0, Math.floor(totalAfterPromo - 1)) : totalAfterPromo;
    const actualBonusToUse = isUseBonus && bonusPoints > 0 ? Math.min(bonusPoints, maxBonusToUse) : 0;
    const finalTotal = Math.max(0, totalAfterPromo - actualBonusToUse);
    const userVisibleSavings = totalSavings + promoDiscountAmount + actualBonusToUse;
    const totalBeforeVolumeDiscount = total + totalSavings;
    const totalSavingsPercent =
        totalSavings > 0 && totalBeforeVolumeDiscount > 0 ? (totalSavings / totalBeforeVolumeDiscount) * 100 : 0;
    const promoCodeLabel = promoCode?.trim().toUpperCase();
    const finalOldTotal = finalTotal + userVisibleSavings;
    const hasFinalOldTotal = finalOldTotal > finalTotal;

    useEffect(() => {
        if (!examplesDone) {
            setAiTrainingComplete(false);
            setAiTrainingRequested(false);
            return undefined;
        }

        if (shouldSkipAiTraining || aiTrainingComplete || !aiTrainingRequested) {
            return undefined;
        }

        const scrollTimer = window.setTimeout(() => {
            aiTrainingBlockRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 120);
        const trainingTimer = window.setTimeout(() => {
            setAiTrainingComplete(true);
            setAiTrainingRequested(false);
        }, 5000);

        return () => {
            window.clearTimeout(scrollTimer);
            window.clearTimeout(trainingTimer);
        };
    }, [examplesDone, shouldSkipAiTraining, aiTrainingComplete, aiTrainingRequested]);

    useEffect(() => {
        if (!shouldRestoreScrollRef.current || restoredLeadstreamScrollRef.current || analysisLoading) {
            return undefined;
        }

        let target: HTMLDivElement | null = null;

        if (!analysis) {
            target = setupFormRef.current;
        } else if (showAiTraining) {
            target = aiTrainingBlockRef.current;
        } else if (shouldShowManualCategories && selectedCategories.size === 0) {
            target = manualCategoriesSectionRef.current;
        } else if (shouldShowPostExamples) {
            target = launchSectionRef.current;
        } else if (hasLeadExamples && !examplesDone) {
            target = examplesSectionRef.current;
        } else if (noExamplesFound) {
            target = examplesSectionRef.current;
        }

        if (!target) {
            return undefined;
        }

        restoredLeadstreamScrollRef.current = true;
        const scrollTimer = window.setTimeout(() => {
            target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 180);

        return () => window.clearTimeout(scrollTimer);
    }, [
        analysis,
        analysisLoading,
        examplesDone,
        hasLeadExamples,
        noExamplesFound,
        shouldShowManualCategories,
        shouldShowPostExamples,
        showAiTraining,
        selectedCategories.size,
    ]);

    // Валидация промокода при изменении
    useEffect(() => {
        const validatePromo = async () => {
            if (!promoCode || !promoCode.trim()) {
                setPromoDiscount(null);
                setPromoError(null);
                return;
            }

            setIsValidatingPromo(true);
            setPromoError(null);
            try {
                const response = await SubscriptionsService.checkPromocode(promoCode.trim());
                if (response && response.discount_percent) {
                    setPromoDiscount(response.discount_percent);
                } else {
                    setPromoDiscount(null);
                    setPromoError('Недействительный промокод');
                }
            } catch (error: any) {
                setPromoDiscount(null);
                setPromoError(error?.error || 'Недействительный промокод');
            } finally {
                setIsValidatingPromo(false);
            }
        };

        const timeoutId = setTimeout(validatePromo, 500); // Debounce
        return () => clearTimeout(timeoutId);
    }, [promoCode]);

    // Сохранение выбора при изменении формы
    useEffect(() => {
        saveSelection(selectedCategories, {
            promo_code: promoCode,
            auto_renewal: autoRenewal,
            auto_renewal_touched: autoRenewalTouched,
            ai_prompt: aiPrompt,
            setup_description: setupDescription,
            setup_geo: setupGeo,
            setup_address: setupAddress,
        });
    }, [
        promoCode,
        autoRenewal,
        autoRenewalTouched,
        aiPrompt,
        selectedCategories,
        setupDescription,
        setupGeo,
        setupAddress,
        analysis,
        exampleChoices,
        exampleSkipReasons,
        showManualCategories,
        hasCheckoutStarted,
        manualCategoryFallback,
        aiTrainingComplete,
    ]);

    const handleResetFlow = () => {
        localStorage.removeItem(STORAGE_KEY);
        setSearchQuery('');
        setSelectedCategories(new Map());
        setCategoryPrices(new Map());
        setLoadingPrices(new Set());
        setAnalysis(null);
        setDirectAuditResult(null);
        setDirectConnectionStatus('');
        setAnalysisError(null);
        setExampleChoices({});
        setExampleSkipReasons({});
        setShowManualCategories(false);
        setHasCheckoutStarted(false);
        setManualCategoryFallback(false);
        setAiTrainingComplete(false);
        setAiTrainingRequested(false);
        setPromoDiscount(null);
        setPromoError(null);
        setSetupDescription('');
        setSetupGeo('');
        setSetupAddress('');
        setSetupAddressEnabled(false);
        setSetupSelectedLocation(null);
        setSetupLocationOptions([]);
        setSetupLocationOpen(false);
        setSetupSelectedAddress(null);
        setSetupAddressOptions([]);
        setSetupAddressOpen(false);
        setValue('promo_code', '', { shouldDirty: false });
        setValue('ai_prompt', '', { shouldDirty: false });
        setValue('bonus', false, { shouldDirty: false });
        setValue('auto_renewal', false, { shouldDirty: false });
        setValue('auto_renewal_touched', false, { shouldDirty: false });
        setValue('paymentMethod', 'UMoney', { shouldDirty: false });
    };

    const handleRefineDescription = () => {
        setupFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        window.setTimeout(() => {
            setupDescriptionRef.current?.focus();
        }, 350);
    };

    const handleContinueWithManualCategories = () => {
        setSearchQuery('');
        setSelectedCategories(new Map());
        setCategoryPrices(new Map());
        setLoadingPrices(new Set());
        setManualCategoryFallback(true);
        setShowManualCategories(true);
        setHasCheckoutStarted(false);
        setAiTrainingComplete(false);
        setAiTrainingRequested(false);
    };

    const onSubmit = async (data: LeadStreamForm) => {
        if (requestedFlowId && !launchWorkspace) {
            enqueueSnackbar('Профиль потока ещё не загружен. Обновите страницу перед продолжением.', {
                variant: 'warning',
            });
            return;
        }
        const categoriesWithoutPackage = Array.from(selectedCategories.values()).filter((s) => !s.packageSize);
        if (categoriesWithoutPackage.length > 0) {
            enqueueSnackbar('Выберите объём для всех категорий', { variant: 'warning' });
            return;
        }

        setIsSubmitting(true);

        try {
            const buildProfileForCategory = (category: LeadCategory) =>
                buildLeadStreamFilterProfile({
                    businessDescription: setupDescription,
                    geo: buildGeoContext(setupGeo, setupAddress) || setupGeo,
                    category,
                    examples: analysis?.lead_examples || [],
                    choices: exampleChoices,
                    skipReasons: exampleSkipReasons,
                });
            const reviewedPrompt = primaryCategory
                ? compileLeadStreamPromptPreview(buildProfileForCategory(primaryCategory))
                : '';
            const aiPrompt = (reviewedPrompt || data.ai_prompt || watch('ai_prompt') || '').trim() || undefined;

            if (launchWorkspace) {
                const saved = await IntentService.save({
                    ...launchWorkspace,
                    profile: { ...launchWorkspace.profile, description: setupDescription, region: setupGeo },
                });
                setLaunchWorkspace(saved);
            }
            // Создаем stream'ы и покупки для каждой категории
            const purchasePromises = Array.from(selectedCategories.values()).map(async (selected) => {
                const filterProfile = buildProfileForCategory(selected.category);
                const streamPrompt = compileLeadStreamPromptPreview(filterProfile);

                // Создаем или получаем stream для категории
                // Бэкенд автоматически вернет существующий stream, если он уже есть
                const stream = await LeadStreamsService.createStream({
                    category_id: selected.category.id,
                    prompt: streamPrompt || aiPrompt,
                    filter_profile: filterProfile,
                    project: launchWorkspace?.project_id || analysis?.project_id || null,
                    ...(launchWorkspace ? { name: launchWorkspace.name } : {}),
                });

                EventService.dispatch('LEAD_STREAMS_UPDATED', stream);

                const purchaseData: any = {
                    use_wallet_rub: !monthlyMode,
                    auto_renewal: monthlyMode,
                };
                if (!selected.packageSize) {
                    throw new Error('Не выбран объём лидов');
                }
                purchaseData.package_size = selected.packageSize;

                // Добавляем промокод, если он валиден
                if (promoCode && promoCode.trim() && promoDiscount && !promoError) {
                    purchaseData.promo_code = promoCode.trim();
                }

                // Добавляем информацию об использовании бонусов
                if (isUseBonus) {
                    purchaseData.use_bonus_points = true;
                }

                purchaseData.request_key = packageRequestKey(useUserStore.getState().id, stream.id, purchaseData);
                const purchaseResponse = await LeadStreamsService.purchasePackage(stream.id, purchaseData);

                return { response: purchaseResponse, streamId: stream.id };
            });

            const settled = await Promise.allSettled(purchasePromises);
            const completed = settled.flatMap((result) => result.status === 'fulfilled' ? [result.value] : []);
            const purchaseResponses = completed.map((result) => result.response);
            if (!completed.length) {
                const failed = settled.find((result) => result.status === 'rejected') as PromiseRejectedResult | undefined;
                throw failed?.reason || new Error('Не удалось создать покупку');
            }
            saveSelection(selectedCategories, getCurrentFormData(aiPrompt), analysis, { hasCheckoutStarted: true });
            rememberPackageCheckout(useUserStore.getState().id, completed, STORAGE_KEY);
            if (completed.length !== settled.length) {
                enqueueSnackbar('Часть пакетов создана. Проверьте их оплату перед повторным запуском.', { variant: 'warning' });
                history.push(`${ROUTES_URL.FEED}?payment_success=true`);
                return;
            }

            // Если все покупки успешны, перенаправляем на первую ссылку оплаты
            // В будущем можно объединить все покупки в один платеж
            const payable = purchaseResponses.find((response) => response.confirmation_url && !response.paid_immediately);
            if (payable) {
                setHasCheckoutStarted(true);
                saveSelection(
                    selectedCategories,
                    {
                        ...getCurrentFormData(aiPrompt),
                        auto_renewal: data.auto_renewal,
                        auto_renewal_touched: data.auto_renewal_touched,
                    },
                    analysis,
                    { hasCheckoutStarted: true }
                );
                rememberPackageCheckout(useUserStore.getState().id, completed, STORAGE_KEY);
                window.location.href = payable.confirmation_url;
            } else if (
                purchaseResponses.length > 0 &&
                purchaseResponses.every((response) => response.paid_immediately)
            ) {
                setHasCheckoutStarted(true);
                enqueueSnackbar('Поток лидов активирован', {
                    variant: 'success',
                });
                history.push(`${ROUTES_URL.FEED}?payment_success=true`);
            } else {
                enqueueSnackbar('Покупки созданы, но ссылка на оплату не получена', {
                    variant: 'warning',
                });
                history.push(`${ROUTES_URL.FEED}?payment_success=true`);
            }
        } catch (error: any) {
            console.error('Ошибка создания покупок:', error);
            const errorMessage =
                error?.message ||
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Не удалось создать покупки. Попробуйте позже.';
            enqueueSnackbar(errorMessage, { variant: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderContent = () => {
        return (
            <Box
                display="flex"
                flexDirection="column"
                minHeight="100%"
                flexGrow={1}
                sx={{
                    '& .MuiButton-root': {
                        fontWeight: LEADSTREAM_BUTTON_FONT_WEIGHT,
                    },
                    '& .MuiButton-root .MuiTypography-root': {
                        fontWeight: LEADSTREAM_BUTTON_FONT_WEIGHT,
                    },
                }}
            >
                <Box mb={3}>
                    <Typography
                        variant="h1"
                        color="text.primary"
                    >
                        Запуск потока лидов
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--lm-muted)',
                            fontSize: 16,
                            fontWeight: 400,
                            mt: 1.5,
                            lineHeight: 1.5,
                            maxWidth: '800px',
                        }}
                    >
                        Опишите, какие клиенты вам нужны.
                    </Typography>
                </Box>

                <SetupDrafts currentId={requestedFlowId} />

                <form onSubmit={handleSubmit(onSubmit)}>
                    <Box
                        ref={setupFormRef}
                        mb={3}
                    >
                        <Card
                            elevation={0}
                            sx={LEADSTREAM_CARD_SX}
                        >
                            <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
                                <Stack spacing={2.5}>
                                    <Stack spacing={2.25}>
                                        <Stack spacing={1}>
                                            <Typography sx={{ fontWeight: 700 }}>Какие заявки нужны</Typography>
                                            <TextField
                                                inputRef={setupDescriptionRef}
                                                value={setupDescription}
                                                onChange={(event) => setSetupDescription(event.target.value)}
                                                placeholder="Например: нужны заявки от владельцев малого бизнеса на разработку сайта. Не подходят вакансии, обучение и реклама агентств"
                                                fullWidth
                                                multiline
                                                InputProps={{ inputComponent: 'textarea' }}
                                                inputProps={{ rows: 4, 'aria-label': 'Какие заявки хотите получать' }}
                                                sx={{
                                                    '& .MuiInputBase-root': {
                                                        height: 'auto',
                                                        minHeight: 118,
                                                        alignItems: 'flex-start',
                                                        border: 'none',
                                                        borderRadius: LEADSTREAM_PANEL_RADIUS,
                                                        p: 0,
                                                    },
                                                    '& .MuiOutlinedInput-root': {
                                                        p: 0,
                                                    },
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: LEADSTREAM_BORDER,
                                                    },
                                                    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: 'var(--lm-accent-ink)',
                                                    },
                                                    '& textarea.MuiInputBase-input': {
                                                        minHeight: 118,
                                                        resize: 'vertical',
                                                        p: '14px 16px',
                                                        color: 'var(--lm-text)',
                                                        fontWeight: 400,
                                                        lineHeight: '22px',
                                                    },
                                                }}
                                            />
                                        </Stack>

                                        <Stack spacing={1.5}>
                                            <Stack spacing={1}>
                                                <Typography sx={{ fontWeight: 700 }}>
                                                    Укажите локацию (если это важно)
                                                </Typography>
                                                <Box position="relative">
                                                    <TextField
                                                        value={setupGeo}
                                                        onChange={handleSetupGeoChange}
                                                        onFocus={() => {
                                                            if (setupLocationOptions.length > 0) {
                                                                setSetupLocationOpen(true);
                                                            }
                                                        }}
                                                        onBlur={() =>
                                                            window.setTimeout(() => setSetupLocationOpen(false), 120)
                                                        }
                                                        placeholder="Например, город Москва"
                                                        fullWidth
                                                        inputProps={{ 'aria-label': 'Укажите локацию, если это важно' }}
                                                        InputProps={{
                                                            endAdornment: setupLocationLoading ? (
                                                                <InputAdornment position="end">
                                                                    <CircularProgress size={18} />
                                                                </InputAdornment>
                                                            ) : undefined,
                                                        }}
                                                        sx={{
                                                            '& .MuiInputBase-root': {
                                                                height: 48,
                                                                border: 'none',
                                                                borderRadius: LEADSTREAM_PANEL_RADIUS,
                                                                p: 0,
                                                            },
                                                            '& .MuiOutlinedInput-root': {
                                                                p: 0,
                                                            },
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: LEADSTREAM_BORDER,
                                                            },
                                                            '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'var(--lm-accent-ink)',
                                                            },
                                                            '& input.MuiInputBase-input': {
                                                                p: '13px 16px',
                                                                color: 'var(--lm-text)',
                                                                fontWeight: 400,
                                                            },
                                                        }}
                                                    />

                                                    {setupLocationOpen && setupLocationOptions.length > 0 && (
                                                        <Paper
                                                            elevation={4}
                                                            sx={{
                                                                position: 'absolute',
                                                                zIndex: 20,
                                                                top: 'calc(100% + 6px)',
                                                                left: 0,
                                                                right: 0,
                                                                maxHeight: 260,
                                                                overflow: 'auto',
                                                                border: `1px solid ${LEADSTREAM_BORDER}`,
                                                                borderRadius: LEADSTREAM_PANEL_RADIUS,
                                                            }}
                                                        >
                                                            <List
                                                                dense
                                                                disablePadding
                                                            >
                                                                {setupLocationOptions.map((location, index) => (
                                                                    <ListItemButton
                                                                        key={`${location.id}-${index}`}
                                                                        onMouseDown={(event) => event.preventDefault()}
                                                                        onClick={() =>
                                                                            handleSetupLocationSelect(location)
                                                                        }
                                                                        sx={{
                                                                            minHeight: 50,
                                                                            px: 1.5,
                                                                            py: 0.75,
                                                                        }}
                                                                    >
                                                                        <ListItemText
                                                                            primary={getLocationDisplayName(location)}
                                                                            secondary={
                                                                                location.country_code ||
                                                                                location.country
                                                                            }
                                                                            primaryTypographyProps={{
                                                                                fontSize: 14,
                                                                                fontWeight: 700,
                                                                                color: 'var(--lm-text)',
                                                                            }}
                                                                            secondaryTypographyProps={{
                                                                                fontSize: 12,
                                                                                color: 'var(--lm-muted)',
                                                                            }}
                                                                        />
                                                                    </ListItemButton>
                                                                ))}
                                                            </List>
                                                        </Paper>
                                                    )}
                                                </Box>
                                            </Stack>
                                            <Stack
                                                direction="row"
                                                flexWrap="wrap"
                                                gap={1}
                                            >
                                                {GEO_QUICK_REPLIES.map((reply) => {
                                                    const selected = setupGeo === reply;

                                                    return (
                                                        <Button
                                                            key={reply}
                                                            type="button"
                                                            variant="outlined"
                                                            disableRipple
                                                            onClick={() => handleSetupGeoQuickReply(reply)}
                                                            sx={{
                                                                minHeight: 34,
                                                                height: 34,
                                                                px: 1.5,
                                                                borderRadius: LEADSTREAM_CONTROL_RADIUS,
                                                                borderColor: selected
                                                                    ? 'var(--lm-text)'
                                                                    : 'var(--lm-border)',
                                                                bgcolor: selected ? '#EEF2F6' : 'var(--lm-canvas)',
                                                                color: selected ? 'var(--lm-text)' : '#334155',
                                                                fontSize: 13,
                                                                fontWeight: LEADSTREAM_BUTTON_FONT_WEIGHT,
                                                                lineHeight: '18px',
                                                                textTransform: 'none',
                                                                boxShadow: 'none',
                                                                '&:hover': {
                                                                    borderColor: selected
                                                                        ? 'var(--lm-text)'
                                                                        : 'var(--lm-accent-border)',
                                                                    bgcolor: selected
                                                                        ? '#E6EDF5'
                                                                        : 'var(--lm-accent-soft)',
                                                                    boxShadow: 'none',
                                                                },
                                                                '&.Mui-focusVisible': {
                                                                    outline: 'none',
                                                                    boxShadow: '0 0 0 3px rgba(17, 24, 39, 0.08)',
                                                                },
                                                            }}
                                                        >
                                                            {reply}
                                                        </Button>
                                                    );
                                                })}
                                            </Stack>

                                            <Stack spacing={1}>
                                                <FormControlLabel
                                                    label="Указать конкретный адрес"
                                                    sx={{
                                                        m: 0,
                                                        gap: 1,
                                                        minHeight: 44,
                                                        '& .MuiFormControlLabel-label': {
                                                            fontWeight: 600,
                                                            color: 'var(--lm-text)',
                                                        },
                                                    }}
                                                    control={
                                                        <Checkbox
                                                            checked={setupAddressEnabled}
                                                            onChange={(_, checked) => setSetupAddressEnabled(checked)}
                                                            inputProps={{
                                                                'aria-controls': 'setup-exact-address',
                                                                'aria-expanded': setupAddressEnabled,
                                                                'aria-describedby': 'setup-exact-address-hint',
                                                            }}
                                                            sx={{
                                                                p: 0.5,
                                                                color: 'var(--lm-muted)',
                                                                '&.Mui-checked': { color: 'var(--lm-accent-ink)' },
                                                            }}
                                                        />
                                                    }
                                                />
                                                <Typography
                                                    id="setup-exact-address-hint"
                                                    sx={{ fontSize: 14, color: 'var(--lm-muted)', lineHeight: 1.5 }}
                                                >
                                                    Для локального бизнеса: салонов маникюра, ремонта обуви, кафе и
                                                    других заведений.
                                                </Typography>
                                                {setupAddressEnabled && (
                                                    <Box
                                                        id="setup-exact-address"
                                                        position="relative"
                                                    >
                                                        <TextField
                                                            value={setupAddress}
                                                            onChange={handleSetupAddressChange}
                                                            onFocus={() => {
                                                                if (setupAddressOptions.length > 0) {
                                                                    setSetupAddressOpen(true);
                                                                }
                                                            }}
                                                            onBlur={() =>
                                                                window.setTimeout(() => setSetupAddressOpen(false), 120)
                                                            }
                                                            placeholder="Например, Петровка 15"
                                                            fullWidth
                                                            inputProps={{ 'aria-label': 'Уточните улицу и дом' }}
                                                            InputProps={{
                                                                endAdornment: setupAddressLoading ? (
                                                                    <InputAdornment position="end">
                                                                        <CircularProgress size={18} />
                                                                    </InputAdornment>
                                                                ) : undefined,
                                                            }}
                                                            sx={{
                                                                '& .MuiInputBase-root': {
                                                                    height: 48,
                                                                    border: 'none',
                                                                    borderRadius: LEADSTREAM_PANEL_RADIUS,
                                                                    p: 0,
                                                                },
                                                                '& .MuiOutlinedInput-root': {
                                                                    p: 0,
                                                                },
                                                                '& .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: LEADSTREAM_BORDER,
                                                                },
                                                                '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: 'var(--lm-accent-ink)',
                                                                },
                                                                '& input.MuiInputBase-input': {
                                                                    p: '13px 16px',
                                                                    color: 'var(--lm-text)',
                                                                    fontWeight: 400,
                                                                },
                                                            }}
                                                        />

                                                        {setupAddressOpen && setupAddressOptions.length > 0 && (
                                                            <Paper
                                                                elevation={4}
                                                                sx={{
                                                                    position: 'absolute',
                                                                    zIndex: 20,
                                                                    top: 'calc(100% + 6px)',
                                                                    left: 0,
                                                                    right: 0,
                                                                    maxHeight: 260,
                                                                    overflow: 'auto',
                                                                    border: `1px solid ${LEADSTREAM_BORDER}`,
                                                                    borderRadius: LEADSTREAM_PANEL_RADIUS,
                                                                }}
                                                            >
                                                                <List
                                                                    dense
                                                                    disablePadding
                                                                >
                                                                    {setupAddressOptions.map((location, index) => (
                                                                        <ListItemButton
                                                                            key={`${location.id}-${index}`}
                                                                            onMouseDown={(event) =>
                                                                                event.preventDefault()
                                                                            }
                                                                            onClick={() =>
                                                                                handleSetupAddressSelect(location)
                                                                            }
                                                                            sx={{
                                                                                minHeight: 50,
                                                                                px: 1.5,
                                                                                py: 0.75,
                                                                            }}
                                                                        >
                                                                            <ListItemText
                                                                                primary={
                                                                                    location.formatted_address ||
                                                                                    getLocationDisplayName(location)
                                                                                }
                                                                                secondary={
                                                                                    [
                                                                                        location.district_name,
                                                                                        location.precision,
                                                                                    ]
                                                                                        .filter(Boolean)
                                                                                        .join(' · ') ||
                                                                                    location.country_code ||
                                                                                    location.country
                                                                                }
                                                                                primaryTypographyProps={{
                                                                                    fontSize: 14,
                                                                                    fontWeight: 700,
                                                                                    color: 'var(--lm-text)',
                                                                                }}
                                                                                secondaryTypographyProps={{
                                                                                    fontSize: 12,
                                                                                    color: 'var(--lm-muted)',
                                                                                }}
                                                                            />
                                                                        </ListItemButton>
                                                                    ))}
                                                                </List>
                                                            </Paper>
                                                        )}
                                                    </Box>
                                                )}
                                            </Stack>
                                        </Stack>
                                    </Stack>

                                    {analysisError && <Alert severity="error">{analysisError}</Alert>}

                                    <Box
                                        display="flex"
                                        justifyContent="flex-end"
                                        alignItems={{ xs: 'stretch', sm: 'center' }}
                                        flexDirection={{ xs: 'column', sm: 'row' }}
                                        gap={1.5}
                                    >
                                        <Button
                                            type="button"
                                            variant="contained"
                                            onClick={runSetupAnalysis}
                                            disabled={analysisLoading || !setupDescription.trim()}
                                            sx={{
                                                minWidth: 190,
                                                minHeight: 44,
                                                borderRadius: LEADSTREAM_CONTROL_RADIUS,
                                                fontWeight: LEADSTREAM_BUTTON_FONT_WEIGHT,
                                                textTransform: 'none',
                                            }}
                                        >
                                            {analysisLoading ? (
                                                <>
                                                    <CircularProgress
                                                        color="inherit"
                                                        size={18}
                                                        sx={{ mr: 1 }}
                                                    />
                                                    Анализируем...
                                                </>
                                            ) : (
                                                'Анализ ниши'
                                            )}
                                        </Button>
                                    </Box>

                                    {isMobile && (
                                        <LeadStreamProgress
                                            hasAnalysis={Boolean(analysis)}
                                            examplesDone={examplesDone}
                                            examplesCount={examplesCount}
                                            examplesProgressPercent={examplesProgressPercent}
                                            hasPrompt={hasCheckoutStarted}
                                            onReset={handleResetFlow}
                                        />
                                    )}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Box>

                    {(analysisLoading || analysis) && <StageDivider />}

                    {analysisLoading && <NicheAnalysisLoadingCard />}

                    {analysis && (
                        <>
                            {/* Direct is a separate service; the legacy implementation remains available in code. */}
                            {process.env.REACT_APP_ENABLE_LEGACY_DIRECT === 'true' && directForecast && (
                                <DirectForecastCard
                                    forecast={directForecast}
                                    audit={directAuditResult}
                                    isAuditLoading={directAuditLoading}
                                    isApplyLoading={directApplyLoading}
                                    isConfirmLoading={directConfirmLoading}
                                    onRunAudit={handleRunDirectAudit}
                                    onApplyAudit={handleApplyDirectAudit}
                                    onConfirmAdvertiserData={handleConfirmDirectAdvertiserData}
                                    onConnect={handleConnectYandexDirect}
                                />
                            )}

                            {analysis && hasLeadExamples && (
                                <Box mb={3}>
                                    <Stack spacing={2}>
                                        <Box>
                                            <Typography
                                                variant="h5"
                                                color="text.primary"
                                                sx={LEADSTREAM_SECTION_TITLE_SX}
                                            >
                                                Примеры лидов из вашей ниши
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Box>
                            )}

                            {noExamplesFound && !manualCategoryFallback && (
                                <Box
                                    ref={examplesSectionRef}
                                    mb={4}
                                    sx={{
                                        ...LEADSTREAM_CARD_SX,
                                        p: { xs: 2, sm: 2.5 },
                                    }}
                                >
                                    <Stack
                                        direction={{ xs: 'column', md: 'row' }}
                                        alignItems={{ xs: 'stretch', md: 'center' }}
                                        justifyContent="space-between"
                                        gap={2}
                                    >
                                        <Box>
                                            <Typography
                                                variant="h6"
                                                sx={{ fontWeight: 800, mb: 0.75 }}
                                            >
                                                Не нашли достаточно похожих примеров
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: 'var(--lm-muted)', lineHeight: 1.55, maxWidth: 680 }}
                                            >
                                                Уточните описание ниши или продолжите с ручным выбором категории.
                                            </Typography>
                                        </Box>
                                        <Stack
                                            direction={{ xs: 'column', sm: 'row' }}
                                            spacing={1}
                                            flexShrink={0}
                                        >
                                            <Button
                                                type="button"
                                                variant="contained"
                                                onClick={handleRefineDescription}
                                                sx={{ whiteSpace: 'nowrap' }}
                                            >
                                                Уточнить описание
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="outlined"
                                                onClick={handleContinueWithManualCategories}
                                                sx={{
                                                    borderColor: 'var(--lm-border)',
                                                    color: 'text.primary',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                Выбрать категорию вручную
                                            </Button>
                                        </Stack>
                                    </Stack>
                                </Box>
                            )}

                            {analysis && hasLeadExamples && (
                                <Box
                                    ref={examplesSectionRef}
                                    mb={4}
                                >
                                    <Box mb={2}>
                                        <Alert
                                            severity="info"
                                            sx={{
                                                borderRadius: LEADSTREAM_PANEL_RADIUS,
                                                border: '1px solid #BFD7FF',
                                                bgcolor: '#F3F8FF',
                                                color: '#1F3B57',
                                                boxShadow: 'none',
                                                alignItems: 'center',
                                                '& .MuiAlert-icon': {
                                                    color: 'var(--lm-accent-ink)',
                                                    mt: 0,
                                                },
                                                '& .MuiAlert-message': {
                                                    p: 0,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                },
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{ color: 'inherit', lineHeight: 1.55 }}
                                            >
                                                Отметьте подходящие лиды. По вашим ответам агент поймёт, какие лиды
                                                нужны вашему бизнесу, и подготовит поток к запуску
                                            </Typography>
                                        </Alert>
                                    </Box>

                                    <Box
                                        display="grid"
                                        gridTemplateColumns={{ xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }}
                                        gap={2}
                                    >
                                        {analysis.lead_examples.slice(0, 6).map((example, index) => (
                                            <Box
                                                key={example.id}
                                                sx={getLeadstreamAppearSx(index * 45)}
                                            >
                                                <LeadExampleReviewCard
                                                    example={example}
                                                    index={index + 1}
                                                    choice={exampleChoices[example.id]}
                                                    skipReason={exampleSkipReasons[String(example.id)]}
                                                    onChoose={handleExampleChoice}
                                                    onSkipReason={handleSkipReason}
                                                />
                                            </Box>
                                        ))}
                                    </Box>

                                    {examplesReviewed && !hasPositiveExample && !manualCategoryFallback && (
                                        <Box
                                            mt={2}
                                            sx={{
                                                borderRadius: LEADSTREAM_CARD_RADIUS,
                                                border: `1px solid ${LEADSTREAM_BORDER}`,
                                                bgcolor: 'var(--lm-surface)',
                                                p: { xs: 2, sm: 2.25 },
                                                boxShadow: LEADSTREAM_CARD_SHADOW,
                                            }}
                                        >
                                            <Stack
                                                direction={{ xs: 'column', md: 'row' }}
                                                alignItems={{ xs: 'stretch', md: 'center' }}
                                                justifyContent="space-between"
                                                gap={2}
                                            >
                                                <Box>
                                                    <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
                                                        Нет отмеченных подходящих примеров
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ color: 'var(--lm-muted)', lineHeight: 1.55 }}
                                                    >
                                                        Можно уточнить описание или продолжить с ручным выбором
                                                        категории.
                                                    </Typography>
                                                </Box>
                                                <Stack
                                                    direction={{ xs: 'column', sm: 'row' }}
                                                    spacing={1}
                                                    flexShrink={0}
                                                >
                                                    <Button
                                                        type="button"
                                                        variant="text"
                                                        onClick={handleRefineDescription}
                                                        sx={{
                                                            color: 'var(--lm-muted)',
                                                            fontWeight: LEADSTREAM_BUTTON_FONT_WEIGHT,
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        Уточнить описание
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant="outlined"
                                                        onClick={handleContinueWithManualCategories}
                                                        sx={{
                                                            borderColor: 'var(--lm-border)',
                                                            color: 'text.primary',
                                                            fontWeight: LEADSTREAM_BUTTON_FONT_WEIGHT,
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        Продолжить с ручным выбором
                                                    </Button>
                                                </Stack>
                                            </Stack>
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {showAiTraining && (
                                <Box
                                    ref={aiTrainingBlockRef}
                                    mb={4}
                                >
                                    <StageDivider />
                                    <AiTrainingLoadingCard />
                                </Box>
                            )}

                            {false && shouldShowPostExamples && selectedCategories.size > 0 && (
                                <>
                                    <StageDivider />
                                    <Box
                                        ref={launchSectionRef}
                                        mb={3.5}
                                    >
                                        <Box mb={2}>
                                            <Typography
                                                variant="h5"
                                                color="text.primary"
                                                sx={{
                                                    fontWeight: 700,
                                                    fontSize: { xs: 22, sm: 24 },
                                                    lineHeight: 1.22,
                                                    mb: 0.75,
                                                }}
                                            >
                                                Поиск лидов готов
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: 'var(--lm-muted)', maxWidth: 620, lineHeight: 1.5 }}
                                            >
                                                Мы учли ваши ответы и подготовили агента к запуску.
                                            </Typography>
                                        </Box>

                                        <Box
                                            sx={{
                                                border: '1px solid var(--lm-border)',
                                                borderRadius: '16px',
                                                backgroundColor: 'var(--lm-surface)',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    position: 'relative',
                                                    overflow: 'hidden',
                                                    minHeight: { xs: 320, sm: 286 },
                                                    p: { xs: 2, sm: 2.75 },
                                                    background:
                                                        'linear-gradient(135deg, #05111E 0%, #1C3C5C 42%, #5F8EC7 100%)',
                                                    '&::before': {
                                                        content: '""',
                                                        position: 'absolute',
                                                        inset: { xs: -70, sm: -90 },
                                                        background:
                                                            'radial-gradient(circle at 56% 42%, rgba(190, 234, 255, 0.82) 0 8%, rgba(42, 171, 238, 0.42) 18%, rgba(42, 171, 238, 0) 38%), radial-gradient(circle at 86% 72%, rgba(221, 244, 255, 0.46) 0 5%, rgba(42, 171, 238, 0.18) 22%, rgba(42, 171, 238, 0) 44%), radial-gradient(circle at 19% 88%, rgba(42, 171, 238, 0.46) 0 8%, rgba(42, 171, 238, 0) 32%)',
                                                        filter: 'blur(18px)',
                                                        animation: `${telegramVibeDrift} 22s ease-in-out infinite alternate`,
                                                    },
                                                    '&::after': {
                                                        content: '""',
                                                        position: 'absolute',
                                                        inset: 0,
                                                        background:
                                                            'linear-gradient(90deg, rgba(3, 11, 19, 0.86) 0%, rgba(6, 20, 34, 0.52) 43%, rgba(7, 23, 34, 0.18) 100%), linear-gradient(180deg, rgba(7, 23, 34, 0.08) 0%, rgba(7, 23, 34, 0.58) 100%)',
                                                        zIndex: 1,
                                                    },
                                                    '@media (prefers-reduced-motion: reduce)': {
                                                        '&::before, .agent-vibe-wave, .agent-vibe-ring, .agent-vibe-flow, .agent-vibe-core, .agent-radar, .agent-scan, .agent-packet, .agent-particle':
                                                            {
                                                                animation: 'none',
                                                            },
                                                    },
                                                }}
                                            >
                                                <Box
                                                    aria-hidden="true"
                                                    component="svg"
                                                    viewBox="0 0 860 320"
                                                    preserveAspectRatio="none"
                                                    sx={{
                                                        position: 'absolute',
                                                        inset: 0,
                                                        zIndex: 0,
                                                        width: '100%',
                                                        height: '100%',
                                                        opacity: 0.84,
                                                        '& .agent-vibe-wave': {
                                                            animation: `${telegramVibeWave} 11s ease-in-out infinite alternate`,
                                                            transformOrigin: 'center',
                                                        },
                                                        '& .agent-vibe-wave-slow': {
                                                            animationDuration: '17s',
                                                            animationDelay: '-7s',
                                                        },
                                                        '& .agent-vibe-flow': {
                                                            animation: `${agentSignalDash} 3.6s ease-in-out infinite`,
                                                        },
                                                        '& .agent-vibe-flow-slow': {
                                                            animationDuration: '5.4s',
                                                            animationDelay: '-1.5s',
                                                        },
                                                        '& .agent-vibe-ring': {
                                                            animation: `${telegramVibeRing} 12s ease-in-out infinite alternate`,
                                                            transformBox: 'fill-box',
                                                            transformOrigin: 'center',
                                                        },
                                                        '& .agent-vibe-ring-slow': {
                                                            animationDuration: '19s',
                                                            animationDelay: '-9s',
                                                        },
                                                    }}
                                                >
                                                    <defs>
                                                        <filter
                                                            id="telegramAgentBlur"
                                                            x="-20%"
                                                            y="-40%"
                                                            width="140%"
                                                            height="180%"
                                                        >
                                                            <feGaussianBlur stdDeviation="8" />
                                                        </filter>
                                                    </defs>
                                                    <path
                                                        className="agent-vibe-wave"
                                                        d="M-80 248 C 100 206 206 266 362 214 S 610 118 944 172"
                                                        fill="none"
                                                        stroke="rgba(221, 244, 255, 0.42)"
                                                        strokeWidth="8"
                                                        filter="url(#telegramAgentBlur)"
                                                    />
                                                    <path
                                                        className="agent-vibe-wave agent-vibe-wave-slow"
                                                        d="M-64 112 C 126 46 244 162 390 112 S 616 28 928 88"
                                                        fill="none"
                                                        stroke="rgba(142, 219, 255, 0.24)"
                                                        strokeWidth="10"
                                                        filter="url(#telegramAgentBlur)"
                                                    />
                                                    <path
                                                        className="agent-vibe-flow"
                                                        d="M-40 252 C 118 214 244 238 382 174 S 654 92 898 134"
                                                        fill="none"
                                                        stroke="rgba(221, 244, 255, 0.72)"
                                                        strokeWidth="3"
                                                        strokeLinecap="round"
                                                        strokeDasharray="34 186"
                                                        filter="url(#telegramAgentBlur)"
                                                    />
                                                    <path
                                                        className="agent-vibe-flow agent-vibe-flow-slow"
                                                        d="M26 82 C 170 142 302 56 432 116 S 638 244 860 192"
                                                        fill="none"
                                                        stroke="rgba(142, 219, 255, 0.58)"
                                                        strokeWidth="3"
                                                        strokeLinecap="round"
                                                        strokeDasharray="28 160"
                                                        filter="url(#telegramAgentBlur)"
                                                    />
                                                    <ellipse
                                                        className="agent-vibe-ring"
                                                        cx="548"
                                                        cy="118"
                                                        rx="96"
                                                        ry="56"
                                                        fill="none"
                                                        stroke="rgba(221, 244, 255, 0.36)"
                                                        strokeWidth="5"
                                                        filter="url(#telegramAgentBlur)"
                                                    />
                                                    <ellipse
                                                        className="agent-vibe-ring agent-vibe-ring-slow"
                                                        cx="716"
                                                        cy="216"
                                                        rx="70"
                                                        ry="42"
                                                        fill="none"
                                                        stroke="rgba(174, 232, 255, 0.28)"
                                                        strokeWidth="5"
                                                        filter="url(#telegramAgentBlur)"
                                                    />
                                                </Box>
                                                <Box
                                                    sx={{
                                                        position: 'relative',
                                                        zIndex: 2,
                                                        display: 'grid',
                                                        gridTemplateColumns: {
                                                            xs: '1fr',
                                                            md: 'minmax(0, 1fr) 220px minmax(190px, 0.82fr)',
                                                        },
                                                        alignItems: 'center',
                                                        gap: { xs: 2.25, md: 2.5 },
                                                        minHeight: { xs: 280, sm: 230 },
                                                    }}
                                                >
                                                    <Stack
                                                        gap={1.25}
                                                        sx={{ minWidth: 0, order: { xs: 2, md: 1 } }}
                                                    >
                                                        <Stack
                                                            direction="row"
                                                            alignItems="center"
                                                            gap={1}
                                                            flexWrap="wrap"
                                                        >
                                                            <QuerySearchAgentIcon size={34} />
                                                            <Box
                                                                component="span"
                                                                sx={{
                                                                    px: 1.15,
                                                                    py: 0.45,
                                                                    borderRadius: '999px',
                                                                    color: 'var(--lm-accent-soft)',
                                                                    backgroundColor: 'rgba(221, 244, 255, 0.12)',
                                                                    border: '1px solid rgba(221, 244, 255, 0.24)',
                                                                    fontSize: 12,
                                                                    fontWeight: 700,
                                                                    lineHeight: 1.2,
                                                                    backdropFilter: 'blur(10px)',
                                                                }}
                                                            >
                                                                Готов к запуску
                                                            </Box>
                                                        </Stack>
                                                        <Typography
                                                            sx={{
                                                                color: 'var(--lm-accent-contrast)',
                                                                fontWeight: 800,
                                                                fontSize: { xs: 28, sm: 34 },
                                                                lineHeight: 1.04,
                                                                letterSpacing: 0,
                                                                maxWidth: 360,
                                                            }}
                                                        >
                                                            Поиск лидов
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                color: 'rgba(221, 244, 255, 0.86)',
                                                                fontSize: { xs: 15, sm: 16 },
                                                                lineHeight: 1.45,
                                                                maxWidth: 390,
                                                            }}
                                                        >
                                                            Будет искать запросы по {filterCategoryLabel}.
                                                        </Typography>
                                                    </Stack>

                                                    <Box
                                                        className="agent-vibe-core"
                                                        aria-hidden="true"
                                                        sx={{
                                                            order: { xs: 1, md: 2 },
                                                            justifySelf: { xs: 'center', md: 'center' },
                                                            position: 'relative',
                                                            width: { xs: 156, sm: 190 },
                                                            height: { xs: 156, sm: 190 },
                                                            borderRadius: '42px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            background:
                                                                'linear-gradient(145deg, rgba(221, 244, 255, 0.2), rgba(7, 23, 34, 0.4))',
                                                            border: '1px solid rgba(221, 244, 255, 0.26)',
                                                            boxShadow:
                                                                '0 28px 70px rgba(0, 0, 0, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                                                            backdropFilter: 'blur(14px)',
                                                            animation: `${telegramVibeFloat} 5.6s ease-in-out infinite`,
                                                            '&::before, &::after': {
                                                                content: '""',
                                                                position: 'absolute',
                                                                borderRadius: '50%',
                                                                border: '1px solid rgba(221, 244, 255, 0.22)',
                                                            },
                                                            '&::before': {
                                                                inset: 18,
                                                            },
                                                            '&::after': {
                                                                inset: -16,
                                                                borderColor: 'rgba(142, 219, 255, 0.15)',
                                                            },
                                                        }}
                                                    >
                                                        <Box
                                                            className="agent-radar"
                                                            sx={{
                                                                position: 'absolute',
                                                                inset: { xs: -26, sm: -34 },
                                                                borderRadius: '50%',
                                                                border: '1px solid rgba(221, 244, 255, 0.18)',
                                                                borderTopColor: 'rgba(221, 244, 255, 0.78)',
                                                                borderRightColor: 'rgba(142, 219, 255, 0.42)',
                                                                animation: `${agentRadarSpin} 7s linear infinite`,
                                                                '&::before, &::after': {
                                                                    content: '""',
                                                                    position: 'absolute',
                                                                    inset: 18,
                                                                    borderRadius: '50%',
                                                                    border: '1px dashed rgba(221, 244, 255, 0.22)',
                                                                },
                                                                '&::after': {
                                                                    inset: 42,
                                                                    borderStyle: 'solid',
                                                                    borderTopColor: 'rgba(142, 219, 255, 0.64)',
                                                                    borderLeftColor: 'rgba(221, 244, 255, 0.08)',
                                                                },
                                                            }}
                                                        />
                                                        <Box
                                                            className="agent-scan"
                                                            sx={{
                                                                position: 'absolute',
                                                                inset: { xs: -42, sm: -54 },
                                                                borderRadius: '50%',
                                                                background:
                                                                    'conic-gradient(from 0deg, rgba(142, 219, 255, 0), rgba(142, 219, 255, 0.42), rgba(221, 244, 255, 0.02), rgba(142, 219, 255, 0))',
                                                                opacity: 0.82,
                                                                WebkitMask:
                                                                    'radial-gradient(circle, transparent 0 47%, var(--lm-text) 49%, var(--lm-text) 56%, transparent 58%)',
                                                                mask: 'radial-gradient(circle, transparent 0 47%, var(--lm-text) 49%, var(--lm-text) 56%, transparent 58%)',
                                                                animation: `${agentRadarSpin} 4.8s linear infinite`,
                                                            }}
                                                        />
                                                        {[
                                                            { left: '22%', bottom: '18%', delay: '0s', toX: '-34px' },
                                                            { left: '74%', bottom: '24%', delay: '-1.2s', toX: '28px' },
                                                            { left: '50%', bottom: '10%', delay: '-2.4s', toX: '8px' },
                                                            {
                                                                left: '38%',
                                                                bottom: '30%',
                                                                delay: '-3.1s',
                                                                toX: '-12px',
                                                            },
                                                        ].map((particle) => (
                                                            <Box
                                                                key={`${particle.left}-${particle.delay}`}
                                                                className="agent-particle"
                                                                sx={
                                                                    {
                                                                        '--to-x': particle.toX,
                                                                        position: 'absolute',
                                                                        left: particle.left,
                                                                        bottom: particle.bottom,
                                                                        width: 5,
                                                                        height: 5,
                                                                        borderRadius: '50%',
                                                                        backgroundColor: 'var(--lm-accent-soft)',
                                                                        boxShadow: '0 0 16px rgba(221, 244, 255, 0.78)',
                                                                        animation: `${agentParticleRise} 3.4s ease-in-out infinite`,
                                                                        animationDelay: particle.delay,
                                                                    } as any
                                                                }
                                                            />
                                                        ))}
                                                        <Box
                                                            sx={{
                                                                width: { xs: 96, sm: 116 },
                                                                height: { xs: 96, sm: 116 },
                                                                borderRadius: '32px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                background:
                                                                    'linear-gradient(145deg, rgba(7, 23, 34, 0.9), rgba(14, 37, 51, 0.72))',
                                                                boxShadow:
                                                                    '0 18px 44px rgba(0, 0, 0, 0.28), inset 0 0 32px rgba(42, 171, 238, 0.2)',
                                                                animation: `${agentCorePulse} 2.6s ease-in-out infinite`,
                                                                zIndex: 2,
                                                            }}
                                                        >
                                                            <QuerySearchAgentIcon size={70} />
                                                        </Box>
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                right: { xs: 16, sm: 20 },
                                                                bottom: { xs: 16, sm: 20 },
                                                                px: 1,
                                                                py: 0.45,
                                                                borderRadius: '999px',
                                                                backgroundColor: 'var(--lm-muted)',
                                                                color: 'var(--lm-accent-soft)',
                                                                fontSize: 12,
                                                                fontWeight: 800,
                                                                border: '1px solid rgba(221, 244, 255, 0.22)',
                                                                zIndex: 3,
                                                            }}
                                                        >
                                                            24/7
                                                        </Box>
                                                        {[
                                                            {
                                                                text: 'Нужен разработчик',
                                                                fromX: '-106px',
                                                                fromY: '-70px',
                                                                midX: '-78px',
                                                                midY: '-42px',
                                                                delay: '0s',
                                                                top: 18,
                                                                bottom: undefined,
                                                                left: -66,
                                                                right: undefined,
                                                            },
                                                            {
                                                                text: 'Сколько стоит MVP?',
                                                                fromX: '98px',
                                                                fromY: '-54px',
                                                                midX: '70px',
                                                                midY: '-28px',
                                                                delay: '-1.6s',
                                                                top: 52,
                                                                bottom: undefined,
                                                                left: undefined,
                                                                right: -72,
                                                            },
                                                            {
                                                                text: 'Ищу подрядчика',
                                                                fromX: '-88px',
                                                                fromY: '74px',
                                                                midX: '-54px',
                                                                midY: '38px',
                                                                delay: '-3.2s',
                                                                top: undefined,
                                                                bottom: 30,
                                                                left: -52,
                                                                right: undefined,
                                                            },
                                                        ].map((packet) => (
                                                            <Box
                                                                key={packet.text}
                                                                className="agent-packet"
                                                                sx={
                                                                    {
                                                                        '--from-x': packet.fromX,
                                                                        '--from-y': packet.fromY,
                                                                        '--mid-x': packet.midX,
                                                                        '--mid-y': packet.midY,
                                                                        position: 'absolute',
                                                                        top: packet.top,
                                                                        bottom: packet.bottom,
                                                                        left: packet.left,
                                                                        right: packet.right,
                                                                        zIndex: 4,
                                                                        px: 1,
                                                                        py: 0.55,
                                                                        maxWidth: 150,
                                                                        borderRadius: '999px',
                                                                        color: 'var(--lm-accent-soft)',
                                                                        backgroundColor: 'var(--lm-muted)',
                                                                        border: '1px solid rgba(221, 244, 255, 0.22)',
                                                                        boxShadow: '0 14px 34px rgba(0, 0, 0, 0.22)',
                                                                        backdropFilter: 'blur(14px)',
                                                                        fontSize: 11,
                                                                        fontWeight: LEADSTREAM_BUTTON_FONT_WEIGHT,
                                                                        lineHeight: 1.15,
                                                                        whiteSpace: 'nowrap',
                                                                        pointerEvents: 'none',
                                                                        animation: `${agentPacketFloat} 5.2s ease-in-out infinite`,
                                                                        animationDelay: packet.delay,
                                                                    } as any
                                                                }
                                                            >
                                                                {packet.text}
                                                            </Box>
                                                        ))}
                                                    </Box>

                                                    <Stack
                                                        direction={{ xs: 'row', md: 'column' }}
                                                        gap={1}
                                                        flexWrap="wrap"
                                                        sx={{
                                                            order: { xs: 3, md: 3 },
                                                            justifyContent: { xs: 'flex-start', md: 'center' },
                                                            alignItems: { xs: 'stretch', md: 'stretch' },
                                                        }}
                                                    >
                                                        {[
                                                            ['Категория', filterCategoryLabel],
                                                            ['Гео', filterGeoLabel],
                                                        ].map(([label, value]) => (
                                                            <Box
                                                                key={label}
                                                                sx={{
                                                                    minWidth: {
                                                                        xs: label === 'Категория' ? 156 : 104,
                                                                        md: 'auto',
                                                                    },
                                                                    borderRadius: '16px',
                                                                    border: '1px solid rgba(221, 244, 255, 0.2)',
                                                                    backgroundColor: 'var(--lm-muted)',
                                                                    px: 1.35,
                                                                    py: 1.05,
                                                                    backdropFilter: 'blur(12px)',
                                                                }}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        color: 'rgba(221, 244, 255, 0.58)',
                                                                        fontSize: 11,
                                                                        lineHeight: 1.2,
                                                                        mb: 0.35,
                                                                    }}
                                                                >
                                                                    {label}
                                                                </Typography>
                                                                <Box
                                                                    sx={{
                                                                        color: 'var(--lm-accent-contrast)',
                                                                        fontSize: 13,
                                                                        fontWeight: 700,
                                                                        lineHeight: 1.25,
                                                                    }}
                                                                >
                                                                    {value}
                                                                </Box>
                                                            </Box>
                                                        ))}
                                                    </Stack>
                                                </Box>
                                            </Box>

                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                                                    gap: { xs: 2, md: 2.5 },
                                                    p: { xs: 2, sm: 2.5 },
                                                    borderBottom: '1px solid var(--lm-border)',
                                                }}
                                            >
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.primary"
                                                        sx={{ fontWeight: 800, fontSize: 15, mb: 1 }}
                                                    >
                                                        Агент будет искать лиды, где люди:
                                                    </Typography>
                                                    <Stack gap={0.75}>
                                                        {leadSearchRules.map((rule) => (
                                                            <Stack
                                                                key={rule}
                                                                direction="row"
                                                                gap={1}
                                                                alignItems="flex-start"
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        width: 6,
                                                                        height: 6,
                                                                        borderRadius: '50%',
                                                                        backgroundColor: 'var(--lm-success-fill)',
                                                                        mt: '7px',
                                                                        flexShrink: 0,
                                                                    }}
                                                                />
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        color: 'var(--lm-muted)',
                                                                        fontSize: 14.5,
                                                                        lineHeight: 1.45,
                                                                    }}
                                                                >
                                                                    {rule}
                                                                </Typography>
                                                            </Stack>
                                                        ))}
                                                    </Stack>
                                                </Box>

                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.primary"
                                                        sx={{ fontWeight: 800, fontSize: 15, mb: 1 }}
                                                    >
                                                        Агент будет отсекать:
                                                    </Typography>
                                                    <Stack gap={0.75}>
                                                        {leadRejectRules.map((rule) => (
                                                            <Stack
                                                                key={rule}
                                                                direction="row"
                                                                gap={1}
                                                                alignItems="flex-start"
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        width: 6,
                                                                        height: 6,
                                                                        borderRadius: '50%',
                                                                        backgroundColor: 'var(--lm-muted)',
                                                                        mt: '7px',
                                                                        flexShrink: 0,
                                                                    }}
                                                                />
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        color: 'var(--lm-muted)',
                                                                        fontSize: 14.5,
                                                                        lineHeight: 1.45,
                                                                    }}
                                                                >
                                                                    {rule}
                                                                </Typography>
                                                            </Stack>
                                                        ))}
                                                    </Stack>
                                                </Box>
                                            </Box>

                                            <Box
                                                sx={{
                                                    p: { xs: 2, sm: 2.5 },
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'grid',
                                                        gridTemplateColumns: {
                                                            xs: '1fr',
                                                            md: 'repeat(3, minmax(0, 1fr))',
                                                        },
                                                        gap: 1.25,
                                                    }}
                                                >
                                                    {launchInfoCards.map((step) => (
                                                        <Box
                                                            key={step.title}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'flex-start',
                                                                gap: 1.15,
                                                                borderRadius: LEADSTREAM_CONTROL_RADIUS,
                                                                border: `1px solid ${LEADSTREAM_BORDER}`,
                                                                backgroundColor: 'var(--lm-surface)',
                                                                px: { xs: 1.45, sm: 1.75 },
                                                                py: { xs: 1.35, sm: 1.55 },
                                                                minHeight: 118,
                                                            }}
                                                        >
                                                            <LaunchStepIcon type={step.icon} />
                                                            <Box sx={{ minWidth: 0 }}>
                                                                <Typography
                                                                    variant="body2"
                                                                    color="text.primary"
                                                                    sx={{ fontWeight: 800, fontSize: 15, mb: 1 }}
                                                                >
                                                                    {step.title}
                                                                </Typography>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        color: 'var(--lm-muted)',
                                                                        fontSize: 14.5,
                                                                        lineHeight: 1.45,
                                                                    }}
                                                                >
                                                                    {step.description}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                </>
                            )}

                            {shouldShowManualCategories && (
                                <Box ref={manualCategoriesSectionRef}>
                                    {!manualCategoryFallback && (
                                        <Box mb={2}>
                                            <Typography
                                                variant="h5"
                                                color="text.primary"
                                                sx={{ ...LEADSTREAM_SECTION_TITLE_SX, mb: 1 }}
                                            >
                                                Другие категории
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: 'var(--lm-muted)' }}
                                            >
                                                Можно выбрать дополнительные потоки вручную.
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Поиск категорий */}
                                    <Box mb={3}>
                                        <TextField
                                            fullWidth
                                            placeholder="Поиск категорий..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Typography>🔍</Typography>
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: LEADSTREAM_CARD_RADIUS,
                                                    backgroundColor: 'var(--lm-surface)',
                                                },
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: LEADSTREAM_BORDER,
                                                },
                                            }}
                                        />
                                    </Box>

                                    {/* Ошибка загрузки категорий */}
                                    {categoriesError && (
                                        <Box mb={3}>
                                            <Alert
                                                severity="error"
                                                onClose={() => setCategoriesError(null)}
                                            >
                                                {categoriesError}
                                            </Alert>
                                        </Box>
                                    )}

                                    {/* Список категорий */}
                                    {categoriesLoading ? (
                                        <Box
                                            display="flex"
                                            justifyContent="center"
                                            p={4}
                                        >
                                            <CircularProgress />
                                        </Box>
                                    ) : filteredCategories.length === 0 ? (
                                        <Box
                                            display="flex"
                                            flexDirection="column"
                                            alignItems="center"
                                            justifyContent="center"
                                            p={4}
                                            sx={{
                                                backgroundColor: 'var(--lm-surface)',
                                                borderRadius: LEADSTREAM_CARD_RADIUS,
                                                border: `1px solid ${LEADSTREAM_BORDER}`,
                                                boxShadow: LEADSTREAM_CARD_SHADOW,
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                color="text.secondary"
                                                mb={1}
                                            >
                                                {searchQuery.trim()
                                                    ? 'Категории не найдены'
                                                    : 'Нет доступных категорий'}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                {searchQuery.trim()
                                                    ? 'Попробуйте изменить поисковый запрос'
                                                    : 'Категории будут добавлены позже'}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Box
                                            sx={{
                                                maxHeight: '500px',
                                                overflowY: 'auto',
                                                overflowX: 'hidden',
                                                mb: 4,
                                                pr: 1,
                                                py: 2,
                                                '&::-webkit-scrollbar': {
                                                    width: 8,
                                                },
                                                '&::-webkit-scrollbar-track': {
                                                    backgroundColor: 'var(--lm-hover)',
                                                    borderRadius: 4,
                                                },
                                                '&::-webkit-scrollbar-thumb': {
                                                    backgroundColor: 'var(--lm-hover)',
                                                    borderRadius: 4,
                                                    '&:hover': {
                                                        backgroundColor: 'var(--lm-hover)',
                                                    },
                                                },
                                            }}
                                        >
                                            <Box
                                                display="grid"
                                                gridTemplateColumns={
                                                    isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))'
                                                }
                                                gap={2}
                                            >
                                                {filteredCategories.map((category) => {
                                                    const isSelected = selectedCategories.has(category.id);
                                                    const categoryColor = category.color || 'var(--lm-accent-ink)';
                                                    return (
                                                        <Card
                                                            key={category.id}
                                                            onClick={() => toggleCategory(category)}
                                                            sx={{
                                                                cursor: 'pointer',
                                                                borderRadius: LEADSTREAM_CARD_RADIUS,
                                                                border: `1px solid ${
                                                                    isSelected ? categoryColor : LEADSTREAM_BORDER
                                                                }`,
                                                                boxShadow: isSelected
                                                                    ? `0 8px 22px color-mix(in srgb, ${categoryColor} 15%, transparent)`
                                                                    : LEADSTREAM_CARD_SHADOW,
                                                                transition: 'all 0.3s ease',
                                                                backgroundColor: isSelected
                                                                    ? `color-mix(in srgb, ${categoryColor} 3%, transparent)`
                                                                    : 'var(--lm-surface)',
                                                                '&:hover': {
                                                                    boxShadow: `0 8px 22px color-mix(in srgb, ${categoryColor} 15%, transparent)`,
                                                                    transform: 'translateY(-2px)',
                                                                },
                                                                '&:active': {
                                                                    transform: 'translateY(-1px)',
                                                                },
                                                            }}
                                                        >
                                                            <CardContent>
                                                                <Box
                                                                    display="flex"
                                                                    alignItems="flex-start"
                                                                    gap={1.5}
                                                                    mb={1}
                                                                >
                                                                    <Checkbox
                                                                        checked={isSelected}
                                                                        onChange={(e) => {
                                                                            e.stopPropagation();
                                                                            toggleCategory(category);
                                                                        }}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        sx={{
                                                                            mt: -1,
                                                                            ml: -1,
                                                                            color: categoryColor,
                                                                            '&.Mui-checked': {
                                                                                color: categoryColor,
                                                                            },
                                                                        }}
                                                                    />
                                                                    <Box
                                                                        flex={1}
                                                                        minWidth={0}
                                                                    >
                                                                        <Box
                                                                            display="flex"
                                                                            alignItems="center"
                                                                            gap={1.5}
                                                                            mb={0.5}
                                                                        >
                                                                            <Typography
                                                                                sx={{
                                                                                    fontSize: 20,
                                                                                    flexShrink: 0,
                                                                                    lineHeight: 1,
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                }}
                                                                            >
                                                                                {category.icon || '📁'}
                                                                            </Typography>
                                                                            <Typography
                                                                                variant="body1"
                                                                                sx={{
                                                                                    fontWeight: 600,
                                                                                    flex: 1,
                                                                                    fontSize: 16,
                                                                                    lineHeight: 1.4,
                                                                                }}
                                                                            >
                                                                                {category.name}
                                                                            </Typography>
                                                                        </Box>
                                                                        {category.description && (
                                                                            <Typography
                                                                                variant="body2"
                                                                                sx={{
                                                                                    color: 'var(--lm-muted)',
                                                                                    mt: 0.5,
                                                                                }}
                                                                            >
                                                                                {category.description}
                                                                            </Typography>
                                                                        )}
                                                                        {category.availability_hint && (
                                                                            <Typography
                                                                                variant="caption"
                                                                                sx={{
                                                                                    color: 'var(--lm-muted)',
                                                                                    mt: 0.5,
                                                                                    display: 'block',
                                                                                }}
                                                                            >
                                                                                {category.availability_hint}
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                </Box>
                                                            </CardContent>
                                                        </Card>
                                                    );
                                                })}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {shouldShowPostExamples && (
                                <Box
                                    ref={launchSectionRef}
                                    mb={4}
                                >
                                    <StageDivider />
                                    {shouldShowConfiguredFlowDetails && (
                                        <Stack
                                            spacing={2}
                                            mb={3}
                                            sx={getLeadstreamAppearSx(0)}
                                        >
                                            <Box
                                                sx={{
                                                    ...LEADSTREAM_CARD_SX,
                                                    position: 'relative',
                                                    overflow: 'hidden',
                                                    border: `1px solid ${LEADSTREAM_FLOW_BORDER}`,
                                                    boxShadow: LEADSTREAM_FLOW_SHADOW,
                                                    backgroundColor: 'var(--lm-surface)',
                                                    p: 0,
                                                    '& .leadstream-ready-wave': {
                                                        position: 'absolute',
                                                        right: 0,
                                                        bottom: -32,
                                                        left: 0,
                                                        width: '100%',
                                                        minWidth: '100%',
                                                        zIndex: 0,
                                                        height: 150,
                                                        overflow: 'hidden',
                                                        opacity: 0.52,
                                                        pointerEvents: 'none',
                                                    },
                                                    '& .leadstream-ready-wave canvas': {
                                                        position: 'absolute',
                                                        inset: 0,
                                                        display: 'block',
                                                        width: '100% !important',
                                                        height: '100% !important',
                                                    },
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        position: 'relative',
                                                        minHeight: { xs: 300, sm: 280 },
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        overflow: 'hidden',
                                                        px: { xs: 2.25, sm: 3 },
                                                        py: { xs: 3, sm: 3.5 },
                                                        backgroundColor: 'var(--lm-surface)',
                                                    }}
                                                >
                                                    <WavyBackground
                                                        containerClassName="leadstream-ready-wave"
                                                        backgroundFill="transparent"
                                                        blur={5}
                                                        colors={flowWaveColors}
                                                        speed="fast"
                                                        waveAmplitude={36}
                                                        waveCount={5}
                                                        waveOpacity={0.58}
                                                        waveWidth={22}
                                                        waveY={0.72}
                                                    />
                                                    <Stack
                                                        alignItems="center"
                                                        textAlign="center"
                                                        spacing={1}
                                                        sx={{
                                                            position: 'relative',
                                                            zIndex: 2,
                                                            maxWidth: 980,
                                                            width: '100%',
                                                            transform: 'translateY(-8px)',
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                color: 'var(--lm-text)',
                                                                fontSize: { xs: 18, sm: 21, md: 22 },
                                                                fontWeight: 850,
                                                                letterSpacing: '-0.02em',
                                                                lineHeight: 1.05,
                                                            }}
                                                        >
                                                            Поток лидов
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                color: 'var(--lm-text)',
                                                                fontSize: hasMultipleSelectedCategories
                                                                    ? { xs: 27, sm: 34, md: 40 }
                                                                    : { xs: 30, sm: 42, md: 48 },
                                                                fontWeight: 900,
                                                                letterSpacing: '-0.04em',
                                                                lineHeight: 1.12,
                                                                maxWidth: 980,
                                                            }}
                                                        >
                                                            {selectedCategoriesLabel}
                                                        </Typography>
                                                        {!manualCategoryFallback && (
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: 'var(--lm-muted)',
                                                                    fontSize: { xs: 15.5, sm: 16.5 },
                                                                    fontWeight: 600,
                                                                    lineHeight: 1.42,
                                                                    maxWidth: 760,
                                                                    mt: 0.5,
                                                                }}
                                                            >
                                                                ИИ учел ваши ответы и подготовил поток лидов к запуску
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                </Box>
                                            </Box>

                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: {
                                                        xs: '1fr',
                                                        md: 'repeat(2, minmax(0, 1fr))',
                                                    },
                                                    gap: 1.25,
                                                }}
                                            >
                                                {[
                                                    [
                                                        'Услуга или продукт',
                                                        setupDescription.trim() || 'Описание не указано',
                                                    ],
                                                    ['Локация', leadProductGeoLabel],
                                                ].map(([label, value]) => (
                                                    <Box
                                                        key={label}
                                                        sx={{
                                                            ...LEADSTREAM_CARD_SX,
                                                            px: { xs: 2, sm: 2.25 },
                                                            py: { xs: 1.75, sm: 2 },
                                                            border: `1px solid ${LEADSTREAM_BORDER}`,
                                                            backgroundColor: 'var(--lm-surface)',
                                                            boxShadow: LEADSTREAM_CARD_SHADOW,
                                                            minWidth: 0,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                display: 'block',
                                                                color: 'var(--lm-muted)',
                                                                fontWeight: 750,
                                                                lineHeight: 1.2,
                                                                mb: 0.5,
                                                            }}
                                                        >
                                                            {label}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: 'var(--lm-text)',
                                                                fontSize: { xs: 15.5, sm: 16 },
                                                                fontWeight: 700,
                                                                lineHeight: 1.35,
                                                            }}
                                                        >
                                                            {value}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>

                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: {
                                                        xs: '1fr',
                                                        md: 'repeat(2, minmax(0, 1fr))',
                                                    },
                                                    gap: 1.25,
                                                }}
                                            >
                                                {[
                                                    {
                                                        label: 'Агент будет искать лиды, где люди:',
                                                        items: leadSearchRules,
                                                        dotColor: 'var(--lm-accent-ink)',
                                                        icon: <SummarySearchIcon />,
                                                    },
                                                    {
                                                        label: 'Агент будет отсекать:',
                                                        items: leadPreviewRejectRules,
                                                        dotColor: 'var(--lm-muted)',
                                                        icon: <SummaryRejectIcon />,
                                                    },
                                                ].map((card) => (
                                                    <Box
                                                        key={card.label}
                                                        sx={{
                                                            ...LEADSTREAM_CARD_SX,
                                                            minHeight: 220,
                                                            px: { xs: 2, sm: 2.25 },
                                                            py: { xs: 1.75, sm: 2 },
                                                            border: `1px solid ${LEADSTREAM_BORDER}`,
                                                            bgcolor: 'var(--lm-surface)',
                                                            boxShadow: LEADSTREAM_CARD_SHADOW,
                                                        }}
                                                    >
                                                        <Stack
                                                            direction="row"
                                                            alignItems="center"
                                                            spacing={1}
                                                            sx={{
                                                                mb: 1.5,
                                                            }}
                                                        >
                                                            {card.icon}
                                                            <Typography
                                                                sx={{
                                                                    fontSize: { xs: 16, sm: 17 },
                                                                    lineHeight: 1.25,
                                                                    fontWeight: 850,
                                                                    color: 'var(--lm-text)',
                                                                }}
                                                            >
                                                                {card.label}
                                                            </Typography>
                                                        </Stack>
                                                        <Stack spacing={1.1}>
                                                            {card.items.map((item) => (
                                                                <Box
                                                                    key={item}
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'flex-start',
                                                                        gap: 0.95,
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            width: 6,
                                                                            height: 6,
                                                                            borderRadius: '50%',
                                                                            backgroundColor: card.dotColor,
                                                                            flexShrink: 0,
                                                                            mt: '8px',
                                                                        }}
                                                                    />
                                                                    <Typography
                                                                        sx={{
                                                                            fontSize: { xs: 15.5, sm: 16.5 },
                                                                            lineHeight: 1.42,
                                                                            fontWeight: 600,
                                                                            color: 'var(--lm-text)',
                                                                        }}
                                                                    >
                                                                        {item}
                                                                    </Typography>
                                                                </Box>
                                                            ))}
                                                        </Stack>
                                                    </Box>
                                                ))}
                                            </Box>

                                            <Box
                                                sx={{
                                                    p: { xs: 2, sm: 2.5 },
                                                    backgroundColor: 'rgba(0, 68, 255, 0.05)',
                                                    borderRadius: 2,
                                                    border: '2px solid rgba(0, 68, 255, 0.2)',
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        color: 'var(--lm-text)',
                                                        fontSize: { xs: 15, sm: 16 },
                                                        fontWeight: 600,
                                                        lineHeight: 1.5,
                                                    }}
                                                >
                                                    После запуска агент будет 24/7 мониторить Telegram и другие
                                                    источники, находить лиды с готовым спросом и отбирать релевантные
                                                    именно для вашего бизнеса
                                                </Typography>
                                            </Box>

                                            {potentialResult && (
                                                <Box
                                                    sx={{
                                                        p: { xs: 2, sm: 2.5 },
                                                        borderRadius: 2,
                                                        background:
                                                            'linear-gradient(135deg, rgba(0, 68, 255, 0.08) 0%, rgba(66, 200, 255, 0.12) 100%)',
                                                        border: '1px solid rgba(0, 68, 255, 0.18)',
                                                    }}
                                                >
                                                    <Stack
                                                        direction={{ xs: 'column', sm: 'row' }}
                                                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                                                        justifyContent="space-between"
                                                        gap={1.5}
                                                    >
                                                        <Box>
                                                            <Typography
                                                                sx={{
                                                                    color: 'var(--lm-accent-ink)',
                                                                    fontSize: { xs: 15, sm: 16 },
                                                                    fontWeight: 850,
                                                                    lineHeight: 1.25,
                                                                    mb: 0.35,
                                                                }}
                                                            >
                                                                Потенциальный результат
                                                            </Typography>
                                                            <Typography
                                                                sx={{
                                                                    color: 'var(--lm-muted)',
                                                                    fontSize: { xs: 13.5, sm: 14 },
                                                                    fontWeight: 600,
                                                                    lineHeight: 1.35,
                                                                }}
                                                            >
                                                                Оценка по базе лидов, локации и вашим отметкам
                                                            </Typography>
                                                        </Box>
                                                        <Typography
                                                            sx={{
                                                                color: 'var(--lm-text)',
                                                                fontSize: { xs: 22, sm: 26 },
                                                                fontWeight: 900,
                                                                letterSpacing: '-0.03em',
                                                                lineHeight: 1.05,
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            ~{formatNumber(potentialResult.min * 100)}
                                                            {potentialResult.max > potentialResult.min
                                                                ? `–${formatNumber(potentialResult.max * 100)}`
                                                                : ''}{' '}
                                                            целевых лидов / мес
                                                        </Typography>
                                                    </Stack>
                                                </Box>
                                            )}
                                        </Stack>
                                    )}
                                    {shouldShowConfiguredFlowDetails && (
                                        <>
                                            <StageDivider />
                                            <Box mb={2.5}>
                                                <Typography
                                                    variant="h5"
                                                    color="text.primary"
                                                    sx={{
                                                        ...LEADSTREAM_SECTION_TITLE_SX,
                                                        mb: 0.75,
                                                    }}
                                                >
                                                    Запустите поток лидов
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'var(--lm-muted)',
                                                        lineHeight: 1.5,
                                                        maxWidth: 760,
                                                    }}
                                                >
                                                    Выберите, сколько лидов хотите получить
                                                </Typography>
                                            </Box>
                                        </>
                                    )}

                                    {selectedCategories.size === 0 ? (
                                        <Box
                                            display="flex"
                                            flexDirection="column"
                                            alignItems="center"
                                            justifyContent="center"
                                            p={5}
                                            sx={{
                                                borderRadius: LEADSTREAM_CARD_RADIUS,
                                                border: `1px solid ${LEADSTREAM_BORDER}`,
                                                backgroundColor: 'var(--lm-surface)',
                                                boxShadow: LEADSTREAM_CARD_SHADOW,
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                color="text.primary"
                                                mb={1}
                                                textAlign="center"
                                                sx={{ fontWeight: 700, fontSize: 18 }}
                                            >
                                                Выберите категорию
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.primary"
                                                textAlign="center"
                                                sx={{ maxWidth: 420, opacity: 0.68 }}
                                            >
                                                После выбора категории здесь появятся доступные объёмы.
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Stack gap={2}>
                                            {selectedCategoryItems.map((selected, selectedIndex) => {
                                                const prices = [
                                                    ...getBillingPrices(selected.category.id),
                                                ].sort((a, b) => a.package_size - b.package_size);
                                                const isLoading = loadingPrices.has(selected.category.id);
                                                const categoryColor = selected.category.color || 'var(--lm-accent-ink)';
                                                const packageAccentColor = categoryColor;
                                                const isPackageMode = true;
                                                const activePackagePromotion = monthlyMode
                                                    ? null
                                                    : selected.category.active_package_promotion || null;
                                                const selectedPrice =
                                                    prices.find(
                                                        (price) => price.package_size === selected.packageSize
                                                    ) ||
                                                    prices[0] ||
                                                    null;
                                                const selectedPriceIndex = selectedPrice
                                                    ? Math.max(
                                                          0,
                                                          prices.findIndex((price) => price.id === selectedPrice.id)
                                                      )
                                                    : 0;
                                                const pricePerLead = selectedPrice
                                                    ? parseFloat(selectedPrice.price_per_lead)
                                                    : 0;
                                                const discount = Number(selectedPrice?.discount_percent || 0);
                                                const creditedLeadCount = selectedPrice
                                                    ? getCreditedLeadCount(
                                                          selectedPrice.package_size,
                                                          activePackagePromotion
                                                      )
                                                    : 0;
                                                const totalForCategory = selectedPrice
                                                    ? pricePerLead * selectedPrice.package_size
                                                    : 0;
                                                const basePricePerLead = getBasePrice(prices);
                                                const baseTotalForCategory = selectedPrice
                                                    ? basePricePerLead * selectedPrice.package_size
                                                    : 0;
                                                const hasBasePrice = hasVisibleBasePrice(
                                                    baseTotalForCategory,
                                                    totalForCategory
                                                );
                                                const categorySavings = Math.max(
                                                    0,
                                                    baseTotalForCategory - totalForCategory
                                                );

                                                return (
                                                    <React.Fragment key={selected.category.id}>
                                                        {manualCategoryFallback ? (
                                                            <Box
                                                                sx={{
                                                                    order: selectedIndex * 4,
                                                                    display: 'flex',
                                                                    alignItems: { xs: 'flex-start', sm: 'center' },
                                                                    justifyContent: 'space-between',
                                                                    flexDirection: { xs: 'column', sm: 'row' },
                                                                    gap: 1,
                                                                    mb: -0.75,
                                                                }}
                                                            >
                                                                <Typography
                                                                    color="text.primary"
                                                                    sx={{
                                                                        fontWeight: 850,
                                                                        fontSize: { xs: 18, sm: 20 },
                                                                        lineHeight: 1.25,
                                                                    }}
                                                                >
                                                                    {formatCategoryLabel(selected.category)}
                                                                </Typography>
                                                                <Button
                                                                    type="button"
                                                                    variant="text"
                                                                    color="primary"
                                                                    onClick={() => toggleCategory(selected.category)}
                                                                    sx={{
                                                                        minHeight: 32,
                                                                        px: 0,
                                                                        fontSize: 13,
                                                                        fontWeight: LEADSTREAM_BUTTON_FONT_WEIGHT,
                                                                        textTransform: 'none',
                                                                        alignSelf: { xs: 'flex-start', sm: 'center' },
                                                                    }}
                                                                >
                                                                    Убрать категорию
                                                                </Button>
                                                            </Box>
                                                        ) : null}
                                                        <Box
                                                            sx={{
                                                                border: '1px solid rgba(0, 68, 255, 0.22)',
                                                                borderRadius: LEADSTREAM_CARD_RADIUS,
                                                                backgroundColor: 'var(--lm-accent-soft)',
                                                                p: { xs: 2, sm: 2.5 },
                                                                order: manualCategoryFallback
                                                                    ? selectedIndex * 4 + 1
                                                                    : undefined,
                                                                boxShadow: '0 10px 28px rgba(0, 68, 255, 0.06)',
                                                            }}
                                                        >
                                                            <Box mb={2}>
                                                                <Typography
                                                                    color="text.primary"
                                                                    sx={{
                                                                        fontWeight: 850,
                                                                        fontSize: { xs: 19, sm: 20 },
                                                                        lineHeight: 1.25,
                                                                    }}
                                                                >
                                                                    {monthlyMode ? 'Сколько лидов получать каждый месяц?' : 'Сколько лидов хотите получить?'}
                                                                </Typography>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        color: 'var(--lm-muted)',
                                                                        fontSize: 14,
                                                                        fontWeight: 400,
                                                                        lineHeight: 1.45,
                                                                        mt: 0.35,
                                                                        maxWidth: 620,
                                                                    }}
                                                                >
                                                                    Лиды появляются в кабинете по мере нахождения.
                                                                </Typography>
                                                            </Box>

                                                            {isLoading ? (
                                                                <Box
                                                                    display="flex"
                                                                    justifyContent="center"
                                                                    p={2}
                                                                >
                                                                    <CircularProgress size={24} />
                                                                </Box>
                                                            ) : prices.length > 0 && selectedPrice ? (
                                                                <>
                                                                    <Box
                                                                        display="grid"
                                                                        gridTemplateColumns={{
                                                                            xs: '1fr',
                                                                            md: '1fr 210px',
                                                                        }}
                                                                        gap={2}
                                                                        alignItems="center"
                                                                        mb={prices.length > 1 ? 1.5 : 0}
                                                                    >
                                                                        <Box>
                                                                            {prices.length > 1 ? (
                                                                                <Box sx={{ pt: 1 }}>
                                                                                    <SliderPrimitive.Root
                                                                                        value={[selectedPriceIndex]}
                                                                                        min={0}
                                                                                        max={prices.length - 1}
                                                                                        step={1}
                                                                                        onValueChange={([
                                                                                            nextIndex,
                                                                                        ]) => {
                                                                                            const nextPrice =
                                                                                                prices[nextIndex];
                                                                                            if (nextPrice) {
                                                                                                selectPackage(
                                                                                                    selected.category
                                                                                                        .id,
                                                                                                    nextPrice.package_size
                                                                                                );
                                                                                            }
                                                                                        }}
                                                                                        style={{
                                                                                            position: 'relative',
                                                                                            display: 'flex',
                                                                                            alignItems: 'center',
                                                                                            userSelect: 'none',
                                                                                            touchAction: 'none',
                                                                                            width: '100%',
                                                                                            height: 28,
                                                                                        }}
                                                                                    >
                                                                                        <SliderPrimitive.Track
                                                                                            style={{
                                                                                                position: 'relative',
                                                                                                flexGrow: 1,
                                                                                                height: 6,
                                                                                                borderRadius: 999,
                                                                                                backgroundColor:
                                                                                                    LEADSTREAM_BORDER,
                                                                                            }}
                                                                                        >
                                                                                            <SliderPrimitive.Range
                                                                                                style={{
                                                                                                    position:
                                                                                                        'absolute',
                                                                                                    height: '100%',
                                                                                                    borderRadius: 999,
                                                                                                    backgroundColor:
                                                                                                        isPackageMode
                                                                                                            ? categoryColor
                                                                                                            : 'var(--lm-border)',
                                                                                                }}
                                                                                            />
                                                                                        </SliderPrimitive.Track>
                                                                                        <SliderPrimitive.Thumb
                                                                                            aria-label="Количество лидов"
                                                                                            style={{
                                                                                                display: 'block',
                                                                                                width: 18,
                                                                                                height: 18,
                                                                                                borderRadius: '50%',
                                                                                                border: '3px solid var(--lm-surface)',
                                                                                                backgroundColor:
                                                                                                    isPackageMode
                                                                                                        ? categoryColor
                                                                                                        : 'var(--lm-border)',
                                                                                                boxShadow:
                                                                                                    '0 1px 2px rgba(15, 23, 42, 0.16), 0 0 0 1px rgba(15, 23, 42, 0.08)',
                                                                                                cursor: 'grab',
                                                                                            }}
                                                                                        />
                                                                                    </SliderPrimitive.Root>

                                                                                    <Box
                                                                                        sx={{
                                                                                            display: 'grid',
                                                                                            gridTemplateColumns: {
                                                                                                xs: 'repeat(2, minmax(0, 1fr))',
                                                                                                sm: `repeat(${prices.length}, minmax(0, 1fr))`,
                                                                                            },
                                                                                            gap: 0.75,
                                                                                            mt: 1.25,
                                                                                        }}
                                                                                    >
                                                                                        {prices.map((price, index) => {
                                                                                            const isCurrent =
                                                                                                isPackageMode &&
                                                                                                index ===
                                                                                                    selectedPriceIndex;
                                                                                            const packagePresetLabel =
                                                                                                price.package_size === 100
                                                                                                    ? 'Рекомендуем'
                                                                                                    : null;
                                                                                            const presetBorderColor =
                                                                                                isCurrent
                                                                                                    ? packageAccentColor
                                                                                                    : LEADSTREAM_BORDER;
                                                                                            return (
                                                                                                <Box
                                                                                                    key={price.id}
                                                                                                    sx={{
                                                                                                        minWidth: 0,
                                                                                                        display: 'flex',
                                                                                                        flexDirection:
                                                                                                            'column',
                                                                                                        gap: 0.75,
                                                                                                    }}
                                                                                                >
                                                                                                    <Button
                                                                                                        type="button"
                                                                                                        onClick={() =>
                                                                                                            selectPackage(
                                                                                                                selected
                                                                                                                    .category
                                                                                                                    .id,
                                                                                                                price.package_size
                                                                                                            )
                                                                                                        }
                                                                                                        sx={{
                                                                                                            width: '100%',
                                                                                                            minWidth: 0,
                                                                                                            minHeight: 46,
                                                                                                            px: 1,
                                                                                                            py: 0,
                                                                                                            borderRadius:
                                                                                                                LEADSTREAM_CONTROL_RADIUS,
                                                                                                            border: `1px solid ${presetBorderColor}`,
                                                                                                            backgroundColor:
                                                                                                                isCurrent
                                                                                                                    ? packageAccentColor
                                                                                                                    : 'var(--lm-surface)',
                                                                                                            color: isCurrent
                                                                                                                ? 'var(--lm-surface)'
                                                                                                                : 'var(--lm-muted)',
                                                                                                            fontSize: 13,
                                                                                                            fontWeight:
                                                                                                                LEADSTREAM_BUTTON_FONT_WEIGHT,
                                                                                                            textTransform:
                                                                                                                'none',
                                                                                                            alignItems:
                                                                                                                'center',
                                                                                                            justifyContent:
                                                                                                                'center',
                                                                                                            lineHeight: 1.15,
                                                                                                            boxShadow:
                                                                                                                isCurrent
                                                                                                                    ? '0 10px 22px rgba(0, 68, 255, 0.2)'
                                                                                                                    : 'none',
                                                                                                            '&:hover': {
                                                                                                                backgroundColor:
                                                                                                                    isCurrent
                                                                                                                        ? '#0037D1'
                                                                                                                        : 'var(--lm-canvas)',
                                                                                                                color: isCurrent
                                                                                                                    ? 'var(--lm-surface)'
                                                                                                                    : 'var(--lm-muted)',
                                                                                                                '& .MuiTypography-root':
                                                                                                                    {
                                                                                                                        color: 'inherit',
                                                                                                                    },
                                                                                                            },
                                                                                                        }}
                                                                                                    >
                                                                                                        <Typography
                                                                                                            component="span"
                                                                                                            sx={{
                                                                                                                color: 'inherit',
                                                                                                                fontSize: 16,
                                                                                                                fontWeight:
                                                                                                                    LEADSTREAM_BUTTON_FONT_WEIGHT,
                                                                                                                lineHeight: 1.1,
                                                                                                            }}
                                                                                                        >
                                                                                                            {
                                                                                                                price.package_size
                                                                                                            }
                                                                                                        </Typography>
                                                                                                    </Button>
                                                                                                    <Box
                                                                                                        sx={{
                                                                                                            height: 42,
                                                                                                            display:
                                                                                                                'flex',
                                                                                                            alignItems:
                                                                                                                'center',
                                                                                                            justifyContent:
                                                                                                                'center',
                                                                                                        }}
                                                                                                    >
                                                                                                        {isCurrent &&
                                                                                                        packagePresetLabel ? (
                                                                                                            <Typography
                                                                                                                variant="caption"
                                                                                                                sx={{
                                                                                                                    color: packageAccentColor,
                                                                                                                    fontSize: 11.5,
                                                                                                                    fontWeight: 500,
                                                                                                                    lineHeight: 1.2,
                                                                                                                    textAlign:
                                                                                                                        'center',
                                                                                                                    display:
                                                                                                                        '-webkit-box',
                                                                                                                    WebkitLineClamp: 2,
                                                                                                                    WebkitBoxOrient:
                                                                                                                        'vertical',
                                                                                                                    overflow:
                                                                                                                        'hidden',
                                                                                                                }}
                                                                                                            >
                                                                                                                {
                                                                                                                    packagePresetLabel
                                                                                                                }
                                                                                                            </Typography>
                                                                                                        ) : null}
                                                                                                    </Box>
                                                                                                </Box>
                                                                                            );
                                                                                        })}
                                                                                    </Box>
                                                                                </Box>
                                                                            ) : (
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    sx={{
                                                                                        color: 'var(--lm-muted)',
                                                                                        fontSize: 13,
                                                                                    }}
                                                                                >
                                                                                    Для этой категории доступен один
                                                                                    стартовый пакет.
                                                                                </Typography>
                                                                            )}
                                                                        </Box>

                                                                        <Box
                                                                            sx={{
                                                                                borderRadius: LEADSTREAM_CONTROL_RADIUS,
                                                                                backgroundColor: 'var(--lm-canvas)',
                                                                                border: `1px solid ${LEADSTREAM_SOFT_BORDER}`,
                                                                                p: 1.75,
                                                                                minHeight: 168,
                                                                            }}
                                                                        >
                                                                            {[
                                                                                {
                                                                                    label: 'Цена за лид',
                                                                                    value: `${formatNumber(
                                                                                        pricePerLead
                                                                                    )} ₽`,
                                                                                },
                                                                                {
                                                                                    label: 'Скидка за объём',
                                                                                    value:
                                                                                        discount > 0
                                                                                            ? `${discount.toFixed(0)}%`
                                                                                            : '0%',
                                                                                },
                                                                                ...(hasBasePrice
                                                                                    ? [
                                                                                          {
                                                                                              label: 'Обычная цена',
                                                                                              value: `${formatNumber(
                                                                                                  Math.round(
                                                                                                      baseTotalForCategory
                                                                                                  )
                                                                                              )} ₽`,
                                                                                              strike: true,
                                                                                          },
                                                                                      ]
                                                                                    : []),
                                                                                ...(categorySavings > 0
                                                                                    ? [
                                                                                          {
                                                                                              label: 'Выгода',
                                                                                              value: `${formatNumber(
                                                                                                  Math.round(
                                                                                                      categorySavings
                                                                                                  )
                                                                                              )} ₽`,
                                                                                              success: true,
                                                                                          },
                                                                                      ]
                                                                                    : []),
                                                                                {
                                                                                    label: 'Стоимость',
                                                                                    value: `${formatNumber(
                                                                                        Math.round(totalForCategory)
                                                                                    )} ₽`,
                                                                                    strong: true,
                                                                                },
                                                                            ].map((row) => (
                                                                                <Box
                                                                                    key={row.label}
                                                                                    display="flex"
                                                                                    alignItems="baseline"
                                                                                    justifyContent="space-between"
                                                                                    gap={1.5}
                                                                                    sx={{
                                                                                        py: 0.65,
                                                                                        borderBottom:
                                                                                            row.label === 'Стоимость'
                                                                                                ? 'none'
                                                                                                : `1px solid ${LEADSTREAM_BORDER}`,
                                                                                    }}
                                                                                >
                                                                                    <Typography
                                                                                        variant="caption"
                                                                                        sx={{
                                                                                            color: 'var(--lm-muted)',
                                                                                            fontSize: 12,
                                                                                        }}
                                                                                    >
                                                                                        {row.label}
                                                                                    </Typography>
                                                                                    <Typography
                                                                                        sx={{
                                                                                            fontWeight: row.strong
                                                                                                ? 800
                                                                                                : row.strike
                                                                                                ? 700
                                                                                                : 700,
                                                                                            fontSize: row.strong
                                                                                                ? 18
                                                                                                : 14,
                                                                                            lineHeight: 1.2,
                                                                                            whiteSpace: 'nowrap',
                                                                                            color: row.strike
                                                                                                ? 'var(--lm-muted)'
                                                                                                : row.success
                                                                                                ? 'var(--lm-success-ink)'
                                                                                                : 'primary.main',
                                                                                            textDecoration: row.strike
                                                                                                ? 'line-through'
                                                                                                : 'none',
                                                                                        }}
                                                                                    >
                                                                                        {row.value}
                                                                                    </Typography>
                                                                                </Box>
                                                                            ))}
                                                                            {creditedLeadCount >
                                                                            selectedPrice.package_size ? (
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    sx={{
                                                                                        display: 'block',
                                                                                        mt: 0.75,
                                                                                        color: 'var(--lm-success-ink)',
                                                                                        fontWeight: 600,
                                                                                    }}
                                                                                >
                                                                                    Начислим {creditedLeadCount} лидов
                                                                                    по акции
                                                                                </Typography>
                                                                            ) : null}
                                                                        </Box>
                                                                    </Box>
                                                                    <TariffIncludedBadges
                                                                        badges={getTariffIncludedBadges(
                                                                            selectedPrice.package_size
                                                                        )}
                                                                    />
                                                                </>
                                                            ) : (
                                                                <Alert severity="warning">
                                                                    Нет доступных пакетов для этой категории
                                                                </Alert>
                                                            )}
                                                        </Box>
                                                    </React.Fragment>
                                                );
                                            })}

                                        </Stack>
                                    )}
                                </Box>
                            )}

                            {/* Выбранные категории с пакетами */}
                            {false && shouldShowPostExamples && (
                                <Box mb={4}>
                                    {manualCategoryFallback && (
                                        <Typography
                                            variant="h5"
                                            color="text.primary"
                                            sx={{
                                                fontWeight: 800,
                                                fontSize: 24,
                                                mb: 2.5,
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            Выбранные категории
                                        </Typography>
                                    )}

                                    {selectedCategories.size === 0 ? (
                                        <Box
                                            display="flex"
                                            flexDirection="column"
                                            alignItems="center"
                                            justifyContent="center"
                                            p={6}
                                        >
                                            <Typography sx={{ fontSize: 48, mb: 2 }}>⚡</Typography>
                                            <Typography
                                                variant="h6"
                                                color="text.primary"
                                                mb={1}
                                                textAlign="center"
                                                sx={{ fontWeight: 600 }}
                                            >
                                                Выберите категории
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.primary"
                                                textAlign="center"
                                                sx={{ maxWidth: '400px' }}
                                            >
                                                Выберите нужные категории из предложенных вариантов сверху, чтобы
                                                увидеть доступные пакеты лидов
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <>
                                            {Array.from(selectedCategories.values()).map((selected) => {
                                                const prices = getBillingPrices(selected.category.id);
                                                const basePrice = getBasePrice(prices);
                                                const isLoading = loadingPrices.has(selected.category.id);
                                                const activePackagePromotion = monthlyMode
                                                    ? null
                                                    : selected.category.active_package_promotion || null;

                                                return (
                                                    <Card
                                                        key={selected.category.id}
                                                        sx={{
                                                            mb: 2,
                                                            borderRadius: '16px',
                                                            border: '1px solid rgba(0, 0, 0, 0.12)',
                                                            backgroundColor: 'var(--lm-surface)',
                                                        }}
                                                    >
                                                        <CardContent>
                                                            <Box
                                                                display="flex"
                                                                alignItems="center"
                                                                gap={1.5}
                                                                mb={2}
                                                            >
                                                                <Typography sx={{ fontSize: 24 }}>
                                                                    {selected.category.icon || '📁'}
                                                                </Typography>
                                                                <Box flex={1}>
                                                                    <Typography
                                                                        variant="h6"
                                                                        sx={{ fontWeight: 600 }}
                                                                    >
                                                                        {selected.category.name}
                                                                    </Typography>
                                                                    {activePackagePromotion ? (
                                                                        <Stack
                                                                            direction="row"
                                                                            spacing={1}
                                                                            alignItems="center"
                                                                            flexWrap="wrap"
                                                                            useFlexGap
                                                                            sx={{ mt: 0.75 }}
                                                                        >
                                                                            <Chip
                                                                                size="small"
                                                                                color="success"
                                                                                label={getPromotionBadgeLabel(
                                                                                    activePackagePromotion
                                                                                )}
                                                                                sx={{ fontWeight: 700 }}
                                                                            />
                                                                            <Typography
                                                                                variant="caption"
                                                                                sx={{
                                                                                    color: 'success.main',
                                                                                    fontWeight: 600,
                                                                                }}
                                                                            >
                                                                                К любой покупке добавим лиды по акции.
                                                                            </Typography>
                                                                        </Stack>
                                                                    ) : null}
                                                                    {selected.category.description && (
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                color: 'var(--lm-muted)',
                                                                                mt: 0.5,
                                                                            }}
                                                                        >
                                                                            {selected.category.description}
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                                <Button
                                                                    size="small"
                                                                    onClick={() => toggleCategory(selected.category)}
                                                                    sx={{ color: 'error.main' }}
                                                                >
                                                                    Убрать
                                                                </Button>
                                                            </Box>

                                                            {isLoading ? (
                                                                <Box
                                                                    display="flex"
                                                                    justifyContent="center"
                                                                    p={2}
                                                                >
                                                                    <CircularProgress size={24} />
                                                                </Box>
                                                            ) : prices.length > 0 ? (
                                                                <Box
                                                                    sx={{
                                                                        overflowX: 'auto',
                                                                        overflowY: 'hidden',
                                                                        py: 1,
                                                                        '&::-webkit-scrollbar': {
                                                                            height: 6,
                                                                        },
                                                                        '&::-webkit-scrollbar-track': {
                                                                            backgroundColor: 'var(--lm-hover)',
                                                                            borderRadius: 3,
                                                                        },
                                                                        '&::-webkit-scrollbar-thumb': {
                                                                            backgroundColor: 'var(--lm-hover)',
                                                                            borderRadius: 3,
                                                                            '&:hover': {
                                                                                backgroundColor: 'var(--lm-hover)',
                                                                            },
                                                                        },
                                                                    }}
                                                                >
                                                                    <RadioGroup
                                                                        value={selected.packageSize?.toString() || ''}
                                                                        onChange={(e) =>
                                                                            selectPackage(
                                                                                selected.category.id,
                                                                                parseInt(e.target.value)
                                                                            )
                                                                        }
                                                                        sx={{
                                                                            display: 'flex',
                                                                            flexDirection: 'row',
                                                                            gap: 1.5,
                                                                            flexWrap: 'wrap',
                                                                        }}
                                                                    >
                                                                        {prices.map((price) => {
                                                                            const pricePerLead = parseFloat(
                                                                                price.price_per_lead
                                                                            );
                                                                            const { discount, savings } =
                                                                                calculateDiscount(
                                                                                    basePrice,
                                                                                    pricePerLead,
                                                                                    price.package_size
                                                                                );
                                                                            const creditedLeadCount =
                                                                                getCreditedLeadCount(
                                                                                    price.package_size,
                                                                                    activePackagePromotion
                                                                                );
                                                                            const hasPromotion =
                                                                                creditedLeadCount > price.package_size;
                                                                            const effectivePromoLeadPrice =
                                                                                hasPromotion && creditedLeadCount > 0
                                                                                    ? (pricePerLead *
                                                                                          price.package_size) /
                                                                                      creditedLeadCount
                                                                                    : null;
                                                                            const isSelected =
                                                                                selected.packageSize ===
                                                                                price.package_size;
                                                                            const categoryColor =
                                                                                selected.category.color ||
                                                                                'var(--lm-accent-ink)';

                                                                            return (
                                                                                <FormControlLabel
                                                                                    key={price.id}
                                                                                    value={price.package_size.toString()}
                                                                                    control={
                                                                                        <Radio
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                selectPackage(
                                                                                                    selected.category
                                                                                                        .id,
                                                                                                    price.package_size
                                                                                                );
                                                                                            }}
                                                                                            sx={{
                                                                                                position: 'absolute',
                                                                                                top: 8,
                                                                                                right: 8,
                                                                                                color: categoryColor,
                                                                                                '&.Mui-checked': {
                                                                                                    color: categoryColor,
                                                                                                },
                                                                                                zIndex: 1,
                                                                                            }}
                                                                                        />
                                                                                    }
                                                                                    label={
                                                                                        <Card
                                                                                            onClick={() =>
                                                                                                selectPackage(
                                                                                                    selected.category
                                                                                                        .id,
                                                                                                    price.package_size
                                                                                                )
                                                                                            }
                                                                                            sx={{
                                                                                                width: 180,
                                                                                                minWidth: 180,
                                                                                                height: 140,
                                                                                                borderRadius: '12px',
                                                                                                border: isSelected
                                                                                                    ? `2px solid ${categoryColor}`
                                                                                                    : '1px solid rgba(0, 0, 0, 0.12)',
                                                                                                backgroundColor:
                                                                                                    isSelected
                                                                                                        ? `color-mix(in srgb, ${categoryColor} 3%, transparent)`
                                                                                                        : 'var(--lm-surface)',
                                                                                                boxShadow: isSelected
                                                                                                    ? `0 2px 8px color-mix(in srgb, ${categoryColor} 25%, transparent)`
                                                                                                    : '0 1px 4px rgba(0, 0, 0, 0.1)',
                                                                                                transition:
                                                                                                    'all 0.2s ease',
                                                                                                cursor: 'pointer',
                                                                                                position: 'relative',
                                                                                                '&:hover': {
                                                                                                    boxShadow: `0 4px 12px color-mix(in srgb, ${categoryColor} 25%, transparent)`,
                                                                                                    transform:
                                                                                                        'translateY(-2px)',
                                                                                                },
                                                                                            }}
                                                                                        >
                                                                                            <CardContent
                                                                                                sx={{
                                                                                                    p: 2,
                                                                                                    '&:last-child': {
                                                                                                        pb: 2,
                                                                                                    },
                                                                                                    height: '100%',
                                                                                                    display: 'flex',
                                                                                                    flexDirection:
                                                                                                        'column',
                                                                                                    justifyContent:
                                                                                                        'space-between',
                                                                                                }}
                                                                                            >
                                                                                                <Box>
                                                                                                    {hasPromotion ? (
                                                                                                        <Chip
                                                                                                            size="small"
                                                                                                            color="success"
                                                                                                            label={getPromotionBadgeLabel(
                                                                                                                activePackagePromotion
                                                                                                            )}
                                                                                                            sx={{
                                                                                                                mb: 1,
                                                                                                                fontWeight: 700,
                                                                                                            }}
                                                                                                        />
                                                                                                    ) : null}
                                                                                                    <Typography
                                                                                                        variant="body1"
                                                                                                        sx={{
                                                                                                            fontWeight: 600,
                                                                                                            mb: 0.5,
                                                                                                        }}
                                                                                                    >
                                                                                                        {
                                                                                                            price.package_size
                                                                                                        }{' '}
                                                                                                        лидов
                                                                                                    </Typography>
                                                                                                    <Typography
                                                                                                        variant="body2"
                                                                                                        sx={{
                                                                                                            color: 'var(--lm-muted)',
                                                                                                            mb: 1,
                                                                                                        }}
                                                                                                    >
                                                                                                        {formatNumber(
                                                                                                            pricePerLead
                                                                                                        )}{' '}
                                                                                                        ₽ за лид
                                                                                                        {discount >
                                                                                                            0 && (
                                                                                                            <Typography
                                                                                                                component="span"
                                                                                                                sx={{
                                                                                                                    color: 'var(--lm-muted)',
                                                                                                                    fontWeight: 400,
                                                                                                                    fontSize: 14,
                                                                                                                    textDecoration:
                                                                                                                        'line-through',
                                                                                                                    ml: 1,
                                                                                                                }}
                                                                                                            >
                                                                                                                {formatNumber(
                                                                                                                    basePrice
                                                                                                                )}{' '}
                                                                                                                ₽
                                                                                                            </Typography>
                                                                                                        )}
                                                                                                    </Typography>
                                                                                                    <Typography
                                                                                                        variant="h6"
                                                                                                        sx={{
                                                                                                            fontWeight: 700,
                                                                                                            mb: 0.5,
                                                                                                        }}
                                                                                                    >
                                                                                                        {formatNumber(
                                                                                                            pricePerLead *
                                                                                                                price.package_size
                                                                                                        )}{' '}
                                                                                                        ₽
                                                                                                    </Typography>
                                                                                                    {hasPromotion ? (
                                                                                                        <>
                                                                                                            <Typography
                                                                                                                variant="caption"
                                                                                                                sx={{
                                                                                                                    color: 'var(--lm-success-ink)',
                                                                                                                    display:
                                                                                                                        'block',
                                                                                                                    fontWeight: 700,
                                                                                                                    mb: 0.25,
                                                                                                                }}
                                                                                                            >
                                                                                                                Начислим{' '}
                                                                                                                {
                                                                                                                    creditedLeadCount
                                                                                                                }{' '}
                                                                                                                лидов
                                                                                                                вместо{' '}
                                                                                                                {
                                                                                                                    price.package_size
                                                                                                                }
                                                                                                            </Typography>
                                                                                                            {effectivePromoLeadPrice !==
                                                                                                            null ? (
                                                                                                                <Typography
                                                                                                                    variant="caption"
                                                                                                                    sx={{
                                                                                                                        color: 'var(--lm-muted)',
                                                                                                                        display:
                                                                                                                            'block',
                                                                                                                    }}
                                                                                                                >
                                                                                                                    По
                                                                                                                    акции
                                                                                                                    фактически{' '}
                                                                                                                    {formatNumber(
                                                                                                                        effectivePromoLeadPrice
                                                                                                                    )}{' '}
                                                                                                                    ₽ за
                                                                                                                    лид
                                                                                                                </Typography>
                                                                                                            ) : null}
                                                                                                        </>
                                                                                                    ) : null}
                                                                                                </Box>
                                                                                                {discount > 0 && (
                                                                                                    <Box>
                                                                                                        <Typography
                                                                                                            variant="caption"
                                                                                                            sx={{
                                                                                                                color: 'var(--lm-success-ink)',
                                                                                                                display:
                                                                                                                    'block',
                                                                                                                fontWeight: 600,
                                                                                                            }}
                                                                                                        >
                                                                                                            Выгода:{' '}
                                                                                                            {formatNumber(
                                                                                                                savings
                                                                                                            )}{' '}
                                                                                                            ₽
                                                                                                        </Typography>
                                                                                                    </Box>
                                                                                                )}
                                                                                            </CardContent>
                                                                                        </Card>
                                                                                    }
                                                                                    sx={{
                                                                                        margin: 0,
                                                                                        position: 'relative',
                                                                                    }}
                                                                                />
                                                                            );
                                                                        })}
                                                                    </RadioGroup>
                                                                </Box>
                                                            ) : (
                                                                <Alert severity="warning">
                                                                    Нет доступных пакетов для этой категории
                                                                </Alert>
                                                            )}
                                                        </CardContent>
                                                    </Card>
                                                );
                                            })}
                                        </>
                                    )}
                                </Box>
                            )}

                            {/* Оплата запуска */}
                            {shouldShowPostExamples && selectedCategories.size > 0 && (
                                <>
                                    <Box
                                        sx={{
                                            height: '1px',
                                            backgroundColor: LEADSTREAM_BORDER,
                                            my: 4,
                                            width: '100%',
                                        }}
                                    />

                                    <Box mb={4}>
                                        <Typography
                                            variant="h5"
                                            color="text.primary"
                                            sx={{
                                                ...LEADSTREAM_SECTION_TITLE_SX,
                                                mb: 2.5,
                                            }}
                                        >
                                            Оплата потока лидов
                                        </Typography>

                                        {monthlyCheckoutAvailable && (
                                            <Box sx={{ mb: 2.5 }}>
                                                <AutoTopUpChoice
                                                    checked={monthlyMode}
                                                    onChange={(checked) => setBillingMode(checked ? 'monthly' : 'one_time')}
                                                    oneTimeUnit={oneTimeBillingUnit}
                                                    monthlyUnit={monthlyBillingUnit}
                                                    packageSize={billingSelection?.packageSize || 0}
                                                />
                                            </Box>
                                        )}

                                        <Typography sx={{ color: 'var(--lm-muted)', fontSize: 13, mb: 2.5 }}>
                                            Неиспользованные за месяц лиды сохраняются.
                                        </Typography>

                                        <Box mb={2.5}>
                                            <Points
                                                name="bonus"
                                                control={control}
                                                points={bonusPoints}
                                            />
                                        </Box>

                                        <Box mb={2.5}>
                                            <TicketField
                                                name="promo_code"
                                                control={control}
                                                placeholder="Промокод"
                                            />
                                            {isValidatingPromo && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{ color: 'var(--lm-muted)', mt: 0.5, display: 'block' }}
                                                >
                                                    Проверка промокода...
                                                </Typography>
                                            )}
                                            {promoError && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{ color: 'error.main', mt: 0.5, display: 'block' }}
                                                >
                                                    {promoError}
                                                </Typography>
                                            )}
                                            {promoDiscount && !promoError && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: 'var(--lm-success-ink)',
                                                        mt: 0.5,
                                                        display: 'block',
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    Промокод применен! Скидка {promoDiscount}%
                                                </Typography>
                                            )}
                                        </Box>
                                        {monthlyMode && (promoDiscountAmount > 0 || actualBonusToUse > 0) && (
                                            <Typography sx={{ color: 'var(--lm-muted)', fontSize: 13, mb: 2.5 }}>
                                                Промокод и баллы уменьшают только первую оплату. Далее {formatLeadPrice(total)} ₽ в месяц.
                                            </Typography>
                                        )}
                                    </Box>

                                    {/* Итоговая сумма */}
                                    <Box mb={3}>
                                        <Box
                                            border={`1px solid ${LEADSTREAM_BORDER}`}
                                            bgcolor="var(--lm-surface)"
                                            borderRadius={LEADSTREAM_CARD_RADIUS}
                                            sx={{ overflow: 'hidden', boxShadow: LEADSTREAM_CARD_SHADOW }}
                                        >
                                            {/* Список покупок */}
                                            <Box
                                                px={2}
                                                py={2}
                                                sx={{ borderBottom: `1px solid ${LEADSTREAM_BORDER}` }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{ fontWeight: 600, mb: 1.5, color: 'var(--lm-muted)' }}
                                                >
                                                    {monthlyMode ? 'Лиды каждый месяц' : 'Разовая покупка'}
                                                </Typography>
                                                {Array.from(selectedCategories.values()).map((selected) => {
                                                    if (!selected.packageSize) return null;
                                                    const prices = getBillingPrices(selected.category.id);
                                                    const priceObj = prices.find(
                                                        (p) => p.package_size === selected.packageSize
                                                    );
                                                    if (!priceObj) return null;

                                                    const pricePerLead = parseFloat(priceObj.price_per_lead);
                                                    const basePrice = getBasePrice(prices);
                                                    const { savings } = calculateDiscount(
                                                        basePrice,
                                                        pricePerLead,
                                                        selected.packageSize
                                                    );
                                                    const totalForCategory = pricePerLead * selected.packageSize;
                                                    const baseTotalForCategory = basePrice * selected.packageSize;
                                                    const hasTariffDiscount =
                                                        savings > 0 && baseTotalForCategory > totalForCategory;
                                                    const visibleTopPrice = hasTariffDiscount
                                                        ? baseTotalForCategory
                                                        : totalForCategory;

                                                    return (
                                                        <Box
                                                            key={selected.category.id}
                                                            mb={1.25}
                                                        >
                                                            <Box
                                                                display="flex"
                                                                alignItems="flex-start"
                                                                justifyContent="space-between"
                                                                gap={2}
                                                            >
                                                                <Box
                                                                    flex={1}
                                                                    minWidth={0}
                                                                >
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            fontWeight: 700,
                                                                            lineHeight: 1.22,
                                                                        }}
                                                                    >
                                                                        {monthlyMode
                                                                            ? `${selected.packageSize} новых лидов каждый месяц`
                                                                            : `Поток: ${selected.packageSize} целевых лидов`}
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            display: 'block',
                                                                            mt: 0.45,
                                                                            color: 'var(--lm-muted)',
                                                                            fontWeight: 600,
                                                                            lineHeight: 1.2,
                                                                        }}
                                                                    >
                                                                        Ниша: {selected.category.name}
                                                                    </Typography>
                                                                    <Typography variant="caption" sx={{ color: 'var(--lm-text)', fontWeight: 700 }}>
                                                                        {formatNumber(pricePerLead)} ₽/лид
                                                                    </Typography>
                                                                </Box>
                                                                <Box
                                                                    sx={{
                                                                        textAlign: 'right',
                                                                        flexShrink: 0,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            fontWeight: 800,
                                                                            fontSize: 20,
                                                                            lineHeight: 1.1,
                                                                            whiteSpace: 'nowrap',
                                                                        }}
                                                                    >
                                                                        {formatNumber(Math.round(visibleTopPrice))} ₽
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>

                                            {userVisibleSavings > 0 && (
                                                <Box
                                                    sx={{
                                                        backgroundColor: 'var(--lm-surface)',
                                                    }}
                                                >
                                                    {totalSavings > 0 && (
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                            justifyContent="space-between"
                                                            gap={2}
                                                            px={2}
                                                            py={1}
                                                            sx={{ backgroundColor: 'var(--lm-surface)' }}
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                sx={{ color: 'var(--lm-success-ink)', fontWeight: 600 }}
                                                            >
                                                                Скидка за объём{' '}
                                                                {totalSavingsPercent.toFixed(0)}%
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{ color: 'var(--lm-success-ink)', fontWeight: 700 }}
                                                            >
                                                                −{formatNumber(Math.round(totalSavings))} ₽
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    {promoDiscountAmount > 0 && (
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                            justifyContent="space-between"
                                                            gap={2}
                                                            px={2}
                                                            py={1}
                                                            sx={{
                                                                borderTop: totalSavings
                                                                    ? `1px solid ${LEADSTREAM_BORDER}`
                                                                    : 'none',
                                                                backgroundColor: 'var(--lm-surface)',
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                sx={{ color: 'var(--lm-success-ink)', fontWeight: 600 }}
                                                            >
                                                                Промокод {promoCodeLabel ? `${promoCodeLabel} ` : ''}
                                                                {promoDiscount}%
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{ color: 'var(--lm-success-ink)', fontWeight: 700 }}
                                                            >
                                                                −{formatNumber(Math.round(promoDiscountAmount))} ₽
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    {actualBonusToUse > 0 && (
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                            justifyContent="space-between"
                                                            gap={2}
                                                            px={2}
                                                            py={1}
                                                            sx={{
                                                                borderTop:
                                                                    totalSavings > 0 || promoDiscountAmount > 0
                                                                        ? `1px solid ${LEADSTREAM_BORDER}`
                                                                        : 'none',
                                                                backgroundColor: 'var(--lm-surface)',
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                sx={{ color: 'var(--lm-success-ink)', fontWeight: 600 }}
                                                            >
                                                                Бонусы применены
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{ color: 'var(--lm-success-ink)', fontWeight: 700 }}
                                                            >
                                                                −{formatNumber(Math.round(actualBonusToUse))} ₽
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                        gap={2}
                                                        px={2}
                                                        py={1.25}
                                                        sx={{
                                                            backgroundColor: 'rgba(74, 226, 135, 0.08)',
                                                            borderTop:
                                                                totalSavings > 0 ||
                                                                promoDiscountAmount > 0 ||
                                                                actualBonusToUse > 0
                                                                    ? `1px solid ${LEADSTREAM_BORDER}`
                                                                    : 'none',
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body1"
                                                            sx={{
                                                                color: 'var(--lm-success-ink)',
                                                                fontWeight: 700,
                                                                fontSize: 16,
                                                            }}
                                                        >
                                                            Ваша выгода
                                                        </Typography>
                                                        <Typography
                                                            variant="body1"
                                                            sx={{
                                                                color: 'var(--lm-success-ink)',
                                                                fontWeight: 600,
                                                                fontSize: 15,
                                                            }}
                                                        >
                                                            {formatNumber(Math.round(userVisibleSavings))} ₽
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            )}
                                            <Box
                                                display="flex"
                                                alignItems="flex-start"
                                                justifyContent="space-between"
                                                gap={2}
                                                px={2}
                                                py={2}
                                                sx={{
                                                    borderTop:
                                                        actualBonusToUse > 0 ||
                                                        promoDiscountAmount > 0 ||
                                                        userVisibleSavings > 0
                                                            ? `1px solid ${LEADSTREAM_BORDER}`
                                                            : 'none',
                                                }}
                                            >
                                                <Typography
                                                    color="text.primary"
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 800,
                                                        fontSize: 20,
                                                    }}
                                                >
                                                    К оплате сегодня
                                                </Typography>
                                                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                                    <Stack
                                                        direction="row"
                                                        alignItems="baseline"
                                                        justifyContent="flex-end"
                                                        spacing={1}
                                                    >
                                                        {hasFinalOldTotal && (
                                                            <Typography
                                                                sx={{
                                                                    color: 'var(--lm-muted)',
                                                                    fontWeight: 700,
                                                                    fontSize: 13,
                                                                    lineHeight: 1,
                                                                    textDecoration: 'line-through',
                                                                    whiteSpace: 'nowrap',
                                                                }}
                                                            >
                                                                {formatNumber(Math.round(finalOldTotal))} ₽
                                                            </Typography>
                                                        )}
                                                        <Typography
                                                            color="text.primary"
                                                            variant="h6"
                                                            sx={{
                                                                fontWeight: 800,
                                                                fontSize: 20,
                                                                lineHeight: 1.15,
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            {formatLeadPrice(finalTotal)} ₽
                                                        </Typography>
                                                    </Stack>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>

                                    <Box mt={3}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="primary"
                                            size="large"
                                            fullWidth
                                            sx={{
                                                minHeight: 56,
                                                borderRadius: LEADSTREAM_CARD_RADIUS,
                                                fontWeight: LEADSTREAM_BUTTON_FONT_WEIGHT,
                                                fontSize: 16,
                                                textTransform: 'none',
                                            }}
                                            disabled={
                                                isSubmitting ||
                                                !hasLaunchPrompt ||
                                                Array.from(selectedCategories.values()).some((s) => !s.packageSize) ||
                                                isValidatingPromo || Boolean(promoCode?.trim() && promoError)
                                            }
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <CircularProgress
                                                        size={20}
                                                        sx={{ mr: 1 }}
                                                    />
                                                    Обработка...
                                                </>
                                            ) : (
                                                monthlyMode
                                                    ? `Получать лиды каждый месяц — ${formatLeadPrice(total)} ₽/мес`
                                                    : `Оплатить и запустить поток за ${formatLeadPrice(finalTotal)} ₽`
                                            )}
                                        </Button>
                                    </Box>
                                </>
                            )}
                        </>
                    )}
                </form>
            </Box>
        );
    };

    if (!isMobile) {
        return (
            <DesktopLayout
                rightSidebar={
                    <LeadStreamRightSidebar
                        hasAnalysis={Boolean(analysis)}
                        examplesDone={examplesDone}
                        examplesCount={examplesCount}
                        examplesProgressPercent={examplesProgressPercent}
                        hasPrompt={hasLaunchPrompt}
                        onReset={analysis || selectedCategories.size > 0 ? handleResetFlow : undefined}
                    />
                }
            >
                {renderContent()}
            </DesktopLayout>
        );
    }

    return <DesktopLayout hideRightSidebar>{renderContent()}</DesktopLayout>;
}
